// Imports de l'écosystème React, Google Maps et Firebase Auth/Firestore/Functions
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { GoogleMap, useJsApiLoader, OverlayView, Rectangle, Circle } from '@react-google-maps/api';
import { httpsCallable } from "firebase/functions";
import { GoogleAuthProvider, signInWithPopup, onAuthStateChanged, signOut } from "firebase/auth";
import { collection, getDocs, query, getCountFromServer, where, GeoPoint, doc, getDoc, addDoc, setDoc, arrayUnion, serverTimestamp, onSnapshot, orderBy, limit, updateDoc, increment } from "firebase/firestore";
import { useTranslation } from "react-i18next";
import ZoomGauge from './ZoomGauge'; import './ZoomGauge.css';
import { auth, db, functions } from './firebase';
import logo from './logo.png'; import ZMLogo from './ZM.png'; import EmojiPickerButton from './EmojiPickerButton';
import badgeTouriste from './touriste.svg';
import badgeVoyageur from './voyageur.svg';
import badgeAventurier from './aventurier.svg';
import badgeExplorateur from './explorateur.svg';
import badgeLegende from './legende.svg';

// Coordonnées de centrage par défaut de la carte
const initialCenter = { lat: 18.8566, lng: 2.3522 };

// Configuration des styles d'affichage de la carte Google Maps (masquage routes/poi)
const mapStyles = [
    { featureType: "road", elementType: "all", stylers: [{ visibility: "off" }] }, { featureType: "poi", elementType: "all", stylers: [{ visibility: "off" }] },
    { featureType: "transit", elementType: "all", stylers: [{ visibility: "off" }] }, { featureType: "water", elementType: "labels.text", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.country", elementType: "geometry", stylers: [{ visibility: "off" }] }, { featureType: "administrative.country", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.province", elementType: "geometry", stylers: [{ visibility: "off" }] }, { featureType: "administrative.province", elementType: "labels", stylers: [{ visibility: "off" }] },
    { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#ffffff" }, { visibility: "on" }] }, { featureType: "administrative.locality", elementType: "labels.text.stroke", stylers: [{ color: "#000000" }, { weight: 2 }, { visibility: "on" }] }
];

// Styles CSS inline pour les éléments d'interface utilisateur
const userImageStyle = { width: '42px', height: '42px', borderRadius: '50%' };
const accountOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(6px)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' };
const accountModalBoxStyle = { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)', position: 'relative', maxHeight: '85vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '20px' };
// Styles du marqueur logo sur la carte (22px avec ombre portée)
const guessLogoMarkerStyle = { 
    position: 'absolute', 
    transform: 'translate(-50%, -50%)', 
    display: 'flex', 
    flexDirection: 'column', 
    alignItems: 'center', 
    pointerEvents: 'none', 
    userSelect: 'none' 
};

const guessLogoImageStyle = { 
    width: '22px', 
    height: '22px', 
    display: 'block', 
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.6))', 
    userSelect: 'none' 
};

const guessDistanceBadgeStyle = { 
    marginTop: '3px', 
    background: 'rgba(10, 15, 24, 0.85)', 
    color: '#EAEDF2', 
    border: '1px solid rgba(76, 224, 209, 0.5)', 
    borderRadius: '4px', 
    padding: '1px 5px', 
    fontSize: '11px', 
    fontWeight: 'bold', 
    fontFamily: 'Orbitron, sans-serif', 
    whiteSpace: 'nowrap', 
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.7)', 
    textShadow: '0 1px 2px rgba(0, 0, 0, 0.8)' 
};

// Composants d'affichage UI (Badges)
const BadgeZM = ({ text, imgSize = '1.6em', fontSize = '14px', padding = '2px 8px', style = {} }) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'linear-gradient(135deg,rgb(106, 37, 15) 0%,rgb(181, 139, 25) 100%)', padding, borderRadius: '20px', color: '#fff', fontWeight: 'bold', fontSize, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', border: '1px solid rgba(255, 255, 255, 0.4)', ...style }}>
        <span style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.3)', whiteSpace: 'nowrap' }}>{text}</span>
        <img src={ZMLogo} alt="ZM" style={{ height: imgSize, width: imgSize, filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))' }} />
    </div>
);

const BadgePoints = ({ text, iconSize = '16px', fontSize = '14px', padding = '2px 8px', iconFirst = true, style = {} }) => (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: 'linear-gradient(135deg, #1e3c72 0%,rgb(123, 30, 154) 100%)', padding, borderRadius: '20px', color: '#fff', fontWeight: 'bold', fontSize, boxShadow: '0 2px 4px rgba(0,0,0,0.2)', border: '1px solid rgba(255, 255, 255, 0.4)', ...style }}>
        {iconFirst && <span style={{ fontSize: iconSize, filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))' }}></span>}
        
        {/* LA POLICE EST FORCÉE DIRECTEMENT ICI SUR LE TEXTE */}
        <span style={{ textShadow: '0px 1px 2px rgba(0,0,0,0.3)', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
            {text}
        </span>
        
        {!iconFirst && <span style={{ fontSize: iconSize, filter: 'drop-shadow(0px 1px 2px rgba(0,0,0,0.2))' }}></span>}
    </div>
);

const ENABLE_PHASE3_CIRCLES = true; // Mettre sur false pour désactiver les cercles avec les flèches

// Appels Firebase
const submitGeoDetectiveGuess = httpsCallable(functions, 'submitGeoDetectiveGuess');

// Composant Flèche GPS Moderne
const DirectionArrow = ({ emoji, size = "30px", isSplash = false }) => {
    // On convertit l'emoji envoyé par le backend en degrés de rotation
    const getRotation = (dir) => {
        switch(dir) {
            case '⬆️': return 0;
            case '↗️': return 45;
            case '➡️': return 90;
            case '↘️': return 135;
            case '⬇️': return 180;
            case '↙️': return 225;
            case '⬅️': return 270;
            case '↖️': return 315;
            default: return 0;
        }
    };

    return (
        <svg 
            width={size} 
            height={size} 
            viewBox="0 0 24 24" 
            fill={isSplash ? "rgba(76, 224, 209, 0.4)" : "rgba(76, 224, 209, 0.1)"} 
            stroke="#4CE0D2" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            style={{ 
                transform: `rotate(${getRotation(emoji)}deg)`,
                filter: isSplash ? 'drop-shadow(0px 0px 20px rgba(76, 224, 209, 0.9))' : 'drop-shadow(0px 2px 5px rgba(0,0,0,0.6))',
                transition: 'transform 0.3s ease-out'
            }}
        >
            {/* Forme de curseur GPS / Avion furtif */}
            <path d="M12 2L20 21L12 17L4 21L12 2Z" />
        </svg>
    );
};

// Icône 🌍 : Daily Zoom (Globe filaire moderne)
const IconDaily = ({ size = "22px", color = "#4CE0D2" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }}>
        <circle cx="12" cy="12" r="10" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        <path d="M2 12h20" />
    </svg>
);

// Icône 👤 : Mode Solo (Silhouette épurée)
const IconSolo = ({ size = "22px", color = "#4CE0D2" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }}>
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

// Icône ⚔️ : Mode Duel (Épées croisées géométriques)
const IconDuel = ({ size = "22px", color = "#4CE0D2" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }}>
        <path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
        <path d="M13 19l6-6" />
        <path d="M16 16l4 4" />
        <path d="M19 21l2-2" />
        <path d="M14.5 6.5L18 3h3v3l-3.5 3.5" />
        <path d="M7 17l-3 3" />
        <path d="M5 14l4 4" />
        <path d="M3 19l2 2" />
    </svg>
);

// Icône 🔁 : Rejouer / Collection (Boucle de replay moderne)
const IconReplay = ({ size = "22px", color = "#4CE0D2" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }}>
        <polyline points="17 1 21 5 17 9" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <polyline points="7 23 3 19 7 15" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
);

// Icône Cadeau (SVG pour le cadeau de bienvenue)
const IconGift = ({ size = "24px", color = "#FFD700" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ filter: 'drop-shadow(0px 2px 4px rgba(0,0,0,0.5))' }}>
        <polyline points="20 12 20 22 4 22 4 12" />
        <rect x="2" y="7" width="20" height="5" />
        <line x1="12" y1="22" x2="12" y2="7" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" />
    </svg>
);

// Icône Bouclier (SVG réassurance sans engagement)
const IconShield = ({ size = "16px", color = "#4CE0D2" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
);

// Icône Twitch (SVG officiel pour streamers VIP)
const IconTwitch = ({ size = "15px", color = "currentColor" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
    </svg>
);

// Icône YouTube (SVG officiel)
const IconYouTube = ({ size = "16px" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <path fill="#FF0000" d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" />
        <path fill="#FFFFFF" d="M9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
    </svg>
);

// Icône TikTok (SVG officiel)
const IconTikTok = ({ size = "15px", color = "#FFFFFF" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.29 0 .58.04.85.12V9.4a6.33 6.33 0 0 0-1-.08A6.34 6.34 0 0 0 3 15.66a6.34 6.34 0 0 0 10.82 4.49 6.27 6.27 0 0 0 1.86-4.49V8.52a8.28 8.28 0 0 0 5.21 1.85V6.92a4.85 4.85 0 0 1-1.3-.23z"/>
    </svg>
);

// Icône Instagram (SVG officiel avec dégradé authentique)
const IconInstagram = ({ size = "15px" }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <defs>
            <linearGradient id="igIconGrad" x1="0%" y1="100%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#FD5949" />
                <stop offset="50%" stopColor="#D6249F" />
                <stop offset="100%" stopColor="#285AEB" />
            </linearGradient>
        </defs>
        <rect x="2.5" y="2.5" width="19" height="19" rx="5" ry="5" stroke="url(#igIconGrad)" strokeWidth="2.2" />
        <circle cx="12" cy="12" r="4.3" stroke="url(#igIconGrad)" strokeWidth="2.2" />
        <circle cx="17.2" cy="6.8" r="1.3" fill="url(#igIconGrad)" />
    </svg>
);

// Détecteur et formateur pour les réseaux sociaux de créateurs VIP
const getVipSocialInfo = (u) => {
    if (!u) return null;

    // 1. Twitch
    const twitchRaw = u.twitch || u.twitchChannel || u.twitchUrl;
    if (twitchRaw && typeof twitchRaw === 'string' && twitchRaw.trim()) {
        const val = twitchRaw.trim();
        const url = val.startsWith('http') ? val : `https://twitch.tv/${val.replace(/^@/, '')}`;
        const name = val.replace(/^https?:\/\/(www\.)?twitch\.tv\//i, '').replace(/^@/, '');
        return {
            platform: 'twitch',
            label: 'Twitch',
            url,
            name,
            borderColor: '#9146FF',
            bgColor: 'rgba(145, 70, 255, 0.16)',
            color: '#A970FF',
            hoverBg: '#9146FF',
            hoverColor: '#FFFFFF',
            shadow: '0 0 8px rgba(145, 70, 255, 0.35)',
            hoverShadow: '0 0 12px rgba(145, 70, 255, 0.8)'
        };
    }

    // 2. YouTube
    const ytRaw = u.youtube || u.youtubeChannel || u.youtubeUrl;
    if (ytRaw && typeof ytRaw === 'string' && ytRaw.trim()) {
        const val = ytRaw.trim();
        let url = val;
        if (!val.startsWith('http')) {
            if (val.startsWith('@')) {
                url = `https://youtube.com/${val}`;
            } else if (val.startsWith('UC') || val.startsWith('channel/')) {
                url = `https://youtube.com/channel/${val.replace(/^channel\//, '')}`;
            } else {
                url = `https://youtube.com/@${val}`;
            }
        }
        const name = val.replace(/^https?:\/\/(www\.)?youtube\.com\/(@|channel\/)?/i, '@');
        return {
            platform: 'youtube',
            label: 'YouTube',
            url,
            name: name.startsWith('@') ? name : `@${name}`,
            borderColor: '#FF0000',
            bgColor: 'rgba(255, 0, 0, 0.16)',
            color: '#FF4D4D',
            hoverBg: '#FF0000',
            hoverColor: '#FFFFFF',
            shadow: '0 0 8px rgba(255, 0, 0, 0.35)',
            hoverShadow: '0 0 12px rgba(255, 0, 0, 0.8)'
        };
    }

    // 3. TikTok
    const tiktokRaw = u.tiktok || u.tiktokChannel || u.tiktokUrl;
    if (tiktokRaw && typeof tiktokRaw === 'string' && tiktokRaw.trim()) {
        const val = tiktokRaw.trim();
        const url = val.startsWith('http') ? val : `https://tiktok.com/@${val.replace(/^@/, '')}`;
        const name = val.replace(/^https?:\/\/(www\.)?tiktok\.com\/(@)?/i, '@');
        return {
            platform: 'tiktok',
            label: 'TikTok',
            url,
            name: name.startsWith('@') ? name : `@${name}`,
            borderColor: '#00F2FE',
            bgColor: 'rgba(0, 242, 254, 0.16)',
            color: '#00F2FE',
            hoverBg: '#050B14',
            hoverColor: '#00F2FE',
            shadow: '0 0 8px rgba(0, 242, 254, 0.35)',
            hoverShadow: '0 0 12px #00F2FE, 0 0 16px rgba(255, 0, 79, 0.7)'
        };
    }

    // 4. Instagram
    const instaRaw = u.instagram || u.insta || u.instagramUrl;
    if (instaRaw && typeof instaRaw === 'string' && instaRaw.trim()) {
        const val = instaRaw.trim();
        const url = val.startsWith('http') ? val : `https://instagram.com/${val.replace(/^@/, '')}`;
        const name = val.replace(/^https?:\/\/(www\.)?instagram\.com\//i, '').replace(/^@/, '');
        return {
            platform: 'instagram',
            label: 'Instagram',
            url,
            name,
            borderColor: '#E1306C',
            bgColor: 'rgba(225, 48, 108, 0.16)',
            color: '#FF6492',
            hoverBg: 'linear-gradient(45deg, #FD5949, #D6249F)',
            hoverColor: '#FFFFFF',
            shadow: '0 0 8px rgba(225, 48, 108, 0.35)',
            hoverShadow: '0 0 12px rgba(225, 48, 108, 0.8)'
        };
    }

    return null;
};

// Badge VIP cliquable affiché à droite du pseudo dans les classements
const VipSocialLink = ({ user, isMobile }) => {
    const social = getVipSocialInfo(user);
    if (!social) return null;

    return (
        <a 
            href={social.url}
            target="_blank"
            rel="noopener noreferrer"
            title={`${social.label}: ${social.name}`}
            onClick={(e) => e.stopPropagation()}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: isMobile ? '24px' : '26px',
                height: isMobile ? '24px' : '26px',
                borderRadius: '6px',
                background: social.bgColor,
                border: `1px solid ${social.borderColor}`,
                color: social.color,
                flexShrink: 0,
                textDecoration: 'none',
                boxShadow: social.shadow,
                transition: 'all 0.2s ease',
                cursor: 'pointer',
                marginLeft: 'auto'
            }}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = social.hoverBg;
                e.currentTarget.style.color = social.hoverColor;
                e.currentTarget.style.transform = 'scale(1.12)';
                e.currentTarget.style.boxShadow = social.hoverShadow;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = social.bgColor;
                e.currentTarget.style.color = social.color;
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.boxShadow = social.shadow;
            }}
        >
            {social.platform === 'twitch' && <IconTwitch size="15px" />}
            {social.platform === 'youtube' && <IconYouTube size="16px" />}
            {social.platform === 'tiktok' && <IconTikTok size="15px" />}
            {social.platform === 'instagram' && <IconInstagram size="15px" />}
        </a>
    );
};

// Formate une date d'expiration pour l'affichage (ex: "29 novembre", "November 29")
const formatExpirationDate = (dateOrIso, lang = 'fr') => {
    if (!dateOrIso) return '';
    const d = new Date(dateOrIso);
    if (isNaN(d.getTime())) return '';
    try {
        const locale = lang?.startsWith('en') ? 'en-US' : (lang?.startsWith('es') ? 'es-ES' : 'fr-FR');
        return d.toLocaleDateString(locale, { day: 'numeric', month: 'long' });
    } catch {
        return d.toLocaleDateString();
    }
};

// Formate une date courte (ex: "29 nov.", "Nov 29")
const formatShortDate = (dateOrIso, lang = 'fr') => {
    if (!dateOrIso) return '';
    const d = new Date(dateOrIso);
    if (isNaN(d.getTime())) return '';
    try {
        const locale = lang?.startsWith('en') ? 'en-US' : (lang?.startsWith('es') ? 'es-ES' : 'fr-FR');
        return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
    } catch {
        return d.toLocaleDateString();
    }
};

// ==========================================
// BADGES DE RANG (Touriste, Voyageur, Aventurier, Explorateur, Légende)
// ==========================================
const getRankData = (rankNameOrKey) => {
    const r = (rankNameOrKey || '').toString().toLowerCase().trim();
    if (r.includes('légende') || r.includes('legende') || r.includes('legend') || r.includes('leyenda')) {
        return {
            key: 'legend',
            title: 'Légende',
            color: '#FFD700',
            bgGlow: 'rgba(255, 215, 0, 0.65)',
            bgGlowLow: 'rgba(255, 215, 0, 0.28)',
            borderColor: '#FFD700',
            svg: badgeLegende
        };
    }
    if (r.includes('explorateur') || r.includes('explorer') || r.includes('explorador')) {
        return {
            key: 'explorer',
            title: 'Explorateur',
            color: '#FF7F50',
            bgGlow: 'rgba(255, 127, 80, 0.65)',
            bgGlowLow: 'rgba(255, 127, 80, 0.28)',
            borderColor: '#FF7F50',
            svg: badgeExplorateur
        };
    }
    if (r.includes('aventurier') || r.includes('adventurer') || r.includes('aventurero')) {
        return {
            key: 'adventurer',
            title: 'Aventurier',
            color: '#9D4EDD',
            bgGlow: 'rgba(157, 78, 221, 0.65)',
            bgGlowLow: 'rgba(157, 78, 221, 0.28)',
            borderColor: '#9D4EDD',
            svg: badgeAventurier
        };
    }
    if (r.includes('voyageur') || r.includes('traveler') || r.includes('traveller') || r.includes('viajero')) {
        return {
            key: 'traveler',
            title: 'Voyageur',
            color: '#4CE0D2',
            bgGlow: 'rgba(76, 224, 209, 0.65)',
            bgGlowLow: 'rgba(76, 224, 209, 0.28)',
            borderColor: '#4CE0D2',
            svg: badgeVoyageur
        };
    }
    return {
        key: 'tourist',
        title: 'Touriste',
        color: '#888E8E',
        bgGlow: 'rgba(136, 142, 142, 0.55)',
        bgGlowLow: 'rgba(136, 142, 142, 0.22)',
        borderColor: '#888E8E',
        svg: badgeTouriste
    };
};

const RankBadge = ({ rank = 'Touriste', size = 42, style = {}, title }) => {
    const rankInfo = getRankData(rank);
    const s = typeof size === 'number' ? `${size}px` : size;
    const badgeTitle = title || rankInfo.title;

    return (
        <div 
            title={badgeTitle}
            style={{
                width: s,
                height: s,
                minWidth: s,
                minHeight: s,
                borderRadius: '50%',
                overflow: 'hidden',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxSizing: 'border-box',
                position: 'relative',
                backgroundColor: 'transparent',
                ...style
            }}
        >
            <img 
                src={rankInfo.svg} 
                alt={badgeTitle}
                style={{
                    width: '100%',
                    height: '100%',
                    transform: 'scale(1.14)',
                    display: 'block',
                    objectFit: 'cover'
                }}
            />
        </div>
    );
};

// Composant du point découvert sur la carte (Ping Radar)
const MapPing = ({ lat, lng, onClick, showWave }) => {
    return (
        <OverlayView position={{ lat, lng }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
            <div 
                onClick={onClick}
                style={{
                    position: 'absolute',
                    transform: 'translate(-50%, -50%)', 
                    width: '30px', 
                    height: '30px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'transform 0.2s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1.4)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translate(-50%, -50%) scale(1)'}
            >
                {/* Le cœur du ping (Toujours visible) */}
                <div style={{
                    width: '8px',
                    height: '8px',
                    backgroundColor: '#4CE0D2',
                    borderRadius: '50%',
                    boxShadow: '0 0 10px #4CE0D2',
                    zIndex: 2
                }} />
                
                {/* L'onde radar (Toujours là, mais dynamique selon showWave) */}
                <div style={{
                    position: 'absolute',
                    // Si showWave est faux, l'onde est plus petite (20px au lieu de 30px)
                    width: showWave ? '100%' : '20px',
                    height: showWave ? '100%' : '20px',
                    // Si showWave est faux, le trait est plus fin et transparent
                    border: showWave ? '2px solid #4CE0D2' : '1px solid rgba(76, 224, 209, 0.3)',
                    borderRadius: '50%',
                    // Si showWave est faux, l'animation est plus lente (3s au lieu de 2s)
                    animation: showWave 
                        ? 'pingRadar 2s infinite cubic-bezier(0.25, 1, 0.5, 1)'
                        : 'pingRadar 3.5s infinite cubic-bezier(0.25, 1, 0.5, 1)',
                    zIndex: 1
                }} />
            </div>
        </OverlayView>
    );
};

const DisplayZoomModal = ({ zoom, onClose, isMobile, t, showContinueButton = false }) => {
    // On récupère i18n pour connaître la langue actuelle
    const { i18n } = useTranslation();
    
    // On stocke d'emblée les données passées en props (zoom)
    const [liveData, setLiveData] = useState(zoom);

    useEffect(() => {
        const fetchLiveData = async () => {
            // Si l'objet n'a pas d'ID, on arrête ici (c'est le cas lors de la victoire)
            if (!zoom || !zoom.id) return; 
            
            try {
                const docRef = doc(db, 'challenges_france', String(zoom.id));
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    // On fusionne les données au cas où la BDD n'aurait pas l'image ou l'URL
                    setLiveData({ ...zoom, ...docSnap.data() });
                }
            } catch (error) {
                console.error("Erreur lors de la récupération des données en direct :", error);
            }
        };

        fetchLiveData();
    }, [zoom.id, zoom]); // Ajout de 'zoom' dans les dépendances pour la sécurité

    if (!zoom) return null;

    const getVideoInfo = (url) => {
        if (!url) return { embedUrl: null, isVertical: false, type: null };

        if (url.includes('instagram.com')) {
            const match = url.match(/(?:p|reel)\/([A-Za-z0-9_-]+)/);
            if (match && match[1]) {
                return { 
                    embedUrl: `https://www.instagram.com/p/${match[1]}/embed/`, 
                    isVertical: true, 
                    type: 'instagram' 
                };
            }
        }

        let videoId = null;
        const isVertical = url.includes('/shorts/');
        if (isVertical) {
            const parts = url.split('/shorts/');
            videoId = parts[1]?.split(/[?&#]/)[0];
        } else if (url.includes('youtu.be/')) {
            videoId = url.split('youtu.be/')[1]?.split(/[?&#]/)[0];
        } else if (url.includes('watch?v=')) {
            videoId = url.split('watch?v=')[1]?.split(/[?&#]/)[0];
        } else if (url.includes('embed/')) {
            videoId = url.split('embed/')[1]?.split(/[?&#]/)[0];
        }
        
        return { 
            embedUrl: videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=0&rel=0` : null, 
            isVertical, 
            type: 'youtube' 
        };
    };

    // On récupère l'URL vidéo fraîche
    const { embedUrl, isVertical, type } = getVideoInfo(liveData.info || liveData.videoUrl || zoom.youtubeUrl);

    // Détermination dynamique du nom selon la langue de l'utilisateur
    const currentLang = i18n.language ? i18n.language.substring(0, 2).toUpperCase() : 'FR';
    let displayTitle = zoom.title || zoom.name || t('zoom_discovered_fallback');
    
    if (currentLang === 'FR' && liveData.name_FR) {
        displayTitle = liveData.name_FR;
    } else if (currentLang === 'ES' && liveData.name_ES) {
        displayTitle = liveData.name_ES;
    } else if (liveData.name) {
        displayTitle = liveData.name; // Anglais par défaut
    }

    return (
        <div style={{ position: 'fixed', inset: 0, zIndex: 10020, background: 'rgba(10, 15, 24, 0.88)', backdropFilter: 'blur(12px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: isMobile ? '12px' : '40px', boxSizing: 'border-box' }}>
            
            <div style={{ position: 'relative', width: '100%', maxWidth: isVertical ? '1200px' : '1500px', maxHeight: '92vh', background: 'linear-gradient(180deg, rgba(20, 27, 38, 0.95) 0%, rgba(12, 17, 26, 0.98) 100%)', border: '1px solid #4CE0D2', borderRadius: '16px', boxShadow: '0 0 35px rgba(76, 224, 209, 0.25)', display: 'flex', flexDirection: 'column', overflowY: 'auto', padding: isMobile ? '16px' : '20px', boxSizing: 'border-box' }}>
                
                {/* EN-TÊTE DE LA MODALE */}
                <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '24px', borderBottom: '1px solid rgba(76, 224, 209, 0.25)', paddingBottom: '20px', minHeight: isMobile ? '34px' : '44px' }}>
                    
                    {/* LE BADGE : Fixé absolument à gauche */}
                    <div style={{ position: 'absolute', left: 0 }}>
                        <span style={{ background: '#4CE0D2', color: '#141B26', fontWeight: 'bold', fontSize: isMobile ? '12px' : '18px', padding: '6px 14px', borderRadius: '12px', letterSpacing: '1px', fontFamily: 'Orbitron, sans-serif' }}>
                            #{String(zoom.id || zoom.index || '01').padStart(2, '0')}
                        </span>
                    </div>

                    {/* LE TEXTE : Parfaitement au centre */}
                    <h2 style={{ margin: 0, color: '#FFFFFF', fontSize: isMobile ? '18px' : '30px', fontWeight: 700, letterSpacing: '0.5px', fontFamily: 'Orbitron, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', maxWidth: isMobile ? '50%' : '65%' }}>
                        {displayTitle.includes(':') ? (
                            <>
                                <span>{displayTitle.substring(0, displayTitle.indexOf(':')).trim()}</span>
                                <span style={{ fontSize: isMobile ? '12px' : '26px', color: '#4CE0D2', fontWeight: 400, marginTop: isMobile ? '2px' : '6px', letterSpacing: '0px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                    {displayTitle.substring(displayTitle.indexOf(':') + 1).trim()}
                                </span>
                            </>
                        ) : (
                            displayTitle
                        )}
                    </h2>

                    {/* LE BOUTON FERMER / CONTINUER : Fixé absolument à droite */}
                    <div style={{ position: 'absolute', right: isMobile ? (showContinueButton ? '-10px' : 0) : 0 }}>
                        {showContinueButton ? (
                            <button
                                onClick={onClose}
                                style={{
                                    background: 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)',
                                    color: '#0A0F18',
                                    border: 'none',
                                    borderRadius: isMobile ? '7px' : '10px',
                                    padding: isMobile ? '5px 4px' : '10px 22px',
                                    margin: 0,
                                    fontSize: isMobile ? '10px' : '14px',
                                    fontWeight: 'bold',
                                    cursor: 'pointer',
                                    fontFamily: 'Orbitron, sans-serif',
                                    letterSpacing: isMobile ? '0.2px' : '1px',
                                    textTransform: 'uppercase',
                                    boxShadow: '0 4px 15px rgba(76, 224, 209, 0.45)',
                                    transition: 'all 0.2s ease',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: isMobile ? '3px' : '6px',
                                    whiteSpace: 'nowrap'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(76, 224, 209, 0.7)';
                                    e.currentTarget.style.filter = 'brightness(1.1)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(76, 224, 209, 0.45)';
                                    e.currentTarget.style.filter = 'brightness(1)';
                                }}
                            >
                                <span>{t('continue_btn', 'Continuer')}</span>
                                <span style={{ fontSize: '1.1em' }}>▶</span>
                            </button>
                        ) : (
                            <button onClick={onClose} style={{ background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '50%', width: isMobile ? '34px' : '44px', height: isMobile ? '34px' : '44px', color: '#FFF', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: isMobile ? '18px' : '24px', transition: 'all 0.2s ease' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#4CE0D2'; e.currentTarget.style.color = '#4CE0D2'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)'; e.currentTarget.style.color = '#FFF'; }}>✕</button>
                        )}
                    </div>

                </div>

                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (isVertical ? '1.3fr 1fr' : '0.8fr 1.2fr'), gap: isMobile ? '16px' : '40px', alignItems: 'center' }}>
                    
                    <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(76, 224, 209, 0.3)', width: '100%' }}>
                        <img src={zoom.imageUrl || zoom.image} alt={displayTitle} style={{ width: '100%', height: 'auto', display: 'block' }} />
                    </div>

                    {embedUrl ? (
                        <div style={{ position: 'relative', borderRadius: '12px', overflow: 'hidden', border: '1px solid rgba(76, 224, 209, 0.3)', background: '#000', aspectRatio: isVertical ? '9 / 16' : '16 / 9', maxHeight: isVertical && !isMobile ? '75vh' : 'auto', maxWidth: isVertical && !isMobile ? '450px' : '100%', display: 'flex', margin: isVertical ? '0 auto' : '0', width: '100%' }}>
                            <iframe 
                                src={embedUrl} 
                                title={displayTitle} 
                                style={{ width: '100%', height: '100%', border: 'none' }} 
                                scrolling={type === 'instagram' ? 'no' : 'auto'}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                                allowFullScreen 
                            />
                        </div>
                    ) : (
                        <div style={{ borderRadius: '12px', border: '1px dashed rgba(76, 224, 209, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255, 255, 255, 0.4)', fontSize: isMobile ? '13px' : '18px', padding: '20px', textAlign: 'center', fontFamily: 'Orbitron, sans-serif' }}>
                            {t('no_video_archived')}
                        </div>
                    )}
                </div>

                {zoom.description && (
                    <div style={{ marginTop: isMobile ? '18px' : '32px', padding: isMobile ? '14px 18px' : '24px 32px', background: 'rgba(76, 224, 209, 0.06)', borderLeft: '4px solid #4CE0D2', borderRadius: '0 8px 8px 0', color: '#E0E6ED', fontSize: isMobile ? '13px' : '18px', lineHeight: 1.6, fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {zoom.description}
                    </div>
                )}
            </div>
        </div>
    );
};


// // 2. Composant PokedexItem (Anti-décalage au survol)
const PokedexItem = ({ zoom, isMobile, onOpenDisplayZoom, onPlayGame, isPremiumUser = false, onUnlockPremium, t }) => {
    const [isHovered, setIsHovered] = useState(false);

    // --- CASE VERROUILLÉE ---
    if (!zoom.unlocked) {
        return (
            <div style={{ 
                width: '100%', 
                minWidth: 0,
                aspectRatio: '4 / 3',
                background: 'rgba(255, 255, 255, 0.05)', 
                border: '1px dashed rgba(76, 224, 209, 0.2)', 
                borderRadius: '6px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                color: 'rgba(255, 255, 255, 0.2)', 
                fontSize: isMobile ? '12px' : '14px', 
                fontFamily: 'Orbitron, sans-serif',
                boxSizing: 'border-box'
            }}>
                #{String(zoom.id).padStart(3, '0')}
            </div>
        );
    }

    // --- CASE DÉVERROUILLÉE ---
    return (
        <div 
            style={{ 
                position: 'relative', 
                width: '100%', 
                minWidth: 0,
                aspectRatio: '4 / 3',
                boxSizing: 'border-box'
            }} 
            onMouseEnter={() => setIsHovered(true)} 
            onMouseLeave={() => setIsHovered(false)}
        >
            <div onClick={() => onOpenDisplayZoom(zoom)} style={{ 
                position: 'relative', 
                width: '100%', 
                height: '100%',
                aspectRatio: '4 / 3',
                borderRadius: '6px', 
                // CORRECTION ICI : La bordure reste TOUJOURS à 1px pour ne pas décaler le design
                border: '1px solid',
                borderColor: isHovered ? '#4CE0D2' : 'rgba(76, 224, 209, 0.5)', 
                overflow: 'hidden', 
                cursor: 'pointer', 
                transition: 'all 0.2s ease', 
                // L'astuce : Le 1er boxShadow (0 0 0 1px) simule le 2ème pixel de bordure sans casser la grille !
                boxShadow: isHovered ? '0 0 0 1px #4CE0D2, 0 0 10px rgba(76, 224, 209, 0.3)' : 'none',
                boxSizing: 'border-box'
            }}>
                <img src={zoom.imageUrl || zoom.image} alt={`Zoom ${zoom.id}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                
                {isMobile && (
                    isPremiumUser ? (
                        <button onClick={(e) => { e.stopPropagation(); onPlayGame(zoom); }} style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(76, 224, 209, 0.9)', border: 'none', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#141B26', boxShadow: '0 2px 5px rgba(0,0,0,0.5)', cursor: 'pointer', fontSize: '10px' }}>▶</button>
                    ) : (
                        <button onClick={(e) => { e.stopPropagation(); if (onUnlockPremium) onUnlockPremium(); }} title={t('unlock_premium', 'Débloquer Premium')} style={{ position: 'absolute', bottom: '4px', right: '4px', background: 'rgba(20, 27, 38, 0.9)', border: '1px solid #D4AF37', borderRadius: '50%', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFD700', boxShadow: '0 2px 5px rgba(0,0,0,0.6)', cursor: 'pointer', fontSize: '11px' }}>🔒</button>
                    )
                )}

                {!isMobile && (
                    isPremiumUser ? (
                        <button onClick={(e) => { e.stopPropagation(); onPlayGame(zoom); }} 
                            style={{ 
                                position: 'absolute', 
                                bottom: '6px', 
                                left: '50%', 
                                transform: isHovered ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(10px)', 
                                opacity: isHovered ? 1 : 0, 
                                background: 'rgba(10, 15, 24, 0.9)', 
                                color: '#4CE0D2', 
                                border: '1px solid #4CE0D2', 
                                borderRadius: '10px', 
                                padding: '4px 10px', 
                                fontSize: '11px',
                                cursor: 'pointer', 
                                transition: 'all 0.2s ease', 
                                pointerEvents: isHovered ? 'auto' : 'none', 
                                fontFamily: 'Orbitron, sans-serif',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.6)'
                            }}>
                            ▶ {t('play_button')}
                        </button>
                    ) : (
                        <button onClick={(e) => { e.stopPropagation(); if (onUnlockPremium) onUnlockPremium(); }} 
                            style={{ 
                                position: 'absolute', 
                                bottom: '6px', 
                                left: '50%', 
                                transform: isHovered ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(10px)', 
                                opacity: isHovered ? 1 : 0, 
                                background: 'rgba(10, 15, 24, 0.95)', 
                                color: '#FFD700', 
                                border: '1px solid #D4AF37', 
                                borderRadius: '10px', 
                                padding: '4px 10px', 
                                fontSize: '11px', 
                                cursor: 'pointer', 
                                transition: 'all 0.2s ease', 
                                pointerEvents: isHovered ? 'auto' : 'none', 
                                fontFamily: 'Orbitron, sans-serif',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 10px rgba(0,0,0,0.6)'
                            }}>
                            🔒 {t('unlock_premium', 'Débloquer Premium')}
                        </button>
                    )
                )}
            </div>
        </div>
    );
};


// 3. Modale du Pokédex (Espaces ajustés)
const TOTAL_ZOOMS = 150;

// Adresses e-mails autorisées pour les fonctionnalités d'administration (export statistiques, etc.)
const ADMIN_EMAILS = [
    'jeremygryszata@gmail.com',
    'contact@zoomap.world'
];

const PokedexModal = ({ onClose, mesZoomsTrouves, totalZooms = TOTAL_ZOOMS, isMobile, onOpenDisplayZoom, onPlayGame, isPremiumUser = false, onUnlockPremium, t }) => {
    const gridItems = Array.from({ length: totalZooms }, (_, index) => {
        const expectedId = index + 1;
        const foundZoom = mesZoomsTrouves.find(z => z.id === expectedId);
        return foundZoom ? { ...foundZoom, unlocked: true } : { id: expectedId, unlocked: false };
    });

    return (
        <div style={{ 
            position: 'fixed', 
            inset: 0, 
            zIndex: 10010, 
            background: 'rgba(10, 15, 24, 0.95)', 
            backdropFilter: 'blur(10px)', 
            display: 'flex', 
            flexDirection: 'column', 
            
            // CORRECTIONS DES ESPACES ICI (Haut | Droite | Bas | Gauche)
            // - Sur PC : on gratte en haut (16px), on ajoute à droite (44px) pour la scrollbar, on réduit un peu à gauche (20px).
            padding: isMobile ? '12px 16px 16px 16px' : '16px 44px 32px 20px', 
            boxSizing: 'border-box', 
            overflowY: 'auto' 
        }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? '16px' : '20px' }}>
                
                <h2 style={{ margin: 0, fontFamily: 'Orbitron, sans-serif', fontSize: isMobile ? '18px' : '24px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                    <span style={{ color: '#FFF' }}>{t('collection_title')} :</span>
                    <span style={{ color: '#4CE0D2' }}>
                        {mesZoomsTrouves.length} {t('zooms_found')} {totalZooms}
                    </span>
                </h2>
                
                <button onClick={onClose} style={{ background: 'transparent', border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: '50%', width: '40px', height: '40px', color: '#FFF', cursor: 'pointer', fontSize: '20px' }}>✕</button>
            </div>
            
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: isMobile ? 'repeat(auto-fill, minmax(80px, 1fr))' : 'repeat(10, minmax(0, 1fr))', 
                gap: isMobile ? '8px' : '12px', 
                paddingBottom: '40px' 
            }}>
                {gridItems.map((item) => (
                    <PokedexItem key={item.id} zoom={item} isMobile={isMobile} onOpenDisplayZoom={onOpenDisplayZoom} onPlayGame={onPlayGame} isPremiumUser={isPremiumUser} onUnlockPremium={onUnlockPremium} t={t} />
                ))}
            </div>
        </div>
    );
};

// ==========================================
// MODALES DUEL : INTRODUCTION & FINALISATION
// ==========================================
const DuelIntroModal = ({ player1, player2, isMobile, onFinish, t }) => {
    const [countdown, setCountdown] = useState(3);
    const onFinishRef = useRef(onFinish);

    const playCountdownBeep = useCallback((isGo = false) => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return;
            const ctx = new AudioCtx();
            if (ctx.state === 'suspended') ctx.resume();

            const now = ctx.currentTime;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();

            // 3, 2, 1 : Bip dynamique à 587.33 Hz (D5) -> GO ! : Bip éclatant à 1174.66 Hz (D6)
            osc.type = isGo ? 'triangle' : 'sine';
            osc.frequency.setValueAtTime(isGo ? 1174.66 : 587.33, now);

            gain.gain.setValueAtTime(0, now);
            gain.gain.linearRampToValueAtTime(isGo ? 0.35 : 0.22, now + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.001, now + (isGo ? 0.38 : 0.13));

            osc.connect(gain);
            gain.connect(ctx.destination);

            osc.start(now);
            osc.stop(now + (isGo ? 0.4 : 0.15));
        } catch (e) {
            console.log("Audio countdown bloqué :", e);
        }
    }, []);

    useEffect(() => {
        onFinishRef.current = onFinish;
    }, [onFinish]);

    useEffect(() => {
        // Déclenche le bip correspondant dès que le chiffre change (3, 2, 1, et GO)
        if (countdown > 0) {
            playCountdownBeep(false);
        } else if (countdown === 0) {
            playCountdownBeep(true);
        }
    }, [countdown, playCountdownBeep]);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(timer);
                    setTimeout(() => {
                        if (onFinishRef.current) {
                            onFinishRef.current();
                        }
                    }, 650);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const p1Grade = player1?.soloGrade || 'Touriste';
    const p2Grade = player2?.soloGrade || 'Touriste';
    const p1RankData = getRankData(p1Grade);
    const p2RankData = getRankData(p2Grade);

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10025,
            background: 'radial-gradient(ellipse at center, rgba(16, 24, 40, 0.96) 0%, rgba(6, 9, 15, 0.98) 100%)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '20px 16px' : '40px 24px',
            boxSizing: 'border-box',
            userSelect: 'none'
        }}>
            {/* Titre supérieur */}
            <div style={{
                background: 'linear-gradient(135deg, rgba(76, 224, 209, 0.2) 0%, rgba(30, 60, 114, 0.3) 100%)',
                border: '1px solid #4CE0D2',
                borderRadius: '24px',
                padding: '6px 20px',
                color: '#4CE0D2',
                fontFamily: 'Orbitron, sans-serif',
                fontWeight: 'bold',
                fontSize: isMobile ? '12px' : '15px',
                letterSpacing: '2px',
                textTransform: 'uppercase',
                boxShadow: '0 0 15px rgba(76, 224, 209, 0.3)',
                marginBottom: isMobile ? '20px' : '36px',
                animation: 'pulse 2s infinite',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
            }}>
                <IconDuel size={isMobile ? "16px" : "20px"} color="#4CE0D2" />
                <span>{t('duel_mode', 'DUEL')}</span>
                <IconDuel size={isMobile ? "16px" : "20px"} color="#4CE0D2" />
            </div>

            {/* Face-off : Joueur 1 vs Joueur 2 */}
            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '16px' : '36px',
                width: '100%',
                maxWidth: '850px',
                marginBottom: isMobile ? '24px' : '36px'
            }}>
                {/* Joueur 1 */}
                <div style={{
                    flex: 1,
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '280px' : '330px',
                    background: 'rgba(20, 27, 38, 0.88)',
                    border: `2px solid ${p1RankData.borderColor || '#4CE0D2'}`,
                    borderRadius: '16px',
                    padding: isMobile ? '14px 16px' : '22px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: `0 0 25px ${p1RankData.bgGlowLow || 'rgba(76, 224, 209, 0.25)'}`,
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: '900',
                        fontSize: isMobile ? '17px' : '21px',
                        color: '#EAEDF2',
                        marginBottom: '10px',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: '100%'
                    }}>
                        {player1?.displayName || 'Joueur 1'}
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                        <RankBadge rank={p1Grade} size={isMobile ? 54 : 68} />
                    </div>

                    <div style={{
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '13px' : '15px',
                        color: p1RankData.color || '#4CE0D2',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>
                        {t(`rank_${p1RankData.key || 'tourist'}`, p1RankData.title || p1Grade)}
                    </div>
                </div>

                {/* Badge central VS */}
                <div style={{
                    width: isMobile ? '52px' : '72px',
                    height: isMobile ? '52px' : '72px',
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, #E03030 0%, #1A1E26 100%)',
                    border: '2px solid #FF6B6B',
                    boxShadow: '0 0 25px rgba(224, 48, 48, 0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: 'Orbitron, sans-serif',
                    fontWeight: '900',
                    fontSize: isMobile ? '20px' : '28px',
                    color: '#FFFFFF',
                    textShadow: '0 0 10px rgba(255, 107, 107, 0.9)',
                    flexShrink: 0
                }}>
                    VS
                </div>

                {/* Joueur 2 */}
                <div style={{
                    flex: 1,
                    width: isMobile ? '100%' : 'auto',
                    maxWidth: isMobile ? '280px' : '330px',
                    background: 'rgba(20, 27, 38, 0.88)',
                    border: `2px solid ${p2RankData.borderColor || '#E07A5F'}`,
                    borderRadius: '16px',
                    padding: isMobile ? '14px 16px' : '22px 24px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    boxShadow: `0 0 25px ${p2RankData.bgGlowLow || 'rgba(224, 122, 95, 0.25)'}`,
                    boxSizing: 'border-box'
                }}>
                    <div style={{
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontWeight: '900',
                        fontSize: isMobile ? '17px' : '21px',
                        color: '#EAEDF2',
                        marginBottom: '10px',
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: '100%'
                    }}>
                        {player2?.displayName || 'Joueur 2'}
                    </div>

                    <div style={{ marginBottom: '8px' }}>
                        <RankBadge rank={p2Grade} size={isMobile ? 54 : 68} />
                    </div>

                    <div style={{
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: 'bold',
                        fontSize: isMobile ? '13px' : '15px',
                        color: p2RankData.color || '#E07A5F',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>
                        {t(`rank_${p2RankData.key || 'tourist'}`, p2RankData.title || p2Grade)}
                    </div>
                </div>
            </div>

            {/* Compte à rebours animé */}
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px'
            }}>
                <div style={{
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: countdown === 0 ? (isMobile ? '44px' : '56px') : (isMobile ? '46px' : '62px'),
                    fontWeight: '900',
                    color: countdown === 0 ? '#3DDC97' : '#4CE0D2',
                    textShadow: countdown === 0 ? '0 0 25px rgba(61, 220, 151, 0.8)' : '0 0 25px rgba(76, 224, 209, 0.8)',
                    animation: 'pulse 0.8s infinite',
                    minHeight: '65px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    {countdown === 0 ? 'GO !' : countdown}
                </div>
                <div style={{
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: isMobile ? '12px' : '14px',
                    color: '#A0AEC0',
                    fontStyle: 'italic',
                    textAlign: 'center'
                }}>
                    {countdown === 0 ? t('duel_starting', 'C\'est parti !') : t('duel_get_ready', 'Préparez-vous à localiser la cible...')}
                </div>
            </div>
        </div>
    );
};

const KM_TO_MILES = 0.621371;

const formatDistance = (distKm, decimals = 0, isCompact = false, unit = 'km', t = null) => {
    if (distKm === null || distKm === undefined || isNaN(distKm) || distKm >= 99999) return '---';
    if (unit === 'miles') {
        const distMi = distKm * KM_TO_MILES;
        const formattedVal = decimals > 0 ? distMi.toFixed(decimals) : Math.round(distMi);
        const roundedInt = Math.round(distMi);
        let unitLabel = 'mi';
        if (!isCompact) {
            if (t) {
                unitLabel = roundedInt <= 1 ? t('unit_mile_single', 'mile') : t('unit_miles', 'miles');
            } else {
                unitLabel = roundedInt <= 1 ? 'mile' : 'miles';
            }
        }
        return `${formattedVal} ${unitLabel}`;
    } else {
        const formattedVal = decimals > 0 ? distKm.toFixed(decimals) : Math.round(distKm);
        return `${formattedVal} km`;
    }
};

const DuelOutroModal = ({ 
    isWin, 
    isTie, 
    isLost, 
    isRefund, 
    isForfeitWin, 
    myData, 
    oppData, 
    myDistance, 
    oppDistance, 
    isMobile, 
    onContinue, 
    distanceUnit = 'km',
    t 
}) => {
    const isDraw = isTie || isRefund;

    const myGrade = myData?.soloGrade || 'Touriste';
    const oppGrade = oppData?.soloGrade || 'Touriste';
    const myRankData = getRankData(myGrade);
    const oppRankData = getRankData(oppGrade);

    // Détermination Vainqueur / Perdant
    const winner = isWin ? myData : oppData;
    const winnerGrade = isWin ? myGrade : oppGrade;
    const winnerRankData = isWin ? myRankData : oppRankData;
    const winnerDist = isWin ? myDistance : oppDistance;

    const loser = isWin ? oppData : myData;
    const loserGrade = isWin ? oppGrade : myGrade;
    const loserRankData = isWin ? oppRankData : myRankData;
    const loserDist = isWin ? oppDistance : myDistance;

    return (
        <div style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10030,
            background: 'radial-gradient(ellipse at center, rgba(14, 20, 32, 0.97) 0%, rgba(6, 9, 15, 0.99) 100%)',
            backdropFilter: 'blur(16px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '20px 16px' : '36px 24px',
            boxSizing: 'border-box',
            userSelect: 'none',
            overflowY: 'auto'
        }}>
            {/* Titre Principal */}
            <div style={{ textAlign: 'center', marginBottom: isMobile ? '18px' : '28px' }}>
                <h1 style={{
                    margin: 0,
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: isMobile ? '26px' : '38px',
                    fontWeight: '900',
                    letterSpacing: '2px',
                    color: isDraw ? '#4CE0D2' : (isWin ? '#3DDC97' : '#E03030'),
                    textShadow: isDraw 
                        ? '0 0 25px rgba(76, 224, 209, 0.7)' 
                        : (isWin ? '0 0 30px rgba(61, 220, 151, 0.8)' : '0 0 30px rgba(224, 48, 48, 0.8)'),
                    animation: 'fadeIn 0.4s'
                }}>
                    {isDraw ? t('draw', 'MATCH NUL') : (isWin ? `🏆 ${t('victory_caps', 'VICTOIRE !')} 🏆` : t('defeat_caps', 'DÉFAITE'))}
                </h1>

                <p style={{
                    margin: '6px 0 0 0',
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: isMobile ? '12.5px' : '15px',
                    color: '#EAEDF2',
                    fontStyle: 'italic'
                }}>
                    {isDraw ? (
                        isRefund ? t('draw_desc', 'Aucun joueur n\'a joué, partie remboursée.') : t('tie_desc', 'Une précision parfaitement équivalente !')
                    ) : (
                        isForfeitWin 
                            ? t('duel_forfeit_win', 'Votre adversaire a déclaré forfait !')
                            : (isWin ? t('duel_win_desc', 'Vous avez été le plus proche de la cible !') : t('duel_loss_desc', 'Votre rival a été plus proche de la cible cette fois-ci.'))
                    )}
                </p>
            </div>

            {/* Arène / Cartes des joueurs */}
            <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '16px' : '36px',
                width: '100%',
                maxWidth: '820px',
                marginBottom: isMobile ? '24px' : '36px'
            }}>
                {isDraw ? (
                    /* CAS ÉGALITÉ : 2 Cartes équilibrées */
                    <>
                        {/* Joueur 1 (Moi) */}
                        <div style={{
                            flex: 1,
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '280px' : '330px',
                            background: 'rgba(20, 27, 38, 0.88)',
                            border: '2px solid #4CE0D2',
                            borderRadius: '16px',
                            padding: isMobile ? '14px' : '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 0 20px rgba(76, 224, 209, 0.3)',
                            boxSizing: 'border-box'
                        }}>
                            <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '900', fontSize: isMobile ? '16px' : '19px', color: '#EAEDF2', marginBottom: '8px' }}>
                                {myData?.displayName || 'Moi'}
                            </div>
                            <RankBadge rank={myGrade} size={isMobile ? 50 : 64} />
                            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '13px', color: myRankData.color, marginTop: '6px', fontWeight: 'bold' }}>
                                {t(`rank_${myRankData.key || 'tourist'}`, myRankData.title || myGrade)}
                            </div>
                            <div style={{ marginTop: '12px', background: 'rgba(76, 224, 209, 0.15)', border: '1px solid #4CE0D2', borderRadius: '8px', padding: '4px 14px', fontFamily: 'Orbitron, sans-serif', fontSize: '14px', color: '#4CE0D2', fontWeight: 'bold' }}>
                                🎯 {myDistance !== null && myDistance < 99999 ? formatDistance(myDistance, 0, false, distanceUnit, t) : '---'}
                            </div>
                        </div>

                        {/* Égalité */}
                        <div style={{ fontFamily: 'Orbitron, sans-serif', fontWeight: '900', fontSize: '24px', color: '#4CE0D2', opacity: 0.8 }}>
                            =
                        </div>

                        {/* Joueur 2 (Rival) */}
                        <div style={{
                            flex: 1,
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '280px' : '330px',
                            background: 'rgba(20, 27, 38, 0.88)',
                            border: '2px solid #4CE0D2',
                            borderRadius: '16px',
                            padding: isMobile ? '14px' : '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 0 20px rgba(76, 224, 209, 0.3)',
                            boxSizing: 'border-box'
                        }}>
                            <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '900', fontSize: isMobile ? '16px' : '19px', color: '#EAEDF2', marginBottom: '8px' }}>
                                {oppData?.displayName || 'Adversaire'}
                            </div>
                            <RankBadge rank={oppGrade} size={isMobile ? 50 : 64} />
                            <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: '13px', color: oppRankData.color, marginTop: '6px', fontWeight: 'bold' }}>
                                {t(`rank_${oppRankData.key || 'tourist'}`, oppRankData.title || oppGrade)}
                            </div>
                            <div style={{ marginTop: '12px', background: 'rgba(76, 224, 209, 0.15)', border: '1px solid #4CE0D2', borderRadius: '8px', padding: '4px 14px', fontFamily: 'Orbitron, sans-serif', fontSize: '14px', color: '#4CE0D2', fontWeight: 'bold' }}>
                                🎯 {oppDistance !== null && oppDistance < 99999 ? formatDistance(oppDistance, 0, false, distanceUnit, t) : '---'}
                            </div>
                        </div>
                    </>
                ) : (
                    /* CAS VAINQUEUR vs PERDANT */
                    <>
                        {/* CARTE DU VAINQUEUR (Mise en avant, dorée, couronne) */}
                        <div style={{
                            flex: 1,
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '290px' : '350px',
                            background: 'linear-gradient(180deg, rgba(255, 215, 0, 0.16) 0%, rgba(20, 27, 38, 0.95) 100%)',
                            border: '2px solid #FFD700',
                            borderRadius: '20px',
                            padding: isMobile ? '16px' : '24px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            boxShadow: '0 0 35px rgba(255, 215, 0, 0.45)',
                            transform: isMobile ? 'scale(1.02)' : 'scale(1.06)',
                            position: 'relative',
                            boxSizing: 'border-box'
                        }}>
                            {/* Couronne */}
                            <div style={{
                                position: 'absolute',
                                top: '-22px',
                                fontSize: '28px',
                                filter: 'drop-shadow(0 0 10px rgba(255, 215, 0, 0.9))',
                                animation: 'pulse 1.8s infinite'
                            }}>
                                👑
                            </div>

                            {/* Badge VAINQUEUR */}
                            <div style={{
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                color: '#0B0E14',
                                fontFamily: 'Orbitron, sans-serif',
                                fontWeight: '900',
                                fontSize: '12px',
                                letterSpacing: '1.5px',
                                padding: '3px 14px',
                                borderRadius: '12px',
                                marginBottom: '10px',
                                marginTop: '4px',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.5)'
                            }}>
                                {t('winner_badge', 'VAINQUEUR')}
                            </div>

                            <div style={{
                                fontFamily: 'Inter, system-ui, sans-serif',
                                fontWeight: '900',
                                fontSize: isMobile ? '18px' : '22px',
                                color: '#FFF',
                                marginBottom: '8px',
                                textAlign: 'center'
                            }}>
                                {winner?.displayName || 'Vainqueur'}
                            </div>

                            <div style={{ marginBottom: '6px' }}>
                                <RankBadge rank={winnerGrade} size={isMobile ? 56 : 70} />
                            </div>

                            <div style={{
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: isMobile ? '12px' : '14px',
                                color: winnerRankData.color,
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                            }}>
                                {t(`rank_${winnerRankData.key || 'tourist'}`, winnerRankData.title || winnerGrade)}
                            </div>

                            <div style={{
                                marginTop: '14px',
                                background: 'rgba(61, 220, 151, 0.2)',
                                border: '1px solid #3DDC97',
                                borderRadius: '10px',
                                padding: '6px 18px',
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: isMobile ? '15px' : '17px',
                                color: '#3DDC97',
                                fontWeight: '900',
                                boxShadow: '0 0 15px rgba(61, 220, 151, 0.3)'
                            }}>
                                🎯 {winnerDist !== null && winnerDist < 99999 ? formatDistance(winnerDist, 0, false, distanceUnit, t) : formatDistance(0, 0, false, distanceUnit, t)}
                            </div>
                        </div>

                        {/* CARTE DU PERDANT (En retrait, assombrie) */}
                        <div style={{
                            flex: 1,
                            width: isMobile ? '100%' : 'auto',
                            maxWidth: isMobile ? '260px' : '310px',
                            background: 'rgba(20, 27, 38, 0.55)',
                            border: '1px solid rgba(255, 255, 255, 0.15)',
                            borderRadius: '16px',
                            padding: isMobile ? '12px' : '18px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            opacity: 0.68,
                            filter: 'grayscale(25%)',
                            transform: isMobile ? 'scale(0.96)' : 'scale(0.95)',
                            boxSizing: 'border-box'
                        }}>
                            <div style={{
                                background: 'rgba(224, 48, 48, 0.2)',
                                border: '1px solid rgba(224, 48, 48, 0.4)',
                                color: '#FF6B6B',
                                fontFamily: 'Orbitron, sans-serif',
                                fontWeight: 'bold',
                                fontSize: '11px',
                                letterSpacing: '1px',
                                padding: '2px 12px',
                                borderRadius: '10px',
                                marginBottom: '10px'
                            }}>
                                {t('defeat_badge', 'DÉFAITE')}
                            </div>

                            <div style={{
                                fontFamily: 'Inter, system-ui, sans-serif',
                                fontWeight: 'bold',
                                fontSize: isMobile ? '16px' : '18px',
                                color: '#A0AEC0',
                                marginBottom: '8px',
                                textAlign: 'center'
                            }}>
                                {loser?.displayName || 'Adversaire'}
                            </div>

                            <div style={{ marginBottom: '6px' }}>
                                <RankBadge rank={loserGrade} size={isMobile ? 46 : 56} />
                            </div>

                            <div style={{
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: '12px',
                                color: '#A0AEC0',
                                fontWeight: 'bold',
                                textTransform: 'uppercase'
                            }}>
                                {t(`rank_${loserRankData.key || 'tourist'}`, loserRankData.title || loserGrade)}
                            </div>

                            <div style={{
                                marginTop: '12px',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                borderRadius: '8px',
                                padding: '4px 14px',
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: '13px',
                                color: '#EAEDF2',
                                fontWeight: 'bold'
                            }}>
                                🎯 {loserDist !== null && loserDist < 99999 ? formatDistance(loserDist, 0, false, distanceUnit, t) : '---'}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Bouton Continuer */}
            <button
                onClick={onContinue}
                style={{
                    background: 'linear-gradient(135deg, #4CE0D2 0%, #20B2AA 100%)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: isMobile ? '12px 36px' : '14px 50px',
                    color: '#0B0E14',
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: isMobile ? '15px' : '17px',
                    fontWeight: '900',
                    letterSpacing: '1.5px',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(76, 224, 209, 0.5)',
                    transition: 'all 0.2s ease',
                    textTransform: 'uppercase'
                }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; e.currentTarget.style.boxShadow = '0 6px 25px rgba(76, 224, 209, 0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(76, 224, 209, 0.5)'; }}
            >
                {t('continue_btn', 'Continuer')}
            </button>
        </div>
    );
};

// ==========================================
// COMPOSANT D'ANIMATION DE FIN DE PARTIE
// ==========================================
const ScoreAnimation = ({ score = 0, isMobile, gameMode = 'solo', onComplete, availableZooms = 0, onStartNewGame, onUnlockPremium, isPremiumUser = false, countdownText = '', onOpenLeaderboard }) => {
    const { t } = useTranslation();
    const [displayScore, setDisplayScore] = useState(0);
    const [gaugePercent, setGaugePercent] = useState(0);

    // Détermination dynamique du rang selon le score affiché
    const getRankInfo = (val) => {
        if (val >= 4000) {
            return {
                name: t('rank_legend', 'Légende'),
                color: '#FFD700',
                isLegend: true
            };
        }
        if (val >= 3000) {
            return { name: t('rank_explorer', 'Explorateur'), color: '#FF7F50', isLegend: false };
        }
        if (val >= 2000) {
            return { name: t('rank_adventurer', 'Aventurier'), color: '#9D4EDD', isLegend: false };
        }
        if (val >= 1000) {
            return { name: t('rank_traveler', 'Voyageur'), color: '#4CE0D2', isLegend: false };
        }
        return { name: t('rank_tourist', 'Touriste'), color: '#5C6675', isLegend: false };
    };

    const rank = getRankInfo(displayScore);

    // Animation d'incrémentation du score (ease-out sur 3 secondes)
    useEffect(() => {
        const targetScore = Math.max(0, Number(score) || 0);
        if (targetScore === 0) {
            setDisplayScore(0);
            return;
        }

        let animationFrameId;
        const startTime = performance.now();
        const duration = 3000; // 3 secondes d'animation

        const animate = (currentTime) => {
            const elapsed = currentTime - startTime;
            const t = Math.min(1, elapsed / duration);
            // Formule ease-out cubique (rapide au début, lent à la fin)
            const easeOut = 1 - Math.pow(1 - t, 3);
            const currentVal = Math.round(targetScore * easeOut);
            setDisplayScore(currentVal);

            if (t < 1) {
                animationFrameId = requestAnimationFrame(animate);
            }
        };

        animationFrameId = requestAnimationFrame(animate);

        return () => {
            if (animationFrameId) {
                cancelAnimationFrame(animationFrameId);
            }
        };
    }, [score]);

    // Remplissage autonome de la jauge (déclenché au montage, animé par transition CSS fluide 3s)
    // Cela isole la jauge du re-rendering du score et supprime tout tremblement
    useEffect(() => {
        const targetPercent = Math.min(100, Math.max(0, (Number(score) / 6000) * 100));
        const timer = setTimeout(() => {
            setGaugePercent(targetPercent);
        }, 50);
        return () => clearTimeout(timer);
    }, [score]);

    // Rangs disponibles pour l'affichage de la jauge
    const allRanks = [
        { key: 'rank_legend', defaultName: 'Légende', color: '#FFD700', min: 4000, isLegend: true },
        { key: 'rank_explorer', defaultName: 'Explorateur', color: '#FF7F50', min: 3000, isLegend: false },
        { key: 'rank_adventurer', defaultName: 'Aventurier', color: '#9D4EDD', min: 2000, isLegend: false },
        { key: 'rank_traveler', defaultName: 'Voyageur', color: '#4CE0D2', min: 1000, isLegend: false },
        { key: 'rank_tourist', defaultName: 'Touriste', color: '#5C6675', min: 0, isLegend: false }
    ];

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(11, 14, 20, 0.95)',
            backdropFilter: 'blur(10px)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box',
            padding: isMobile ? '20px' : '40px'
        }}>
            {/* Bloc principal : Score à gauche/haut, Jauge & Rangs isolés à droite/bas */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: isMobile ? 'column' : 'row',
                gap: isMobile ? '30px' : '70px',
                width: '100%',
                maxWidth: '850px'
            }}>
                {/* Élément 1 : Le Score (Boîte rigide avec tabular-nums pour éviter tout tremblement) */}
                <div style={{
                    width: isMobile ? '280px' : '360px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'baseline',
                        justifyContent: 'center',
                        gap: isMobile ? '8px' : '12px',
                        fontSize: isMobile ? '56px' : '82px',
                        fontWeight: 'bold',
                        fontFamily: 'Orbitron, sans-serif',
                        fontVariantNumeric: 'tabular-nums',
                        color: '#EAEDF2',
                        lineHeight: 1.1,
                        whiteSpace: 'nowrap',
                        textShadow: rank.isLegend
                            ? '0 0 10px rgba(255, 215, 0, 0.7), 0 0 25px rgba(255, 215, 0, 0.5)'
                            : `0 0 20px ${rank.color}`,
                        transition: 'text-shadow 0.2s ease'
                    }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{displayScore}</span>
                        <span style={{
                            fontSize: isMobile ? '22px' : '32px',
                            color: rank.color,
                            fontWeight: 'bold',
                            letterSpacing: '1px'
                        }}>
                            pts
                        </span>
                    </div>
                    <div style={{
                        fontSize: isMobile ? '16px' : '22px',
                        fontWeight: 'bold',
                        fontFamily: 'Orbitron, sans-serif',
                        letterSpacing: '3px',
                        textTransform: 'uppercase',
                        color: rank.color,
                        marginTop: '10px',
                        textShadow: rank.isLegend ? '0 0 10px rgba(255, 215, 0, 0.7)' : 'none',
                        transition: 'color 0.2s ease'
                    }}>
                        {rank.name}
                    </div>

                    {/* Bouton Classement (Solo ou Daily Zoom) - Juste en dessous du score */}
                    {(gameMode === 'daily' || gameMode === 'solo') && onOpenLeaderboard && (
                        <button
                            onClick={onOpenLeaderboard}
                            style={{
                                marginTop: isMobile ? '14px' : '20px',
                                background: 'rgba(20, 27, 38, 0.85)',
                                color: '#EAEDF2',
                                border: '1.5px solid #B24BF3',
                                borderRadius: '12px',
                                padding: isMobile ? '10px 18px' : '11px 24px',
                                fontSize: isMobile ? '12px' : '13px',
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                fontFamily: 'Orbitron, sans-serif',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                boxShadow: '0 4px 16px rgba(178, 75, 243, 0.25)',
                                transition: 'all 0.2s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                width: isMobile ? '100%' : 'auto',
                                maxWidth: isMobile ? '280px' : '320px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.04)';
                                e.currentTarget.style.borderColor = '#C084FC';
                                e.currentTarget.style.boxShadow = '0 6px 24px rgba(178, 75, 243, 0.5)';
                                e.currentTarget.style.color = '#F3E8FF';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.borderColor = '#B24BF3';
                                e.currentTarget.style.boxShadow = '0 4px 16px rgba(178, 75, 243, 0.25)';
                                e.currentTarget.style.color = '#EAEDF2';
                            }}
                        >
                            <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🏆</span>
                            <span>{gameMode === 'daily' ? t('btn_leaderboard_daily', 'Classement Daily Zoom') : t('btn_leaderboard_solo', 'Classement Solo')}</span>
                        </button>
                    )}
                </div>

                {/* Élément 2 : Jauge au centre, Nombres de légende à gauche, Rangs à droite (sans tiret) */}
                <div style={{
                    width: isMobile ? '100%' : '360px',
                    maxWidth: isMobile ? '340px' : '360px',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: isMobile ? '10px' : '16px'
                }}>
                    {/* Colonne Gauche : Les Nombres de légende (alignés à droite vers la jauge) */}
                    <div style={{
                        flex: 1,
                        height: isMobile ? '220px' : '280px',
                        position: 'relative',
                        boxSizing: 'border-box'
                    }}>
                        {allRanks.map((r) => {
                            const isReached = displayScore >= r.min;
                            const isCurrent = rank.name.toUpperCase() === t(r.key, r.defaultName).toUpperCase();
                            const bottomPos = r.min === 4000 ? '66.67%'
                                            : r.min === 3000 ? '50%'
                                            : r.min === 2000 ? '33.33%'
                                            : r.min === 1000 ? '16.67%'
                                            : '0%';
                            const translateY = r.min === 0 ? '25%' : '50%';

                            return (
                                <div
                                    key={`num-${r.key}`}
                                    style={{
                                        position: 'absolute',
                                        bottom: bottomPos,
                                        left: 0,
                                        right: 0,
                                        transform: `translateY(${translateY})`,
                                        display: 'flex',
                                        justifyContent: 'flex-end',
                                        alignItems: 'center',
                                        opacity: isReached ? 1 : 0.35,
                                        transition: 'opacity 0.3s ease'
                                    }}
                                >
                                    <span style={{
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '11px' : '13px',
                                        fontWeight: 'bold',
                                        fontVariantNumeric: 'tabular-nums',
                                        color: isReached ? r.color : '#8A95A5',
                                        textShadow: isCurrent && r.isLegend
                                            ? '0 0 10px rgba(255, 215, 0, 0.7)'
                                            : (isCurrent ? `0 0 8px ${r.color}80` : 'none'),
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {r.min}
                                        <span style={{
                                            fontSize: isMobile ? '9px' : '10.5px',
                                            textTransform: 'none',
                                            marginLeft: '3px',
                                            opacity: 0.9
                                        }}>
                                            pts
                                        </span>
                                    </span>
                                </div>
                            );
                        })}
                    </div>

                    {/* Centre : La Jauge Verticale épurée sans nombres */}
                    <div style={{
                        width: isMobile ? '32px' : '40px',
                        height: isMobile ? '220px' : '280px',
                        borderRadius: '25px',
                        border: '2px solid #5C6675',
                        backgroundColor: '#1A1E26',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
                        flexShrink: 0
                    }}>
                        {/* 4 fines lignes horizontales divisant en sections (base 6000 pts max) */}
                        {[16.67, 33.33, 50, 66.67].map((pct) => (
                            <div
                                key={pct}
                                style={{
                                    position: 'absolute',
                                    bottom: `${pct}%`,
                                    left: 0,
                                    right: 0,
                                    height: '1px',
                                    backgroundColor: '#5C6675',
                                    zIndex: 2,
                                    opacity: 0.7
                                }}
                            />
                        ))}

                        {/* Barre de remplissage montée depuis le bas (Transition CSS pure 3s indépendante) */}
                        <div
                            style={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                height: `${gaugePercent}%`,
                                backgroundColor: rank.color,
                                boxShadow: `0 0 15px ${rank.color}`,
                                zIndex: 1,
                                transition: 'height 3s cubic-bezier(0.1, 0.7, 0.1, 1), background-color 0.3s ease, box-shadow 0.3s ease',
                                willChange: 'height'
                            }}
                        />
                    </div>

                    {/* Colonne Droite : Les Noms de Rangs (alignés à gauche depuis la jauge, sans tiret) */}
                    <div style={{
                        flex: 1,
                        height: isMobile ? '220px' : '280px',
                        position: 'relative',
                        boxSizing: 'border-box'
                    }}>
                        {allRanks.map((r) => {
                            const isReached = displayScore >= r.min;
                            const isCurrent = rank.name.toUpperCase() === t(r.key, r.defaultName).toUpperCase();
                            const bottomPos = r.min === 4000 ? '66.67%'
                                            : r.min === 3000 ? '50%'
                                            : r.min === 2000 ? '33.33%'
                                            : r.min === 1000 ? '16.67%'
                                            : '0%';
                            const translateY = r.min === 0 ? '25%' : '50%';

                            return (
                                <div
                                    key={`name-${r.key}`}
                                    style={{
                                        position: 'absolute',
                                        bottom: bottomPos,
                                        left: 0,
                                        right: 0,
                                        transform: `translateY(${translateY})`,
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        opacity: isReached ? 1 : 0.35,
                                        transition: 'opacity 0.3s ease'
                                    }}
                                >
                                    <span style={{
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '11px' : '13px',
                                        fontWeight: 'bold',
                                        letterSpacing: '1px',
                                        textTransform: 'uppercase',
                                        color: isReached ? r.color : '#8A95A5',
                                        textShadow: isCurrent && r.isLegend
                                            ? '0 0 10px rgba(255, 215, 0, 0.7)'
                                            : (isCurrent ? `0 0 8px ${r.color}80` : 'none'),
                                        transition: 'all 0.3s ease',
                                        whiteSpace: 'nowrap'
                                    }}>
                                        {t(r.key, r.defaultName)}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* 1. Texte des zooms disponibles (free_missions) */}
            <div style={{
                marginTop: isMobile ? '20px' : '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                fontSize: isMobile ? '13px' : '15px',
                fontWeight: 'bold',
                fontFamily: 'Orbitron, sans-serif',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                color: '#EAEDF2'
            }}>
                <span>{t('free_missions', 'Zooms disponibles')} :</span>
                <span style={{
                    color: availableZooms === 0 ? '#FF4D4D' : '#4CE0D2',
                    fontWeight: '900',
                    fontSize: isMobile ? '16px' : '20px',
                    textShadow: availableZooms === 0
                        ? '0 0 12px rgba(255, 77, 77, 0.8), 0 0 20px rgba(255, 77, 77, 0.4)'
                        : '0 0 12px rgba(76, 224, 209, 0.6)'
                }}>
                    {availableZooms}
                </span>
            </div>

            {/* 2. Boutons d'action (Nouveau Jeu / Pub + Continuer) */}
            <div style={{
                marginTop: isMobile ? '16px' : '22px',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: isMobile ? '12px' : '18px',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '290px' : 'none'
            }}>
                {/* Bouton d'action selon le statut : 
                    1. Si zooms restants > 0 : Nouvelle partie
                    2. Si zooms = 0 et non-premium avec pubs disponibles (jusqu'à 3) : Rejouer avec une pub
                    3. Si quota atteint (5 parties Premium ou 2 parties + 3 pubs Non-Premium) : Compte à rebours new_zooms_in
                */}
                {availableZooms > 0 ? (
                    <button
                        onClick={() => {
                            if (onStartNewGame) onStartNewGame();
                            else if (onComplete) onComplete();
                        }}
                        style={{
                            background: 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)',
                            color: '#0A0F18',
                            border: 'none',
                            borderRadius: '12px',
                            padding: isMobile ? '12px 24px' : '14px 32px',
                            fontSize: isMobile ? '13px' : '15px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            fontFamily: 'Orbitron, sans-serif',
                            letterSpacing: '1.2px',
                            textTransform: 'uppercase',
                            boxShadow: '0 4px 20px rgba(76, 224, 209, 0.45)',
                            transition: 'all 0.2s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: isMobile ? '100%' : 'auto'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.04)';
                            e.currentTarget.style.boxShadow = '0 6px 28px rgba(76, 224, 209, 0.7)';
                            e.currentTarget.style.filter = 'brightness(1.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = '0 4px 20px rgba(76, 224, 209, 0.45)';
                            e.currentTarget.style.filter = 'brightness(1)';
                        }}
                    >
                        <span>{t('new_game_btn', 'Nouvelle partie')}</span>
                        <span style={{ fontSize: '1.1em' }}>▶</span>
                    </button>
                ) : !isPremiumUser ? (
                    <button
                        onClick={() => {
                            if (onUnlockPremium) onUnlockPremium();
                        }}
                        style={{
                            background: '#0B0E14',
                            border: '1px solid #5C6675',
                            borderRadius: '12px',
                            color: '#5C6675',
                            padding: isMobile ? '12px 24px' : '14px 32px',
                            fontSize: isMobile ? '13px' : '15px',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            width: isMobile ? '100%' : 'auto',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.color = '#4CE0D2';
                            e.currentTarget.style.borderColor = '#4CE0D2';
                            e.currentTarget.style.transform = 'scale(1.04)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.color = '#5C6675';
                            e.currentTarget.style.borderColor = '#5C6675';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                    >
                        <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🔒</span>
                        <span>{t('unlock_premium', 'Débloquer Premium')}</span>
                    </button>
                ) : (
                    /* Quota quotidien atteint : affichage informatif new_zooms_in */
                    <div
                        style={{
                            background: '#0B0E14',
                            border: '1px solid #5C6675',
                            borderRadius: '12px',
                            padding: isMobile ? '10px 16px' : '12px 22px',
                            fontSize: isMobile ? '11px' : '13px',
                            fontWeight: 'bold',
                            color: '#8A95A5',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            width: isMobile ? '100%' : 'auto',
                            boxSizing: 'border-box',
                            textAlign: 'center',
                            cursor: 'default'
                        }}
                    >
                        <span>{t('new_zooms_in', 'Nouveaux zooms disponibles dans')}</span>
                        <span style={{ color: '#4CE0D2', fontWeight: 'bold', fontFamily: 'Orbitron, sans-serif' }}>
                            {countdownText}
                        </span>
                    </div>
                )}

                {/* Bouton Continuer */}
                <button
                    onClick={() => {
                        if (onComplete) onComplete();
                    }}
                    style={{
                        background: 'rgba(20, 27, 38, 0.85)',
                        color: '#EAEDF2',
                        border: '1.5px solid rgba(76, 224, 209, 0.5)',
                        borderRadius: '12px',
                        padding: isMobile ? '11px 24px' : '13px 30px',
                        fontSize: isMobile ? '13px' : '15px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        fontFamily: 'Orbitron, sans-serif',
                        letterSpacing: '1.2px',
                        textTransform: 'uppercase',
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                        transition: 'all 0.2s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: isMobile ? '100%' : 'auto'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.04)';
                        e.currentTarget.style.borderColor = '#4CE0D2';
                        e.currentTarget.style.boxShadow = '0 4px 20px rgba(76, 224, 209, 0.3)';
                        e.currentTarget.style.color = '#4CE0D2';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.borderColor = 'rgba(76, 224, 209, 0.5)';
                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0, 0, 0, 0.3)';
                        e.currentTarget.style.color = '#EAEDF2';
                    }}
                >
                    <span>{t('continue_btn', 'Continuer')}</span>
                </button>
            </div>
        </div>
    );
};

// ==========================================
// FONCTION PRINCIPALE APP
// ==========================================
function App() {
    const { t, i18n } = useTranslation(); 
    const currentLang = i18n.language ? i18n.language.substring(0, 2).toUpperCase() : 'FR';
    const { isLoaded } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY });

    // États Carte
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [map, setMap] = useState(null); 
    const [currentView, setCurrentView] = useState('map'); 
    const [mapCenter, setMapCenter] = useState(initialCenter); 
    const [mapZoom, setMapZoom] = useState(window.innerWidth <= 768 ? 2 : 3); 
    const [zoomLevel, setZoomLevel] = useState(window.innerWidth <= 768 ? 2 : 3); 
    const [mapBounds, setMapBounds] = useState(null); 

    // Fonction de recentrage et de dézoom automatique sur la situation de départ (niveau de zoom minimum autorisé)
    const resetMapToInitialView = useCallback(() => {
        const minZ = window.innerWidth <= 768 ? 2 : 3;
        setMapCenter({ ...initialCenter });
        setMapZoom(minZ);
        setZoomLevel(minZ);
        if (map) {
            map.setCenter(initialCenter);
            map.setZoom(minZ);
        }
    }, [map]);
    
    // État Mode de jeu actif
    const [activeGameMode, setActiveGameMode] = useState('geodetective');
    
    // États User & UI
    const [user, setUser] = useState(null); 
    const [userData, setUserData] = useState(null); 
    
    // Unité de mesure (km ou miles)
    const [distanceUnit, setDistanceUnit] = useState(() => {
        return localStorage.getItem('zoomap_distance_unit') || 'km';
    });

    // Synchronisation de l'unité avec le profil Firebase
    useEffect(() => {
        if (userData?.distanceUnit && userData.distanceUnit !== distanceUnit) {
            setDistanceUnit(userData.distanceUnit);
            localStorage.setItem('zoomap_distance_unit', userData.distanceUnit);
        }
    }, [userData?.distanceUnit]);

    const handleSelectDistanceUnit = async (unit) => {
        setDistanceUnit(unit);
        localStorage.setItem('zoomap_distance_unit', unit);
        if (user?.uid) {
            try {
                await updateDoc(doc(db, "users", user.uid), { distanceUnit: unit });
            } catch (err) {
                console.error("Erreur sauvegarde distanceUnit :", err);
            }
        }
    };

    const formatDist = (distKm, decimals = 0, isCompact = false) => {
        return formatDistance(distKm, decimals, isCompact, distanceUnit, t);
    }; 

    // Administration & Export statistiques des collections
    const isAdmin = Boolean(user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase().trim()));
    const [isExportingStats, setIsExportingStats] = useState(false);

    const handleExportCollectionStats = async () => {
        if (isExportingStats) return;
        setIsExportingStats(true);
        try {
            // 1. Récupération de tous les utilisateurs
            const usersSnap = await getDocs(collection(db, "users"));
            
            const counts = {};
            let totalPlayersWithCollection = 0;

            usersSnap.forEach(docSnap => {
                const data = docSnap.data();
                const col = data?.geoDetective?.collection;
                if (Array.isArray(col) && col.length > 0) {
                    totalPlayersWithCollection++;
                    col.forEach(id => {
                        if (id !== undefined && id !== null && String(id).trim() !== '') {
                            const cleanId = String(id).trim();
                            counts[cleanId] = (counts[cleanId] || 0) + 1;
                        }
                    });
                }
            });

            // 2. Transformer en liste et trier par ordre croissant d'occurrences (puis par ID croissant)
            const sortedList = Object.entries(counts).map(([idStr, count]) => {
                const numericId = parseInt(idStr, 10);
                return {
                    id: isNaN(numericId) ? idStr : numericId,
                    count
                };
            }).sort((a, b) => {
                if (a.count !== b.count) {
                    return a.count - b.count; // Croissant : du moins présent au plus présent
                }
                return (typeof a.id === 'number' && typeof b.id === 'number') ? a.id - b.id : String(a.id).localeCompare(String(b.id));
            });

            if (sortedList.length === 0) {
                alert("Aucun ID trouvé dans les collections des joueurs.");
                return;
            }

            // 3. Construction du CSV épuré (ID et nombre d'occurrences)
            const header = "ID_Image;Nombre_Occurrences\n";
            const rows = sortedList.map(item => `${item.id};${item.count}`).join("\n");
            const csvContent = "\uFEFF" + header + rows;

            // 4. Déclenchement automatique du téléchargement
            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
            const url = URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.setAttribute("href", url);
            link.setAttribute("download", `occurrences_zooms_collections_${new Date().toISOString().slice(0, 10)}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

        } catch (error) {
            console.error("Erreur export collections :", error);
            alert("Erreur lors de l'export des statistiques : " + error.message);
        } finally {
            setIsExportingStats(false);
        }
    }; 
    const [leaderboard, setLeaderboard] = useState([]); 
    const [exactRank, setExactRank] = useState("..."); 
    const [isLeaderboardOpen, setIsLeaderboardOpen] = useState(false); 
    const [isInfoModalOpen, setIsInfoModalOpen] = useState(false); 
    const [hoveredCard, setHoveredCard] = useState(null);
    // État pour la modale d'installation
    const [isInstallModalOpen, setIsInstallModalOpen] = useState(false);
    // Détecte si le site est lancé comme une application installée
    const [isStandalone, setIsStandalone] = useState(false);

    // Vérifie le mode d'affichage au chargement
    useEffect(() => {
        const checkStandalone = () => {
            // Détection PWA standard + Détection spécifique iOS Safari
            const isStandAloneMatch = window.matchMedia('(display-mode: standalone)').matches;
            const isIOSStandAlone = window.navigator.standalone === true;
            setIsStandalone(isStandAloneMatch || isIOSStandAlone);
        };
        
        checkStandalone();
    }, []);

    // États Action (Pub et Monnaie)
    const [messages, setMessages] = useState([]); 
    const [userMessages, setUserMessages] = useState([]); 
    const [claimStatus, setClaimStatus] = useState({ loading: false, message: "" }); 
    const [isWatchingAd, setIsWatchingAd] = useState(false); 

    // États Géo-Détective (Solo)
    const [gameId, setGameId] = useState(null); 
    const [gameImage, setGameImage] = useState(null); 
    const [guessCount, setGuessCount] = useState(0); 
    const [isGameLoading, setIsGameLoading] = useState(false); 
    const [isAnalyzingGuess, setIsAnalyzingGuess] = useState(false); 
    const [gameFeedback, setGameFeedback] = useState(null); 
    const [lastGuessLocation, setLastGuessLocation] = useState(null); 
    const [guessHistory, setGuessHistory] = useState([]); 
    const [clickPixel, setClickPixel] = useState(null);
    const [showAbandonConfirm, setShowAbandonConfirm] = useState(false); 
    const [dailyLimitReached, setDailyLimitReached] = useState(false); 
    const [isFullscreenImage, setIsFullscreenImage] = useState(false); 
    const [showVideoModal, setShowVideoModal] = useState(false); 
    const [showScoreAnimation, setShowScoreAnimation] = useState(false);
    const [finalScore, setFinalScore] = useState(0);
    const handledGameFeedbackRef = useRef(null);
    const isPostGameZoomRef = useRef(false);
    const [isRoulettePlaying, setIsRoulettePlaying] = useState(false); 
    const [currentRouletteImage, setCurrentRouletteImage] = useState(null);
    const [premiumAlert, setPremiumAlert] = useState(null);
    const [dailyDateOffset, setDailyDateOffset] = useState(0); // 0 = Aujourd'hui, -1 = Hier, etc.
    const [currentGameMode, setCurrentGameMode] = useState('solo');
    const cachedUsersListRef = useRef([]);
    const prevUserUidRef = useRef(null);

    



    // États Mode Duel
    const [duelRoomId, setDuelRoomId] = useState(null);
    const [duelStatus, setDuelStatus] = useState('none'); 
    const [opponentData, setOpponentData] = useState({ distance: null, attemptsUsed: 0 });
    const [myDuelBestDistance, setMyDuelBestDistance] = useState(null);
    const [isDuelModalOpen, setIsDuelModalOpen] = useState(false);
    const [duelModalStep, setDuelModalStep] = useState('choice'); // 'choice' | 'friend_options' | 'create_private' | 'join_private'
    const [privateDuelCode, setPrivateDuelCode] = useState('');
    const [inputPrivateCode, setInputPrivateCode] = useState('');
    const [privateCodeError, setPrivateCodeError] = useState('');
    const [isJoiningPrivate, setIsJoiningPrivate] = useState(false);
    const [isCreatingPrivate, setIsCreatingPrivate] = useState(false);
    const [duelSearchTimeLeft, setDuelSearchTimeLeft] = useState(30);
    const [duelTimeoutNotice, setDuelTimeoutNotice] = useState(false);
    const duelSearchTimerRef = useRef(null);
    const isDuelSearchCancelledRef = useRef(false);
    const [showDuelIntro, setShowDuelIntro] = useState(false);
    const [duelPlayersData, setDuelPlayersData] = useState({ player1: null, player2: null });
    const duelIntroShownRef = useRef(false);

    // Roulette Images
    const rouletteImages = useMemo(() => [
        "https://firebasestorage.googleapis.com/v0/b/mapit17-93fd1.firebasestorage.app/o/challenge_images%2F14_kilimanjaro.png?alt=media&token=38720313-0024-4d7b-9c07-585f2553d88d", 
        "https://firebasestorage.googleapis.com/v0/b/mapit17-93fd1.firebasestorage.app/o/challenge_images%2F24_richat.png?alt=media&token=0284e0f8-4d9f-4c34-b248-dc6bdeb9d947", 
        "https://firebasestorage.googleapis.com/v0/b/mapit17-93fd1.firebasestorage.app/o/challenge_images%2F28_colosseum.png?alt=media&token=cbc0bea4-c798-4eca-814a-cf761412722c", 
        "https://firebasestorage.googleapis.com/v0/b/mapit17-93fd1.firebasestorage.app/o/challenge_images%2F3_avions.png?alt=media&token=c1c22f79-f183-4693-be87-5603465c3e25",
        "https://firebasestorage.googleapis.com/v0/b/mapit17-93fd1.firebasestorage.app/o/challenge_images%2F48_bourtange.png?alt=media&token=462c918a-a5f0-4bac-978a-aed0ac56cbdb"
    ], []);

    // Effets de base
    useEffect(() => { rouletteImages.forEach(src => { const img = new Image(); img.src = src; }); }, [rouletteImages]);
    useEffect(() => { setMyDuelBestDistance(null); }, [duelRoomId]);
    
    // Interception de fin de partie : lance DisplayZoomModal d'abord si victoire, ou ScoreAnimation si défaite
    useEffect(() => {
        if (!gameFeedback) {
            handledGameFeedbackRef.current = null;
            return;
        }
        if (gameFeedback !== handledGameFeedbackRef.current) {
            // Mode Duel : aucun écran de jauge ni de points, ni modale vidéo (géré par DuelOutroModal)
            if (gameFeedback.isDuelFinished) {
                handledGameFeedbackRef.current = gameFeedback;
                return;
            }

            if (gameFeedback.isWon) {
                handledGameFeedbackRef.current = gameFeedback;
                const score = Number(gameFeedback.rewardScore) || 0;
                setFinalScore(score);
                if (gameFeedback.challengeInfoUrl || gameFeedback.info || gameFeedback.image) {
                    isPostGameZoomRef.current = true;
                    setShowVideoModal(true);
                } else {
                    setShowScoreAnimation(true);
                }
            } else if (gameFeedback.isLost) {
                handledGameFeedbackRef.current = gameFeedback;
                setFinalScore(0);
                setShowScoreAnimation(true);
            }
        }
    }, [gameFeedback]);

    // États Profil & Équipes
    const idleTimerRef = useRef(null); 
    const [isBadgeModalOpen, setIsBadgeModalOpen] = useState(false);
    const [isEditingName, setIsEditingName] = useState(false);
    const [newDisplayName, setNewDisplayName] = useState('');
    const [pseudoError, setPseudoError] = useState('');
    const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
    const [onboardingPseudo, setOnboardingPseudo] = useState('');
    const [onboardingError, setOnboardingError] = useState('');
    const [isOnboardingSaving, setIsOnboardingSaving] = useState(false);
    const [teamNameInput, setTeamNameInput] = useState('');
    const [isProcessingTeam, setIsProcessingTeam] = useState(false);
    
    // Détection du statut Premium : permanent (sans échéance) ou avec date d'expiration
    const isLifetimeUser = Boolean(
        userData?.premiumType === 'permanent' ||
        userData?.premiumType === 'streamer' ||
        userData?.premiumType === 'lifetime' ||
        userData?.isLifetime === true ||
        userData?.premiumExpiresAt === 'permanent'
    );

    const isVipUser = Boolean(
        userData?.premiumType === 'streamer' ||
        userData?.premiumType === 'vip' ||
        userData?.isVIP === true ||
        (userData?.isPremium && Boolean(getVipSocialInfo(userData)))
    );

    const isSubscriptionActive = Boolean(userData?.subscriptionStatus === 'active');
    const isPermanentPremium = Boolean(isLifetimeUser || isSubscriptionActive);

    // Vérifie si l'accès à durée limitée (essai ou période d'abonnement) est expiré
    const isTrialExpired = Boolean(
        userData?.isPremium &&
        !isLifetimeUser &&
        !isSubscriptionActive &&
        userData?.premiumExpiresAt &&
        userData?.premiumExpiresAt !== 'permanent' &&
        new Date(userData.premiumExpiresAt).getTime() <= Date.now()
    );

    const isPremiumUser = Boolean(userData?.isPremium && (isLifetimeUser || isSubscriptionActive || !isTrialExpired));

    const [isWelcomeGiftOpen, setIsWelcomeGiftOpen] = useState(false);
    const [isClaimingGift, setIsClaimingGift] = useState(false); 

    // --- AJOUT : Calcul des parties jouées aujourd'hui ---
    const todayString = new Date().toISOString().slice(0, 10);
    const lastPlayDate = userData?.geoDetective?.lastPlayDate;
    const gamesPlayedCount = lastPlayDate === todayString ? Math.max(0, userData?.geoDetective?.attemptsToday || 0) : 0;

    const [showPremiumModal, setShowPremiumModal] = useState(false);
    const [isCompareModalOpen, setIsCompareModalOpen] = useState(false);
    const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
    const [publicTeams, setPublicTeams] = useState([]);
    const [teamSearch, setTeamSearch] = useState('');
    const [pendingImage, setPendingImage] = useState(null);
    
   // 1. On initialise la liste vide
    const [mesZoomsTrouves, setMesZoomsTrouves] = useState([]);

    // 2. On écoute les données Firebase du joueur pour reconstituer sa collection
    useEffect(() => {
        const fetchCollectionData = async () => {
            if (!userData?.geoDetective?.collection || userData.geoDetective.collection.length === 0) {
                setMesZoomsTrouves([]);
                return;
            }

            const collectionIds = userData.geoDetective.collection;
            
            try {
                // On récupère les défis et on extrait l'ID du document (doc.id)
                const snapshot = await getDocs(collection(db, 'challenges_france'));
                const allChallenges = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                const reconstructedCollection = collectionIds.map((idGagne, index) => {
                    const dataBDD = allChallenges.find(c => c.id === String(idGagne));
                    
                    if (!dataBDD) return null;

                    // On s'assure d'avoir un chiffre (1 à 150) pour la grille du Pokédex
                    const pastilleId = parseInt(idGagne, 10);

                    return {
                        id: pastilleId, 
                        unlocked: true,
                        title: dataBDD.name || dataBDD.name_FR || 'Lieu mystère',
                        imageUrl: dataBDD.imageUrl || dataBDD.image || '',
                        videoUrl: dataBDD.info || dataBDD.videoUrl || dataBDD.challengeInfoUrl || '',
                        latitude: dataBDD.location?.latitude || dataBDD.location?._latitude || 0,
                        longitude: dataBDD.location?.longitude || dataBDD.location?._longitude || 0,
                        description: dataBDD.description || ''
                    };
                }).filter(Boolean); // Retire les éléments null

                setMesZoomsTrouves(reconstructedCollection);
            } catch (error) {
                console.error("Erreur lors de la reconstruction de la collection :", error);
            }
        };

        fetchCollectionData();
    }, [userData?.geoDetective?.collection]);


    // Collection
    const [selectedDisplayZoom, setSelectedDisplayZoom] = useState(null);
    const [isPokedexOpen, setIsPokedexOpen] = useState(false);

    // CHRONOMETRE (Unique et stabilisé)
    const [timeLeft, setTimeLeft] = useState(300);

    useEffect(() => {
        let timer;
        const isSoloActive = gameId && !gameFeedback?.isWon && !gameFeedback?.isLost;
        const isDuelActive = duelRoomId && duelStatus === 'playing' && !showDuelIntro;

        if (isSoloActive || isDuelActive) {
            timer = setInterval(() => {
                setTimeLeft(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0; 
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(timer);
    }, [gameId, gameFeedback?.isWon, gameFeedback?.isLost, duelRoomId, duelStatus, showDuelIntro]);

    // ==========================================
    // ALERTES HUD (TEMPS RESTANT & TENTATIVES)
    // ==========================================
    const [timeAlert, setTimeAlert] = useState(null);
    const alertedMilestonesRef = useRef(new Set());
    const alertedAttemptsRef = useRef(new Set());
    const duelGoalAlertShownRef = useRef(false);
    const timeAlertTimeoutRef = useRef(null);
    const audioCtxRef = useRef(null);

    const getAudioContext = useCallback(() => {
        try {
            const AudioCtx = window.AudioContext || window.webkitAudioContext;
            if (!AudioCtx) return null;
            if (!audioCtxRef.current) {
                audioCtxRef.current = new AudioCtx();
            }
            if (audioCtxRef.current.state === 'suspended') {
                audioCtxRef.current.resume();
            }
            return audioCtxRef.current;
        } catch (e) {
            return null;
        }
    }, []);

    const playTimeAlertSound = useCallback((urgency = 'normal') => {
        try {
            const ctx = getAudioContext();
            if (!ctx) return;
            const now = ctx.currentTime;

            if (urgency === 'urgent') {
                // 30 secondes / dernière tentative : triple bip rythmé et percutant
                [0, 0.11, 0.22].forEach((offset, idx) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(idx === 2 ? 1046.5 : 880, now + offset);

                    gain.gain.setValueAtTime(0, now + offset);
                    gain.gain.linearRampToValueAtTime(0.24, now + offset + 0.015);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.09);

                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.start(now + offset);
                    osc.stop(now + offset + 0.1);
                });
            } else if (urgency === 'warning') {
                // 1 minute / 5 tentatives restantes : double bip montant (G5 -> C6)
                [0, 0.14].forEach((offset, idx) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(idx === 0 ? 784 : 1046.5, now + offset);

                    gain.gain.setValueAtTime(0, now + offset);
                    gain.gain.linearRampToValueAtTime(0.2, now + offset + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.12);

                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.start(now + offset);
                    osc.stop(now + offset + 0.13);
                });
            } else {
                // 4m, 3m, 2m / début duel : double carillon doux (D5 -> A5)
                [0, 0.14].forEach((offset, idx) => {
                    const osc = ctx.createOscillator();
                    const gain = ctx.createGain();
                    osc.type = 'sine';
                    osc.frequency.setValueAtTime(idx === 0 ? 587.33 : 880, now + offset);

                    gain.gain.setValueAtTime(0, now + offset);
                    gain.gain.linearRampToValueAtTime(0.18, now + offset + 0.02);
                    gain.gain.exponentialRampToValueAtTime(0.001, now + offset + 0.13);

                    osc.connect(gain);
                    gain.connect(ctx.destination);
                    osc.start(now + offset);
                    osc.stop(now + offset + 0.14);
                });
            }
        } catch (e) {
            console.log("Audio non disponible ou bloqué :", e);
        }
    }, [getAudioContext]);

    // Déclencheur centralisé d'alertes visuelles HUD (Temps, Tentatives, Objectif Duel)
    const triggerHudAlert = useCallback(({ title, text, urgency = 'normal', icon, playSound = true, duration = 2000 }) => {
        if (playSound) playTimeAlertSound(urgency);
        setTimeAlert({
            key: `hud-${Date.now()}-${Math.random()}`,
            title,
            text,
            urgency,
            icon
        });
        if (timeAlertTimeoutRef.current) clearTimeout(timeAlertTimeoutRef.current);
        timeAlertTimeoutRef.current = setTimeout(() => {
            setTimeAlert(null);
        }, duration);
    }, [playTimeAlertSound]);

    // Alertes de temps restant (4m, 3m, 2m, 1m, 30s)
    useEffect(() => {
        const isSoloActive = gameId && !gameFeedback?.isWon && !gameFeedback?.isLost;
        const isDuelActive = duelRoomId && duelStatus === 'playing' && !showDuelIntro;
        const isGameActive = isSoloActive || isDuelActive;

        if (!isGameActive) {
            alertedMilestonesRef.current.clear();
            alertedAttemptsRef.current.clear();
            duelGoalAlertShownRef.current = false;
            if (timeAlertTimeoutRef.current) clearTimeout(timeAlertTimeoutRef.current);
            setTimeAlert(null);
            return;
        }

        // Pour le mode duel (180s initiales), on évite de déclencher l'alerte de 3 min dès le lancement
        if (isDuelActive && !alertedMilestonesRef.current.has('duel_init')) {
            alertedMilestonesRef.current.add('duel_init');
            alertedMilestonesRef.current.add(180);
            alertedMilestonesRef.current.add(240);
        }

        const milestones = [
            { seconds: 240, text: t('time_left_4m', '4 minutes restantes'), urgency: 'normal' },
            { seconds: 180, text: t('time_left_3m', '3 minutes restantes'), urgency: 'normal' },
            { seconds: 120, text: t('time_left_2m', '2 minutes restantes'), urgency: 'normal' },
            { seconds: 60, text: t('time_left_1m', 'Dernière minute !'), urgency: 'warning' },
            { seconds: 30, text: t('time_left_30s', 'Attention : 30 secondes !'), urgency: 'urgent' },
        ];

        const targetMilestone = milestones.find(m => m.seconds === timeLeft);

        if (targetMilestone && !alertedMilestonesRef.current.has(targetMilestone.seconds)) {
            alertedMilestonesRef.current.add(targetMilestone.seconds);

            triggerHudAlert({
                title: t('time_alert_title', 'Temps restant'),
                text: targetMilestone.text,
                urgency: targetMilestone.urgency,
                icon: targetMilestone.urgency === 'urgent' ? '⚠️' : '⏳'
            });
        }
    }, [timeLeft, gameId, gameFeedback?.isWon, gameFeedback?.isLost, duelRoomId, duelStatus, showDuelIntro, t, triggerHudAlert]);

    // Alerte au lancement du mode Duel : "5 tentatives pour te rapprocher le plus possible" affichée 2s dès le début
    useEffect(() => {
        if (!duelRoomId || duelStatus !== 'playing' || showDuelIntro) {
            if (!duelRoomId || duelStatus === 'none') {
                duelGoalAlertShownRef.current = false;
            }
            return;
        }
        if (!duelGoalAlertShownRef.current) {
            duelGoalAlertShownRef.current = true;
            triggerHudAlert({
                title: t('duel_alert_title', 'Mode Duel'),
                text: t('duel_5_attempts_goal', '5 tentatives pour te rapprocher le plus possible'),
                urgency: 'normal',
                icon: '⚔️',
                duration: 2000
            });
        }
    }, [duelRoomId, duelStatus, showDuelIntro, triggerHudAlert, t]);

    // ==========================================
    // MUSIQUE DE FOND DYNAMIQUE (WEB AUDIO & MP3 HYBRIDE)
    // ==========================================
    const [isMusicMuted, setIsMusicMuted] = useState(() => {
        try {
            return localStorage.getItem('zoomap_music_muted') === 'true';
        } catch (e) {
            return false;
        }
    });
    const isMusicMutedRef = useRef(isMusicMuted);
    const bgmIntervalRef = useRef(null);
    const activeBgmNodesRef = useRef([]);
    const bgmAudioElementRef = useRef(null);

    useEffect(() => {
        isMusicMutedRef.current = isMusicMuted;
    }, [isMusicMuted]);

    const stopBackgroundMusic = useCallback(() => {
        // 1. Arrêt du fichier MP3 s'il tournait
        if (bgmAudioElementRef.current) {
            try {
                const audio = bgmAudioElementRef.current;
                audio.pause();
                audio.currentTime = 0;
            } catch (e) {}
        }

        // 2. Arrêt du synthétiseur procédural
        if (bgmIntervalRef.current) {
            clearInterval(bgmIntervalRef.current);
            bgmIntervalRef.current = null;
        }
        const ctx = audioCtxRef.current;
        if (!ctx) return;
        const now = ctx.currentTime;
        activeBgmNodesRef.current.forEach(item => {
            try {
                if (item.gainNode) {
                    item.gainNode.gain.cancelScheduledValues(now);
                    item.gainNode.gain.setValueAtTime(item.gainNode.gain.value, now);
                    item.gainNode.gain.linearRampToValueAtTime(0, now + 0.6);
                }
                if (item.osc) {
                    item.osc.stop(now + 0.65);
                }
            } catch (e) {}
        });
        activeBgmNodesRef.current = [];
    }, []);

    const startBackgroundMusic = useCallback(() => {
        if (isMusicMutedRef.current) return;
        if (bgmIntervalRef.current) return;

        // --- TENTATIVE DE LECTURE D'UN FICHIER MP3 PERSONNALISÉ (/sounds/bgm.mp3) ---
        if (!bgmAudioElementRef.current) {
            const audio = new Audio('/sounds/bgm.mp3');
            audio.loop = true;
            audio.volume = 0.22;
            bgmAudioElementRef.current = audio;
        }

        const audio = bgmAudioElementRef.current;
        const playPromise = audio.play();

        if (playPromise !== undefined) {
            playPromise.then(() => {
                // Le fichier MP3 existe et a démarré avec succès
                return;
            }).catch(() => {
                // Pas de MP3 dans public/sounds/bgm.mp3 -> repli automatique sur le synthé procédural dynamique
                startProceduralBgm();
            });
        } else {
            startProceduralBgm();
        }

        function startProceduralBgm() {
            if (bgmIntervalRef.current) return;
            const ctx = getAudioContext();
            if (!ctx) return;

            // --- AMBIANCE LO-FI GROOVE & BASSES (STYLE JRPG GROOVY / PERSONA / POKÉMON COLOSSEUM) ---
            // Tempo : 95 BPM (315ms par croche)
            // Beat énergique, grosse caisse punchy, basse qui marche et chops de clavier syncopés
            const GROOVE_PATTERNS = [
                // === SECTION A : GROOVE PRINCIPAL (Rhodes Chops & Ligne de Basse Mobile) ===
                {
                    // Mesure 1 : Cmaj9 (Do)
                    bassNotes: { 0: 65.41, 2: 130.81, 4: 98.00, 6: 110.00 }, // C2 -> C3 -> G2 -> A2
                    chops: [130.81, 196.00, 246.94, 293.66], // C-G-B-D
                    melody: [392.00, 0, 440.00, 523.25, 0, 392.00, 329.63, 0],
                    lead: 'kalimba'
                },
                {
                    // Mesure 2 : A7(b13) (La funk)
                    bassNotes: { 0: 55.00, 2: 110.00, 4: 82.41, 6: 92.50 }, // A1 -> A2 -> E2 -> F#2
                    chops: [110.00, 164.81, 220.00, 261.63], // A-E-A-C
                    melody: [440.00, 0, 523.25, 587.33, 0, 440.00, 392.00, 0],
                    lead: 'kalimba'
                },
                {
                    // Mesure 3 : Dm9 (Ré)
                    bassNotes: { 0: 73.42, 2: 146.83, 4: 110.00, 6: 123.47 }, // D2 -> D3 -> A2 -> B2
                    chops: [146.83, 220.00, 261.63, 329.63], // D-A-C-E
                    melody: [523.25, 0, 587.33, 659.25, 0, 523.25, 440.00, 0],
                    lead: 'kalimba'
                },
                {
                    // Mesure 4 : G13 (Sol turnaround)
                    bassNotes: { 0: 49.00, 2: 98.00, 4: 73.42, 6: 82.41 }, // G1 -> G2 -> D2 -> E2
                    chops: [196.00, 246.94, 293.66, 349.23], // G-B-D-F
                    melody: [392.00, 0, 440.00, 493.88, 587.33, 493.88, 440.00, 392.00],
                    lead: 'kalimba'
                },

                // === SECTION B : VARIATION JRPG AVENTURE (Flûte/Ocarina Lead & Basse Rebondissante) ===
                {
                    // Mesure 5 : Em7 (Mi)
                    bassNotes: { 0: 82.41, 2: 164.81, 4: 123.47, 6: 146.83 }, // E2 -> E3 -> B2 -> D3
                    chops: [164.81, 246.94, 293.66, 329.63],
                    melody: [659.25, 0, 587.33, 0, 523.25, 493.88, 440.00, 0],
                    lead: 'flute'
                },
                {
                    // Mesure 6 : A9 (La)
                    bassNotes: { 0: 55.00, 2: 110.00, 4: 82.41, 6: 98.00 }, // A1 -> A2 -> E2 -> G2
                    chops: [110.00, 164.81, 220.00, 277.18],
                    melody: [587.33, 0, 523.25, 0, 493.88, 440.00, 392.00, 440.00],
                    lead: 'flute'
                },
                {
                    // Mesure 7 : Dm9 -> G7 (Montée)
                    bassNotes: { 0: 73.42, 2: 146.83, 4: 98.00, 6: 123.47 }, // D2 -> D3 -> G2 -> B2
                    chops: [146.83, 220.00, 261.63, 349.23],
                    melody: [523.25, 0, 587.33, 0, 659.25, 698.46, 783.99, 0],
                    lead: 'flute'
                },
                {
                    // Mesure 8 : Cmaj9 (Break & Résolution avant reprise)
                    bassNotes: { 0: 65.41, 2: 130.81, 4: 98.00, 6: 116.54 }, // C2 -> C3 -> G2 -> Bb2
                    chops: [130.81, 196.00, 246.94, 329.63],
                    melody: [523.25, 0, 440.00, 0, 392.00, 329.63, 293.66, 261.63],
                    lead: 'flute'
                }
            ];

            // 1. Grosse caisse (Kick) punchy et percutante dès le premier temps
            const playGrooveKick = (time, isAccent = true) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();

                // Descente rapide de pitch percutante pour claquer fort dans les basses
                osc.frequency.setValueAtTime(isAccent ? 135 : 110, time);
                osc.frequency.exponentialRampToValueAtTime(42, time + 0.13);

                gain.gain.setValueAtTime(isAccent ? 0.12 : 0.08, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.22);

                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(time);
                osc.stop(time + 0.24);
                activeBgmNodesRef.current.push({ osc, gainNode: gain });
            };

            // 2. Caisse claire / Rimshot claquant (Snappy Snare)
            const playGrooveSnare = (time, isAccent = false) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'triangle';
                osc.frequency.setValueAtTime(isAccent ? 210 : 185, time);
                osc.frequency.exponentialRampToValueAtTime(80, time + 0.08);

                gain.gain.setValueAtTime(isAccent ? 0.042 : 0.028, time);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.11);

                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.start(time);
                osc.stop(time + 0.13);

                // Timbre claquant aigu
                const snapOsc = ctx.createOscillator();
                const snapGain = ctx.createGain();
                const snapFilter = ctx.createBiquadFilter();
                snapOsc.type = 'sawtooth';
                snapOsc.frequency.setValueAtTime(320, time);
                snapFilter.type = 'highpass';
                snapFilter.frequency.setValueAtTime(1200, time);

                snapGain.gain.setValueAtTime(0.02, time);
                snapGain.gain.exponentialRampToValueAtTime(0.0005, time + 0.08);

                snapOsc.connect(snapFilter);
                snapFilter.connect(snapGain);
                snapGain.connect(ctx.destination);
                snapOsc.start(time);
                snapOsc.stop(time + 0.09);

                activeBgmNodesRef.current.push({ osc, gainNode: gain }, { osc: snapOsc, gainNode: snapGain });
            };

            // 3. Basse groovy & lourde (Sub-Bass profond + Médium audible)
            const playGroovyBass = (freq, time, duration = 0.26) => {
                const subOsc = ctx.createOscillator();
                const subGain = ctx.createGain();
                subOsc.type = 'sine';
                subOsc.frequency.setValueAtTime(freq, time);

                const midOsc = ctx.createOscillator();
                const midGain = ctx.createGain();
                const midFilter = ctx.createBiquadFilter();
                midOsc.type = 'triangle';
                midOsc.frequency.setValueAtTime(freq, time);
                midFilter.type = 'lowpass';
                midFilter.frequency.setValueAtTime(480, time);

                subGain.gain.setValueAtTime(0, time);
                subGain.gain.linearRampToValueAtTime(0.085, time + 0.02);
                subGain.gain.exponentialRampToValueAtTime(0.001, time + duration);

                midGain.gain.setValueAtTime(0, time);
                midGain.gain.linearRampToValueAtTime(0.045, time + 0.02);
                midGain.gain.exponentialRampToValueAtTime(0.001, time + duration);

                subOsc.connect(subGain);
                subGain.connect(ctx.destination);

                midOsc.connect(midFilter);
                midFilter.connect(midGain);
                midGain.connect(ctx.destination);

                subOsc.start(time);
                midOsc.start(time);
                subOsc.stop(time + duration + 0.05);
                midOsc.stop(time + duration + 0.05);

                activeBgmNodesRef.current.push({ osc: subOsc, gainNode: subGain });
            };

            // 4. Chops de Piano Rhodes rythmiques (accords percutants brefs)
            const playRhodesChop = (freqs, time, duration = 0.28, gainLevel = 0.03) => {
                const masterGain = ctx.createGain();
                const filter = ctx.createBiquadFilter();
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(850, time);

                masterGain.gain.setValueAtTime(0, time);
                masterGain.gain.linearRampToValueAtTime(gainLevel, time + 0.015);
                masterGain.gain.exponentialRampToValueAtTime(0.001, time + duration);

                masterGain.connect(filter);
                filter.connect(ctx.destination);

                freqs.forEach(freq => {
                    const osc = ctx.createOscillator();
                    osc.type = 'triangle';
                    osc.frequency.setValueAtTime(freq, time);
                    osc.connect(masterGain);
                    osc.start(time);
                    osc.stop(time + duration + 0.04);
                    activeBgmNodesRef.current.push({ osc, gainNode: masterGain });
                });
            };

            // 5. Hi-Hat / Shaker groovy pour la pulsation
            const playGrooveHat = (time, isAccent = false) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                const filter = ctx.createBiquadFilter();

                osc.type = 'triangle';
                osc.frequency.setValueAtTime(isAccent ? 520 : 420, time);
                filter.type = 'highpass';
                filter.frequency.setValueAtTime(2400, time);

                gain.gain.setValueAtTime(isAccent ? 0.018 : 0.01, time);
                gain.gain.exponentialRampToValueAtTime(0.0005, time + 0.045);

                osc.connect(filter);
                filter.connect(gain);
                gain.connect(ctx.destination);

                osc.start(time);
                osc.stop(time + 0.05);
                activeBgmNodesRef.current.push({ osc, gainNode: gain });
            };

            // 6. Lead mélodique Kalimba perlé
            const playKalimbaLead = (freq, time) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = 'sine';
                osc.frequency.setValueAtTime(freq, time);

                gain.gain.setValueAtTime(0, time);
                gain.gain.linearRampToValueAtTime(0.02, time + 0.008);
                gain.gain.exponentialRampToValueAtTime(0.0005, time + 0.45);

                osc.connect(gain);
                gain.connect(ctx.destination);

                osc.start(time);
                osc.stop(time + 0.48);
                activeBgmNodesRef.current.push({ osc, gainNode: gain });
            };

            // 7. Lead Flûte / Ocarina JRPG pour la Section B
            const playFluteLead = (freq, time) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                const filter = ctx.createBiquadFilter();

                osc.type = 'triangle';
                osc.frequency.setValueAtTime(freq, time);
                filter.type = 'lowpass';
                filter.frequency.setValueAtTime(950, time);

                gain.gain.setValueAtTime(0, time);
                gain.gain.linearRampToValueAtTime(0.024, time + 0.03);
                gain.gain.exponentialRampToValueAtTime(0.001, time + 0.48);

                osc.connect(filter);
                filter.connect(gain);
                gain.connect(ctx.destination);

                osc.start(time);
                osc.stop(time + 0.52);
                activeBgmNodesRef.current.push({ osc, gainNode: gain });
            };

            let patternIndex = 0;
            let step = 0;

            const tick = () => {
                if (isMusicMutedRef.current) return;
                const now = ctx.currentTime;
                const pattern = GROOVE_PATTERNS[patternIndex % GROOVE_PATTERNS.length];

                // --- 1. GROSSE CAISSE PRÉSENTE DÈS LE STEP 0 ---
                // Step 0 : Gros Kick d'impact
                if (step === 0) {
                    playGrooveKick(now, true);
                } 
                // Step 3 : Kick syncopé sur le contre-temps (groove funk)
                else if (step === 3) {
                    playGrooveKick(now, false);
                } 
                // Step 4 : Deuxième kick
                else if (step === 4) {
                    playGrooveKick(now, false);
                } 
                // Step 7 : Relance sur les mesures impaires
                else if (step === 7 && patternIndex % 2 === 1) {
                    playGrooveKick(now, false);
                }

                // --- 2. CAISSE CLAIRE / RIMSHOT SUR LES TEMPS 2 ET 4 (Step 2 et Step 6) ---
                if (step === 2 || step === 6) {
                    playGrooveSnare(now, step === 2);
                }
                // Petit fill de batterie de fin de cycle (mesure 8, step 7)
                if (patternIndex % GROOVE_PATTERNS.length === 7 && step === 7) {
                    playGrooveSnare(now, false);
                }

                // --- 3. CHARLESTON / SHAKER CONTINU AVEC SWING ---
                playGrooveHat(now, step % 2 === 1);

                // --- 4. BASSE GROOVY ACTIVE SUR TOUTE LA MESURE ---
                if (pattern.bassNotes[step]) {
                    playGroovyBass(pattern.bassNotes[step], now, 0.28);
                }

                // --- 5. CHOPS DE PIANO RHODES RYTHMIQUES (Contretemps percutants) ---
                if (step === 0 || step === 3 || step === 5) {
                    playRhodesChop(pattern.chops, now, 0.26, 0.028);
                }

                // --- 6. LEAD MÉLODIQUE RYTHMÉ (KALIMBA OU FLÛTE) ---
                const melFreq = pattern.melody[step % 8];
                if (melFreq > 0) {
                    if (pattern.lead === 'flute') {
                        playFluteLead(melFreq, now);
                    } else {
                        playKalimbaLead(melFreq, now);
                    }
                }

                step++;
                if (step >= 8) {
                    step = 0;
                    patternIndex++;
                }

                if (activeBgmNodesRef.current.length > 45) {
                    activeBgmNodesRef.current = activeBgmNodesRef.current.slice(-20);
                }
            };

            // Démarrage immédiat puis boucle dynamique à 315ms (~95 BPM)
            tick();
            bgmIntervalRef.current = setInterval(tick, 315);
        }
    }, [getAudioContext]);

    const toggleMusicMuted = useCallback(() => {
        setIsMusicMuted(prev => {
            const next = !prev;
            try {
                localStorage.setItem('zoomap_music_muted', String(next));
            } catch (e) {}
            if (next) {
                stopBackgroundMusic();
            }
            return next;
        });
    }, [stopBackgroundMusic]);

    // GESTION DU CYCLE DE VIE DE LA MUSIQUE (Démarrage et arrêt automatique)
    useEffect(() => {
        const isSoloGameActive = gameId && !gameFeedback?.isWon && !gameFeedback?.isLost;
        const isDuelGameActive = duelRoomId && (showDuelIntro || (duelStatus === 'playing' && !gameFeedback?.isDuelFinished));
        const shouldPlay = (isSoloGameActive || isDuelGameActive) && !isMusicMuted;

        if (shouldPlay) {
            startBackgroundMusic();
        } else {
            stopBackgroundMusic();
        }
    }, [gameId, gameFeedback?.isWon, gameFeedback?.isLost, gameFeedback?.isDuelFinished, duelRoomId, duelStatus, showDuelIntro, isMusicMuted, startBackgroundMusic, stopBackgroundMusic]);

    // GESTION DU TIME OUT (Solo et Duel)
useEffect(() => {
    // 1. TIMEOUT DU MODE SOLO & DAILY ZOOM
    // On ajoute "!duelRoomId" pour être sûr que ça ne se déclenche PAS en duel
    if (gameId && !duelRoomId && timeLeft === 0 && !gameFeedback?.isWon && !gameFeedback?.isLost) {
        const curGameId = gameId;
        const curMode = currentGameMode;
        setGameFeedback(prev => ({
            ...prev,
            isLost: true,
            isTimeOut: true, 
            distance: lastGuessLocation ? (prev?.distance || 0) : 0,
            directionEmoji: null
        }));
        if (curMode === 'daily') {
            const todayStr = new Date().toISOString().slice(0, 10);
            setUserData(prev => prev ? { ...prev, lastDailyZoomDate: todayStr } : prev);
            if (user?.uid) updateDoc(doc(db, "users", user.uid), { lastDailyZoomDate: todayStr }).catch(console.error);
        }
        httpsCallable(functions, 'surrenderGeoDetectiveGame')({ gameId: curGameId }).catch(console.error);
    }

    // 2. TIMEOUT DU MODE DUEL
    // Si le temps est à 0 en duel, on demande au serveur de comparer les distances et de clore la partie
    if (duelRoomId && duelStatus === 'playing' && timeLeft === 0 && !gameFeedback?.isDuelFinished) {
        const resolveDuel = async () => {
            try {
                // On met une petite sécurité visuelle pour dire au joueur de patienter
                setGameFeedback(prev => ({ ...prev, isAnalyzingGuess: true }));
                
                await httpsCallable(functions, 'resolveDuelState')({ 
                    roomId: duelRoomId, 
                    reason: 'timeout' 
                });
                
                // La modale de fin s'affichera toute seule grâce à onSnapshot quand le serveur aura répondu
                setGameFeedback(prev => ({ ...prev, isAnalyzingGuess: false }));
            } catch (error) {
                console.error("Erreur lors de la fin du chrono :", error);
                setGameFeedback(prev => ({ ...prev, isAnalyzingGuess: false }));
            }
        };
        resolveDuel();
    }
}, [timeLeft, gameId, duelRoomId, duelStatus, gameFeedback?.isWon, gameFeedback?.isLost, gameFeedback?.isDuelFinished, lastGuessLocation, currentGameMode, user]);

    // Équipes de clan
    useEffect(() => {
        if (isBadgeModalOpen) {
            import("firebase/firestore").then(({ query, collection, limit, getDocs }) => {
                getDocs(query(collection(db, 'teams'), limit(20))).then(s => {
                    setPublicTeams(s.docs.map(d => d.data()));
                }).catch(console.error);
            });
        } else {
            setPendingImage(null); setTeamSearch(''); 
        }
    }, [isBadgeModalOpen]);

    // Effect : Préchauffe simultanément les instances de Cloud Functions à l'initialisation de l'application
    useEffect(() => { 
        const timer = setTimeout(() => { 
            console.log("🔥 Envoi des pings de chauffe..."); 
            httpsCallable(functions, 'startGeoDetectiveGame')({ warmup: true }).catch(e => console.error(e)); 
            httpsCallable(functions, 'submitGeoDetectiveGuess')({ warmup: true }).catch(e => console.error(e)); 
        }, 2000); 
        
        return () => clearTimeout(timer); 
    }, []);

    /// Effect : Charge le classement dès que le joueur est connecté pour alimenter le bouton tournant
    useEffect(() => {
        if (user) {
            const isUserChanged = prevUserUidRef.current !== user.uid;
            prevUserUidRef.current = user.uid;
            const onlyDaily = !isUserChanged && cachedUsersListRef.current.length > 0;
            fetchLeaderboard(dailyDateOffset, onlyDaily);
        }
    }, [user, dailyDateOffset]);
    
    // Écouteurs Firestore & Map
    const handleMapIdle = useCallback(() => { 
        if (map) { 
            setMapZoom(map.getZoom()); // <--- LA NOUVELLE LIGNE EST ICI
            if (idleTimerRef.current) clearTimeout(idleTimerRef.current); 
            idleTimerRef.current = setTimeout(() => { 
                const bounds = map.getBounds(); 
                if (bounds) { 
                    const newBounds = { north: bounds.getNorthEast().lat(), south: bounds.getSouthWest().lat(), east: bounds.getNorthEast().lng(), west: bounds.getSouthWest().lng() }; 
                    setMapBounds(prev => (prev && prev.north === newBounds.north && prev.south === newBounds.south && prev.east === newBounds.east && prev.west === newBounds.west) ? prev : newBounds); 
                } 
            }, 500); 
        } 
    }, [map]);

   // Clic sur la carte (Jeu Solo et Duel) - 100% protégé contre les rechargements
    const handleMapClick = useCallback(async (event) => {
        if (!gameId && !duelRoomId) return;
        if (isAnalyzingGuess) return; 
        
        const lat = event.latLng.lat();
        const lng = event.latLng.lng(); 
        
        // --- NOUVEAU : Capture des pixels du clic (Compatible PC et Mobile) ---
        let cx = window.innerWidth / 2;
        let cy = window.innerHeight / 2;
        if (event.domEvent) {
            if (event.domEvent.clientX !== undefined) {
                cx = event.domEvent.clientX;
                cy = event.domEvent.clientY;
            } else if (event.domEvent.changedTouches && event.domEvent.changedTouches.length > 0) {
                cx = event.domEvent.changedTouches[0].clientX;
                cy = event.domEvent.changedTouches[0].clientY;
            }
        }
        setClickPixel({ x: cx, y: cy });
        // ----------------------------------------------------------------------
        
        setIsAnalyzingGuess(true); 
        setLastGuessLocation({ lat, lng }); 
        setGuessHistory(prev => [...prev, { lat, lng, distance: null }]);
        setGameFeedback(null);
        
        try {
            if (duelRoomId) {
                const [result] = await Promise.all([
                    httpsCallable(functions, 'submitDuelGuess')({ roomId: duelRoomId, guessLocation: { lat, lng } }), 
                    new Promise(r => setTimeout(r, 1500))
                ]); 
                const data = result.data; 

                if (data && data.distance !== undefined && data.distance !== null) {
                    setMyDuelBestDistance(prev => (prev === null || data.distance < prev) ? data.distance : prev);
                    setGuessHistory(prev => {
                        if (prev.length === 0) return prev;
                        const next = [...prev];
                        next[next.length - 1] = {
                            ...next[next.length - 1],
                            distance: data.distance
                        };
                        return next;
                    });
                }
                
                // CORRECTION : Le bouclier anti-écrasement
                setGameFeedback(prev => {
                    if (prev && prev.isDuelFinished) return prev; 
                    if (data.isFinished) return prev; 
                    return { distance: data.distance };
                });
                
                setGuessCount(data.attemptsUsed);
                
            } else if (gameId) {
                const [result] = await Promise.all([
                    submitGeoDetectiveGuess({ gameId, guessLocation: { lat, lng } }), 
                    new Promise(r => setTimeout(r, 1500))
                ]); 
                const data = result.data; 
                setGameFeedback(data);

                // Attacher la distance calculée à la tentative dans l'historique
                if (data && data.distance !== undefined && data.distance !== null) {
                    setGuessHistory(prev => {
                        if (prev.length === 0) return prev;
                        const next = [...prev];
                        next[next.length - 1] = {
                            ...next[next.length - 1],
                            distance: data.distance
                        };
                        return next;
                    });
                }
                
                if (data.isWon || data.isLost) { 
                    setGameId(null); 
                    if (currentGameMode === 'daily') {
                        const todayStr = new Date().toISOString().slice(0, 10);
                        setUserData(prev => prev ? { ...prev, lastDailyZoomDate: todayStr } : prev);
                        if (user?.uid) updateDoc(doc(db, "users", user.uid), { lastDailyZoomDate: todayStr }).catch(console.error);
                    }
                    if (data.isLost && data.correctLocation && map) { 
                        map.panTo({ 
                            lat: data.correctLocation._latitude || data.correctLocation.latitude, 
                            lng: data.correctLocation._longitude || data.correctLocation.longitude 
                        }); 
                        setTimeout(() => map.setZoom(15), 500); 
                    } 
                } else {
                    setGuessCount(data.guessCount);
                    // Solo & Daily Zoom : Alertes du nombre de tentatives restantes (5 et 1 restante)
                    if (!duelRoomId) {
                        const remaining = 15 - data.guessCount;
                        if (remaining === 5 && !alertedAttemptsRef.current.has(5)) {
                            alertedAttemptsRef.current.add(5);
                            triggerHudAlert({
                                title: t('attempts_alert_title', 'Tentatives'),
                                text: t('attempts_left_5', '5 tentatives restantes'),
                                urgency: 'warning',
                                icon: '🎯'
                            });
                        } else if (remaining === 1 && !alertedAttemptsRef.current.has(1)) {
                            alertedAttemptsRef.current.add(1);
                            triggerHudAlert({
                                title: t('attempts_alert_urgent_title', 'Dernière chance !'),
                                text: t('attempts_left_1', '1 seule tentative restante !'),
                                urgency: 'urgent',
                                icon: '⚠️'
                            });
                        }
                    }
                }
            }
        } catch (error) { 
            setGuessHistory(prev => prev.filter(g => g.distance !== null));
            alert("Erreur technique : " + error.message); 
        } finally { 
            setIsAnalyzingGuess(false); 
        } 
    }, [gameId, duelRoomId, isAnalyzingGuess, map, currentGameMode, user, triggerHudAlert, t]); // <-- C'EST ICI LA MAGIE : La fonction ne se recrée QUE si un de ces 4 états précis change.

   

    // Actions utilisateurs
    const handleWatchAd = async () => { if (isWatchingAd) return; setIsWatchingAd(true); setTimeout(async () => { try { setUserData(prev => prev ? { ...prev, credits: (Number(prev.credits) || 0) + 50 } : prev); if (user?.uid) await updateDoc(doc(db, "users", user.uid), { credits: increment(50) }); alert(`Félicitations ! +50 ZMs ajoutés.`); } catch (error) { console.error("Erreur récompense :", error); } finally { setIsWatchingAd(false); } }, 3000); };
    const signInWithGoogle = () => signInWithPopup(auth, new GoogleAuthProvider()).catch(console.error);
    const handleSignOut = () => signOut(auth).catch(console.error);

    const handleClaimWelcomeGift = async () => {
        if (!user) return;
        setIsClaimingGift(true);
        try {
            const now = new Date();
            const expiresDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
            const expiresIso = expiresDate.toISOString();

            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                isPremium: true,
                premiumType: 'trial',
                premiumExpiresAt: expiresIso,
                hasReceivedWelcomeGift: true
            });

            setUserData(prev => prev ? {
                ...prev,
                isPremium: true,
                premiumType: 'trial',
                premiumExpiresAt: expiresIso,
                hasReceivedWelcomeGift: true
            } : prev);

            setIsWelcomeGiftOpen(false);
            setPremiumAlert('success');
        } catch (error) {
            console.error("Erreur réclamation cadeau bienvenue :", error);
            setPremiumAlert('error');
        } finally {
            setIsClaimingGift(false);
        }
    };

    const handleDismissWelcomeGift = () => {
        setIsWelcomeGiftOpen(false);
        try {
            sessionStorage.setItem('zoomap_welcome_gift_dismissed', 'true');
        } catch (e) {
            console.error(e);
        }
    };

    const handleTestPremium = async () => {
        if (!user) return;
        
        try {
            const now = new Date();
            const expiresDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
            const expiresIso = expiresDate.toISOString();

            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                isPremium: true,
                premiumType: 'trial',
                premiumExpiresAt: expiresIso,
                hasReceivedWelcomeGift: true
            });

            setUserData(prev => prev ? {
                ...prev,
                isPremium: true,
                premiumType: 'trial',
                premiumExpiresAt: expiresIso,
                hasReceivedWelcomeGift: true
            } : prev);
            
            // On indique juste que c'est un succès
            setPremiumAlert('success');
            
        } catch (error) {
            console.error("Erreur lors de l'activation du Premium :", error);
            setPremiumAlert('error');
        }
    };

    // Configuration des liens de paiement Stripe officiels
    const STRIPE_LINKS = {
        monthly: 'https://buy.stripe.com/5kQdR28LV9Bt2tndU4fbq00',
        annual: 'https://buy.stripe.com/5kQ3cobY77tl2tn2bmfbq01'
    };

    // Redirection vers le paiement Stripe sécurisé avec identifiants joueur
    const handleSubscribe = (plan) => {
        if (!user) {
            alert(t('login_required_subscription', 'Veuillez vous connecter pour associer votre abonnement Premium à votre compte.'));
            signInWithGoogle();
            return;
        }

        const baseUrl = plan === 'annual' ? STRIPE_LINKS.annual : STRIPE_LINKS.monthly;
        if (!baseUrl) return;

        try {
            const url = new URL(baseUrl);
            url.searchParams.set('client_reference_id', user.uid);
            if (user.email) {
                url.searchParams.set('prefilled_email', user.email);
            }
            window.location.href = url.toString();
        } catch (e) {
            console.error("Erreur redirection Stripe :", e);
            window.location.href = baseUrl;
        }
    };

    // Résiliation du statut Premium
    const handleUnsubscribe = async () => {
        if (!user) return;
        try {
            const userRef = doc(db, 'users', user.uid);
            await updateDoc(userRef, {
                isPremium: false
            });
            alert(t('unsubscribe_success_msg', 'Votre abonnement Premium a été résilié avec succès.'));
        } catch (error) {
            console.error("Erreur lors de la résiliation :", error);
        } finally {
            setIsCancelModalOpen(false);
        }
    };

// ==========================================
// MÉCANIQUES GÉO-DÉTECTIVE & DUEL
// ==========================================
const handleStartGame = async (mode = 'solo') => {
    if (!user) { alert(t('Connect', "Veuillez vous connecter pour jouer !")); return; } 
    if (mode === 'daily') {
        const todayStr = new Date().toISOString().slice(0, 10);
        try {
            const snap = await getDoc(doc(db, 'daily_leaderboards', todayStr, 'players', user.uid));
            if (snap.exists()) {
                alert(t('already_played_daily', "Vous avez déjà joué le Daily Zoom aujourd'hui ! Rendez-vous demain pour un nouveau défi."));
                return;
            } else if (userData?.lastDailyZoomDate === todayStr) {
                // Nettoyage en cas de faux verrouillage antérieur
                setUserData(prev => prev ? { ...prev, lastDailyZoomDate: null } : prev);
                updateDoc(doc(db, "users", user.uid), { lastDailyZoomDate: null }).catch(console.error);
            }
        } catch (e) {
            console.error("Erreur vérification daily leaderboard :", e);
        }
    }
    if (mode === 'duel' && !isPremiumUser) {
        setCurrentView('account');
        return;
    }

    // Gestion du Mode Duel : Ouverture de la modale de choix (Aléatoire vs Ami)
    if (mode === 'duel') {
        const today = new Date().toISOString().slice(0, 10);
        const gData = userData?.geoDetective || {};
        const attToday = (gData.lastPlayDate === today) ? gData.attemptsToday || 0 : 0;
        if (attToday >= (isPremiumUser ? 5 : 2)) {
            setDailyLimitReached(true);
            return;
        }
        setDuelModalStep('choice');
        setPrivateDuelCode('');
        setInputPrivateCode('');
        setPrivateCodeError('');
        setIsDuelModalOpen(true);
        return;
    }

    setCurrentGameMode(mode);

    alertedMilestonesRef.current.clear();
    alertedAttemptsRef.current.clear();
    duelGoalAlertShownRef.current = false;
    resetMapToInitialView();
    setIsGameLoading(true); setGameFeedback(null); setGameImage(null); setGameId(null); setLastGuessLocation(null); setGuessHistory([]); setIsRoulettePlaying(true);
    let currentIdx = 0; setCurrentRouletteImage(rouletteImages[0]); 
    const rouletteInterval = setInterval(() => { currentIdx = (currentIdx + 1) % rouletteImages.length; setCurrentRouletteImage(rouletteImages[currentIdx]); }, 150);
    
    try {
        const result = await httpsCallable(functions, 'startGeoDetectiveGame')({ gameMode: mode });
        if (result.data?.limitReached) { clearInterval(rouletteInterval); setIsRoulettePlaying(false); setDailyLimitReached(true); return; }
        await new Promise(resolve => { const img = new Image(); img.src = result.data.imageUrl; img.onload = resolve; img.onerror = resolve; });
        clearInterval(rouletteInterval); setIsRoulettePlaying(false); setGameImage(result.data.imageUrl); setGameId(result.data.gameId); setGuessCount(0);
        setTimeLeft(300); 
    } catch (error) { 
        clearInterval(rouletteInterval); setIsRoulettePlaying(false);
        if (error.code === 'already-exists' || error.message?.includes('already_played_daily')) {
            alert(t('already_played_daily', "Vous avez déjà joué le Daily Zoom aujourd'hui ! Rendez-vous demain pour un nouveau défi."));
        } else {
            alert(`Erreur inattendue : ${error.message}`);
        }
    } finally { 
        setIsGameLoading(false); 
    }
};

// 1. Recherche de rival aléatoire avec compte à rebours de 30 secondes
const startRandomDuelSearch = async () => {
    isDuelSearchCancelledRef.current = false;
    setIsDuelModalOpen(false);
    resetMapToInitialView();
    setIsGameLoading(true); setGameFeedback(null); setGameImage(null); setGameId(null); setLastGuessLocation(null); setGuessHistory([]); setShowDuelIntro(false); duelIntroShownRef.current = false; setIsRoulettePlaying(true);
    let currentIdx = 0; setCurrentRouletteImage(rouletteImages[0]); 
    const rouletteInterval = setInterval(() => { currentIdx = (currentIdx + 1) % rouletteImages.length; setCurrentRouletteImage(rouletteImages[currentIdx]); }, 150);

    setDuelSearchTimeLeft(30);

    try {
        const myPseudo = userData?.displayName || (user?.displayName ? user.displayName.trim().split(' ')[0] : '');
        const result = await httpsCallable(functions, 'joinDuelQueue')({ action: 'random', displayName: myPseudo });
        if (isDuelSearchCancelledRef.current) {
            clearInterval(rouletteInterval);
            setIsRoulettePlaying(false);
            setIsGameLoading(false);
            if (result.data?.roomId) {
                httpsCallable(functions, 'cancelDuelQueue')({ roomId: result.data.roomId }).catch(() => {});
            }
            return;
        }
        if (result.data?.limitReached) { 
            clearInterval(rouletteInterval); setIsRoulettePlaying(false); setIsGameLoading(false); setDailyLimitReached(true); return; 
        }
        setTimeLeft(180);
        setDuelRoomId(result.data.roomId);
        setDuelStatus(result.data.status || 'waiting'); 
        clearInterval(rouletteInterval); 
        setCurrentRouletteImage(rouletteImages[0]); 

        if (result.data.status === 'playing') {
            setIsGameLoading(false);
        } else {
            // Lancement du compte à rebours de 30 secondes
            if (duelSearchTimerRef.current) clearInterval(duelSearchTimerRef.current);
            let countdown = 30;
            duelSearchTimerRef.current = setInterval(async () => {
                countdown -= 1;
                setDuelSearchTimeLeft(countdown);
                if (countdown <= 0) {
                    clearInterval(duelSearchTimerRef.current);
                    await handleCancelDuelSearch(true);
                }
            }, 1000);
        }
    } catch (error) { 
        clearInterval(rouletteInterval); setIsRoulettePlaying(false); setIsGameLoading(false); 
        if (!isDuelSearchCancelledRef.current) {
            alert(`Erreur : ${error.message}`); 
        }
    }
};

// 2. Annulation de la recherche aléatoire (fermeture complète : modale, recherche, duel_room)
const handleCancelDuelSearch = async (isTimeout = false) => {
    isDuelSearchCancelledRef.current = true;
    if (duelSearchTimerRef.current) clearInterval(duelSearchTimerRef.current);
    const rId = duelRoomId;
    setIsRoulettePlaying(false);
    setIsGameLoading(false);
    setIsDuelModalOpen(false);
    setDuelRoomId(null);
    setDuelStatus('none');
    setDuelSearchTimeLeft(30);
    setShowDuelIntro(false);
    duelIntroShownRef.current = false;
    resetMapToInitialView();

    if (rId) {
        try {
            await httpsCallable(functions, 'cancelDuelQueue')({ roomId: rId });
        } catch (e) {
            console.error("Erreur annulation queue duel :", e);
        }
    }

    if (isTimeout) {
        setDuelTimeoutNotice(true);
    }
};

// 3. Création d'une partie privée avec code à 2 chiffres généré immédiatement
const handleCreatePrivateDuel = async () => {
    setIsCreatingPrivate(true);
    setPrivateCodeError('');
    duelIntroShownRef.current = false;
    setGameFeedback(null);
    setGameImage(null);
    setGameId(null);
    setLastGuessLocation(null);
    setGuessHistory([]);
    setShowDuelIntro(false);
    setIsRoulettePlaying(false);
    setCurrentView('map');
    setActiveGameMode('geodetective');
    setTimeLeft(180);

    // Génération instantanée d'un code à 2 chiffres (10-99) pour affichage immédiat
    const clientCode = String(Math.floor(10 + Math.random() * 90));
    setPrivateDuelCode(clientCode);
    setDuelModalStep('create_private');

    try {
        const myPseudo = userData?.displayName || (user?.displayName ? user.displayName.trim().split(' ')[0] : '');
        const result = await httpsCallable(functions, 'joinDuelQueue')({ action: 'create_private', code: clientCode, displayName: myPseudo });
        if (result.data?.limitReached) {
            setIsCreatingPrivate(false);
            setIsDuelModalOpen(false);
            setDailyLimitReached(true);
            return;
        }
        setDuelRoomId(result.data.roomId);
        setDuelStatus('waiting');
        if (result.data?.code) {
            setPrivateDuelCode(result.data.code);
        }
    } catch (error) {
        console.error("Erreur création duel privé :", error);
        setPrivateCodeError(error.message);
    } finally {
        setIsCreatingPrivate(false);
    }
};

// 4. Annulation d'une partie privée créée
const handleCancelPrivateDuel = async () => {
    const rId = duelRoomId;
    setPrivateDuelCode('');
    setDuelRoomId(null);
    setDuelStatus('none');
    setShowDuelIntro(false);
    duelIntroShownRef.current = false;
    setDuelModalStep('choice');
    setIsDuelModalOpen(false);
    if (rId) {
        try {
            await httpsCallable(functions, 'cancelDuelQueue')({ roomId: rId });
        } catch (e) {
            console.error(e);
        }
    }
};

// 5. Rejoindre une partie privée avec le code saisi
const handleJoinPrivateDuel = async () => {
    const cleanCode = inputPrivateCode.trim();
    if (cleanCode.length !== 2) {
        setPrivateCodeError(t('duel_enter_code', "Entrez le code à 2 chiffres :"));
        return;
    }
    setIsJoiningPrivate(true);
    setPrivateCodeError('');
    duelIntroShownRef.current = false;
    setGameFeedback(null);
    setGameImage(null);
    setGameId(null);
    setLastGuessLocation(null);
    setGuessHistory([]);
    setShowDuelIntro(false);
    setIsRoulettePlaying(false);
    setCurrentView('map');
    setActiveGameMode('geodetective');
    setTimeLeft(180);

    try {
        const myPseudo = userData?.displayName || (user?.displayName ? user.displayName.trim().split(' ')[0] : '');
        const result = await httpsCallable(functions, 'joinDuelQueue')({ action: 'join_private', code: cleanCode, displayName: myPseudo });
        if (result.data?.notFound) {
            setPrivateCodeError(result.data.message || t('duel_code_not_found', "Code introuvable ou déjà expiré."));
            setIsJoiningPrivate(false);
            return;
        }
        if (result.data?.limitReached) {
            setIsJoiningPrivate(false);
            setIsDuelModalOpen(false);
            setDailyLimitReached(true);
            return;
        }
        setDuelRoomId(result.data.roomId);
        setDuelStatus(result.data.status);
        setIsDuelModalOpen(false);
    } catch (error) {
        setPrivateCodeError(error.message);
    } finally {
        setIsJoiningPrivate(false);
    }
};

// NOUVEAU : Fonction pour rejouer une image spécifique depuis la Collection
const handleReplayGame = async (zoomToPlay) => {
    if (!user) { alert(t('Connect')); return; } 
    if (!isPremiumUser) {
        setIsPokedexOpen(false);
        setCurrentView('account');
        return;
    }
    
    // 1. On ferme toutes les fenêtres pour revenir sur la carte
    setIsPokedexOpen(false);
    setCurrentView('map');
    setIsInfoModalOpen(false);
    setIsBadgeModalOpen(false);
    resetMapToInitialView();
    
    alertedMilestonesRef.current.clear();
    alertedAttemptsRef.current.clear();
    duelGoalAlertShownRef.current = false;
    // 2. On lance l'animation de chargement
    setCurrentGameMode('practice');
    setIsGameLoading(true); setGameFeedback(null); setGameImage(null); setGameId(null); setLastGuessLocation(null); setGuessHistory([]); setIsRoulettePlaying(true);
    let currentIdx = 0; setCurrentRouletteImage(rouletteImages[0]); 
    const rouletteInterval = setInterval(() => { currentIdx = (currentIdx + 1) % rouletteImages.length; setCurrentRouletteImage(rouletteImages[currentIdx]); }, 150);
    
    try {
        // 3. ON APPELLE TON SERVEUR 
        // ⚠️ On lui passe un mode 'practice' (entraînement) et l'ID du document à charger
        const result = await httpsCallable(functions, 'startGeoDetectiveGame')({ 
            gameMode: 'practice', 
            specificChallengeId: zoomToPlay.id 
        });
        
        // 4. On charge l'image
        await new Promise(resolve => { const img = new Image(); img.src = result.data.imageUrl; img.onload = resolve; img.onerror = resolve; });
        
        // 5. On démarre la partie !
        clearInterval(rouletteInterval); 
        setIsRoulettePlaying(false); 
        setGameImage(result.data.imageUrl); 
        setGameId(result.data.gameId); 
        setGuessCount(0);
        setTimeLeft(300); // 5 minutes d'entraînement
        
    } catch (error) { 
        clearInterval(rouletteInterval); setIsRoulettePlaying(false); alert(`Erreur technique : ${error.message}`); 
    } finally { 
        setIsGameLoading(false); 
    }
};

const handleStopGame = () => { 
    setGameId(null); 
    setGameImage(null); 
    setGuessCount(0); 
    setGameFeedback(null); 
    setLastGuessLocation(null); 
    setGuessHistory([]); 
    setShowDuelIntro(false);
    duelIntroShownRef.current = false;
    setTimeAlert(null);
    if (timeAlertTimeoutRef.current) clearTimeout(timeAlertTimeoutRef.current);
    alertedMilestonesRef.current.clear();
    alertedAttemptsRef.current.clear();
    duelGoalAlertShownRef.current = false;
    stopBackgroundMusic();
    resetMapToInitialView();
};
const handleZoomIn = () => { if (map) map.setZoom(map.getZoom() + 1); };
const handleZoomOut = () => { if (map) map.setZoom(map.getZoom() - 1); };



// ==========================================
// CLASSEMENT (Leaderboard)
// ==========================================
const [leaderboardTab, setLeaderboardTab] = useState('daily');
const [leaderboardData, setLeaderboardData] = useState({ daily: [], solo: [], duel: [], myRank: { daily: null, solo: null, duel: null } });
const getPlayerGrade = (player, tab) => {
    if (!player) return t('rank_tourist', 'Touriste');
    let value = 0;

    if (tab === 'duel') {
        // --- MODE DUEL (Basé uniquement sur le Winrate %) ---
        if (player.winRate !== undefined) {
            value = Number(player.winRate) || 0;
        } else if (typeof player.displayScore === 'string' && player.displayScore.includes('%')) {
            value = parseFloat(player.displayScore) || 0;
        } else if (player.score !== undefined) {
            value = Number(player.score) || 0;
        }

        if (value >= 80) return t('rank_legend', 'Légende');
        if (value >= 60) return t('rank_explorer', 'Explorateur');
        if (value >= 40) return t('rank_adventurer', 'Aventurier');
        if (value >= 20) return t('rank_traveler', 'Voyageur');
        return t('rank_tourist', 'Touriste');
        
    } else if (tab === 'daily') {
        // --- MODE DAILY ZOOM (Basé uniquement sur les Points) ---
        if (player.score !== undefined) {
            value = Number(player.score) || 0;
        } else if (player.displayScore) {
            value = Number(player.displayScore.toString().replace(/[^0-9]/g, "")) || 0;
        }

        if (value >= 4000) return t('rank_legend', 'Légende');
        if (value >= 3000) return t('rank_explorer', 'Explorateur');
        if (value >= 2000) return t('rank_adventurer', 'Aventurier');
        if (value >= 1000) return t('rank_traveler', 'Voyageur');
        return t('rank_tourist', 'Touriste');

    } else {
        // --- MODE SOLO (Basé uniquement sur la moyenne ELO ou les Points) ---
        if (player.averageElo !== undefined) {
            value = Number(player.averageElo) || 0;
        } else if (player.score !== undefined) {
            value = Number(player.score) || 0;
        } else if (player.displayScore) {
            value = Number(player.displayScore.toString().replace(/[^0-9]/g, "")) || 0;
        }

        if (value >= 4000) return t('rank_legend', 'Légende');
        if (value >= 3000) return t('rank_explorer', 'Explorateur');
        if (value >= 2000) return t('rank_adventurer', 'Aventurier');
        if (value >= 1000) return t('rank_traveler', 'Voyageur');
        return t('rank_tourist', 'Touriste');
    }
};

// On cherche le joueur actuel dans les 3 tableaux du classement
const myDaily = leaderboardData?.daily?.find(p => p.id === user?.uid) || leaderboardData?.myRank?.daily;
const mySolo = leaderboardData?.solo?.find(p => p.id === user?.uid) || leaderboardData?.myRank?.solo;
const myDuel = leaderboardData?.duel?.find(p => p.id === user?.uid) || leaderboardData?.myRank?.duel;

// Calcul du rang Solo du joueur pour son profil / badge principal
const userSoloElo = (userData?.geoDetective?.gamesPlayed && userData.score)
    ? Math.round(userData.score / userData.geoDetective.gamesPlayed)
    : (Number(userData?.score) || 0);
const userSoloGrade = getPlayerGrade(mySolo || { averageElo: userSoloElo, score: userSoloElo }, 'solo');
const getOpponentSoloGrade = (opp) => {
    if (opp?.soloGrade) return opp.soloGrade;
    const oppInLeaderboard = leaderboardData?.solo?.find(p => p.id === opp?.uid);
    if (oppInLeaderboard) {
        return getPlayerGrade(oppInLeaderboard, 'solo');
    }
    return t('rank_tourist', 'Touriste');
};

// --- DONNÉES DYNAMIQUES DU FACE-À-FACE DUEL ---
const myDuelAttempts = Math.min(5, gameFeedback?.guessCount || guessCount || 0);
const oppDuelAttempts = Math.min(5, opponentData?.attemptsUsed || 0);
const validHistoryDistances = guessHistory
    .map(g => g.distance)
    .filter(d => d !== null && d !== undefined && typeof d === 'number');
const bestHistoryDist = validHistoryDistances.length > 0 ? Math.min(...validHistoryDistances) : null;
const effectiveMyBestDist = (myDuelBestDistance !== null && bestHistoryDist !== null)
    ? Math.min(myDuelBestDistance, bestHistoryDist)
    : (myDuelBestDistance !== null ? myDuelBestDistance : bestHistoryDist);
const effectiveOppBestDist = (opponentData?.bestDistance !== undefined && opponentData?.bestDistance !== null && opponentData.bestDistance < 99999)
    ? opponentData.bestDistance
    : null;
const oppDisplayName = opponentData?.displayName || t('rival', 'Rival');

let duelRaceStatus = 'neutral';
if (effectiveMyBestDist !== null && (effectiveOppBestDist === null || Math.round(effectiveMyBestDist) < Math.round(effectiveOppBestDist))) {
    duelRaceStatus = 'leading';
} else if (effectiveOppBestDist !== null && (effectiveMyBestDist === null || Math.round(effectiveOppBestDist) < Math.round(effectiveMyBestDist))) {
    duelRaceStatus = 'behind';
} else if (effectiveMyBestDist !== null && effectiveOppBestDist !== null && Math.round(effectiveMyBestDist) === Math.round(effectiveOppBestDist)) {
    duelRaceStatus = 'tied';
}

const fetchLeaderboard = async (offset = 0, onlyDaily = false) => { 
    try { 
        let solo = [];
        let mySoloRank = null;
        let duel = [];
        let myDuelRank = null;
        let allUsersList = cachedUsersListRef.current || [];

        const shouldFetchUsers = !onlyDaily || allUsersList.length === 0;

        if (shouldFetchUsers) {
            // 1. CHARGEMENT SOLO ET DUEL (depuis la collection users)
            const usersSnap = await getDocs(query(collection(db, "users"), limit(500)));
            const allUsers = usersSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            allUsersList = allUsers.map(u => {
                if (user && user.uid && u.id === user.uid && userData) {
                    return { ...u, ...userData };
                }
                return u;
            });
            if (user && user.uid && !allUsersList.some(u => u.id === user.uid) && userData) {
                allUsersList.push({ id: user.uid, ...userData });
            }
            cachedUsersListRef.current = allUsersList;

            // --- SOLO ELO ---
            const sortedSoloAll = allUsersList
                .filter(u => u.geoDetective?.gamesPlayed >= 5 && u.score)
                .map(u => ({ 
                    ...u, 
                    displayName: (user?.uid && (u.id === user.uid || u.uid === user.uid) && (userData?.pseudo || userData?.displayName))
                        ? (userData.pseudo || userData.displayName)
                        : (u.displayName || u.pseudo || u.playerName || 'Joueur'),
                    averageElo: Math.round(u.score / u.geoDetective.gamesPlayed),
                    twitch: u.twitch || u.twitchChannel || u.twitchUrl || null,
                    youtube: u.youtube || u.youtubeChannel || u.youtubeUrl || null,
                    tiktok: u.tiktok || u.tiktokChannel || u.tiktokUrl || null,
                    instagram: u.instagram || u.insta || u.instagramUrl || null
                }))
                .sort((a, b) => b.averageElo - a.averageElo)
                .map((u, i) => ({ 
                    ...u, 
                    rank: i + 1, 
                    displayScore: `${(u.averageElo).toLocaleString()} ELO` 
                }));

            solo = sortedSoloAll.slice(0, 50);
            mySoloRank = user?.uid ? sortedSoloAll.find(u => u.id === user.uid || u.uid === user.uid) || null : null;

            // --- DUEL WINRATE ---
            const sortedDuelAll = allUsersList
                .filter(u => u.duelGamesPlayed >= 5)
                .map(u => ({ 
                    ...u, 
                    displayName: (user?.uid && (u.id === user.uid || u.uid === user.uid) && (userData?.pseudo || userData?.displayName))
                        ? (userData.pseudo || userData.displayName)
                        : (u.displayName || u.pseudo || u.playerName || 'Joueur'),
                    winRate: Math.round(((u.duelWins || 0) / u.duelGamesPlayed) * 100),
                    twitch: u.twitch || u.twitchChannel || u.twitchUrl || null,
                    youtube: u.youtube || u.youtubeChannel || u.youtubeUrl || null,
                    tiktok: u.tiktok || u.tiktokChannel || u.tiktokUrl || null,
                    instagram: u.instagram || u.insta || u.instagramUrl || null
                }))
                .sort((a, b) => b.winRate - a.winRate)
                .map((u, i) => ({ 
                    ...u, 
                    rank: i + 1, 
                    displayScore: `${u.winRate}% WIN` 
                }));

            duel = sortedDuelAll.slice(0, 50);
            myDuelRank = user?.uid ? sortedDuelAll.find(u => u.id === user.uid || u.uid === user.uid) || null : null;
        }

        // 2. CHARGEMENT DAILY ZOOM (Nouveau système temporel)
        // Calcul de la date ciblée (ex: si offset = -1, on recule de 24h)
        const targetDate = new Date(Date.now() + offset * 86400000);
        const dateStr = targetDate.toISOString().slice(0, 10); // Format YYYY-MM-DD pour correspondre au backend
        
        const dailyRef = collection(db, 'daily_leaderboards', dateStr, 'players');
        const dailySnap = await getDocs(query(dailyRef)); 
        
        let daily = [];
        let myDailyRank = null;
        let dailyUsers = [];
        if (!dailySnap.empty) {
            dailyUsers = dailySnap.docs.map(doc => {
                const data = doc.data();
                return {
                    ...data,
                    id: doc.id,
                    uid: data.uid || doc.id
                };
            });
        }

        // Si l'utilisateur connecté n'est pas trouvé dans la liste (latence Firestore, pagination ou premier jeu)
        if (user?.uid && !dailyUsers.some(u => u.id === user.uid || u.uid === user.uid)) {
            try {
                const myDirectDoc = await getDoc(doc(db, 'daily_leaderboards', dateStr, 'players', user.uid));
                if (myDirectDoc.exists()) {
                    const data = myDirectDoc.data();
                    dailyUsers.push({
                        ...data,
                        id: user.uid,
                        uid: user.uid
                    });
                } else if (offset === 0 && (userData?.lastDailyZoomDate === dateStr || typeof finalScore === 'number')) {
                    dailyUsers.push({
                        score: typeof finalScore === 'number' ? finalScore : 0,
                        playerName: userData?.pseudo || userData?.displayName || user?.displayName || 'Joueur',
                        id: user.uid,
                        uid: user.uid
                    });
                }
            } catch (e) {
                console.warn("Erreur fetch direct score daily joueur :", e);
            }
        }

        if (dailyUsers.length > 0) {
            const sortedDailyAll = dailyUsers
                .sort((a, b) => {
                    const diff = (Number(b.score) || 0) - (Number(a.score) || 0);
                    if (diff !== 0) return diff;
                    const timeA = a.timestamp?.toMillis ? a.timestamp.toMillis() : (a.timestamp ? new Date(a.timestamp).getTime() : 0);
                    const timeB = b.timestamp?.toMillis ? b.timestamp.toMillis() : (b.timestamp ? new Date(b.timestamp).getTime() : 0);
                    return timeA - timeB;
                })
                .map((u, i) => {
                    const isMe = user?.uid && (u.id === user.uid || u.uid === user.uid);
                    const userDocInAll = allUsersList.find(au => au.id === u.id || au.id === u.uid);
                    const mySocials = isMe ? userData : userDocInAll;
                    return { 
                        ...u, 
                        rank: i + 1, 
                        displayName: isMe && (userData?.pseudo || userData?.displayName)
                            ? (userData.pseudo || userData.displayName)
                            : (u.playerName || u.displayName || u.pseudo || userDocInAll?.displayName || userDocInAll?.pseudo || 'Joueur'),
                        displayScore: `${Number(u.score || 0).toLocaleString()} PTS`,
                        twitch: u.twitch || u.twitchChannel || u.twitchUrl || mySocials?.twitch || mySocials?.twitchChannel || mySocials?.twitchUrl || null,
                        youtube: u.youtube || u.youtubeChannel || u.youtubeUrl || mySocials?.youtube || mySocials?.youtubeChannel || mySocials?.youtubeUrl || null,
                        tiktok: u.tiktok || u.tiktokChannel || u.tiktokUrl || mySocials?.tiktok || mySocials?.tiktokChannel || mySocials?.tiktokUrl || null,
                        instagram: u.instagram || u.insta || u.instagramUrl || mySocials?.instagram || mySocials?.insta || mySocials?.instagramUrl || null
                    };
                });
            daily = sortedDailyAll.slice(0, 50);
            myDailyRank = user?.uid ? sortedDailyAll.find(u => u.id === user.uid || u.uid === user.uid) || null : null;
        }

        // Déverrouillage automatique en cas de faux verrouillage (sans requête supplémentaire !)
        if (offset === 0 && user?.uid && userData?.lastDailyZoomDate === dateStr && !myDailyRank) {
            setUserData(prev => prev ? { ...prev, lastDailyZoomDate: null } : prev);
            updateDoc(doc(db, "users", user.uid), { lastDailyZoomDate: null }).catch(console.error);
        }

        // On met à jour l'interface avec les 3 tableaux et les rangs complets du joueur
        setLeaderboardData(prev => ({ 
            daily, 
            solo: shouldFetchUsers ? solo : prev.solo, 
            duel: shouldFetchUsers ? duel : prev.duel,
            myRank: {
                daily: myDailyRank,
                solo: shouldFetchUsers ? mySoloRank : prev.myRank?.solo,
                duel: shouldFetchUsers ? myDuelRank : prev.myRank?.duel
            }
        })); 
        
    } catch (error) { 
        alert("Impossible de charger le classement."); console.error(error);
    } 
};

// ==========================================
// PROFIL & ÉQUIPES
// ==========================================
const handleUpdatePseudo = async () => { 
    const trimmed = newDisplayName.trim();
    if (!trimmed || !user?.uid) return; 
    if (trimmed.length < 4 || trimmed.length > 15) {
        setPseudoError(t('pseudo_length_error', 'Le pseudo doit comporter entre 4 et 15 caractères.'));
        return;
    }
    try { 
        await updateDoc(doc(db, 'users', user.uid), { 
            displayName: trimmed,
            hasChosenPseudo: true 
        }); 
        setUserData(prev => prev ? { ...prev, displayName: trimmed, hasChosenPseudo: true } : prev);
        setIsEditingName(false); 
        setPseudoError('');
    } catch (error) { 
        console.error("Erreur mise à jour pseudo :", error); 
        setPseudoError(error.message);
    } 
};

const handleValidateOnboarding = async (e) => {
    if (e) e.preventDefault();
    const trimmed = onboardingPseudo.trim();
    if (trimmed.length < 4 || trimmed.length > 15) {
        setOnboardingError(t('pseudo_length_error', 'Le pseudo doit comporter entre 4 et 15 caractères.'));
        return;
    }
    setIsOnboardingSaving(true);
    try {
        await updateDoc(doc(db, 'users', user.uid), {
            displayName: trimmed,
            hasChosenPseudo: true
        });
        setUserData(prev => prev ? { ...prev, displayName: trimmed, hasChosenPseudo: true } : prev);
        setIsOnboardingOpen(false);
        setOnboardingError('');

        // Déclencher le Cadeau de Bienvenue juste après l'enregistrement du pseudo
        if (!userData?.isPremium && !userData?.hasReceivedWelcomeGift) {
            setIsWelcomeGiftOpen(true);
        }
    } catch (err) {
        console.error("Erreur enregistrement pseudo onboarding :", err);
        setOnboardingError(err.message);
    } finally {
        setIsOnboardingSaving(false);
    }
};

const handleJoinTeam = async (teamData) => { setIsProcessingTeam(true); try { await updateDoc(doc(db, 'users', user.uid), { teamName: teamData.name, ownerBadgeName: teamData.name, ownerBadge: teamData.imageUrl }); setUserData(prev => ({ ...prev, teamName: teamData.name, ownerBadgeName: teamData.name, ownerBadge: teamData.imageUrl })); } catch (e) { console.error(e); } setIsProcessingTeam(false); };
const handleImageSelect = (e) => { const file = e.target.files[0]; if (!file) return; const reader = new FileReader(); reader.readAsDataURL(file); reader.onload = ev => { const img = new Image(); img.onload = () => { const cvs = document.createElement('canvas'), ctx = cvs.getContext('2d'), s = 400, min = Math.min(img.width, img.height); cvs.width = cvs.height = s; ctx.drawImage(img, (img.width-min)/2, (img.height-min)/2, min, min, 0, 0, s, s); setPendingImage(cvs.toDataURL('image/webp', 0.92)); }; img.src = ev.target.result; }; };
const handleConfirmCreate = async () => { const t_name = teamNameInput.trim(); if (!t_name || !pendingImage) return; setIsProcessingTeam(true); try { const ref = doc(db, 'teams', t_name.toLowerCase()); if ((await getDoc(ref)).exists()) { alert(t('team_exists', 'Ce nom existe déjà.')); return setIsProcessingTeam(false); } await setDoc(ref, { name: t_name, imageUrl: pendingImage, creatorId: user.uid }); await updateDoc(doc(db, 'users', user.uid), { teamName: t_name, ownerBadgeName: t_name, ownerBadge: pendingImage }); setUserData(prev => ({ ...prev, teamName: t_name, ownerBadgeName: t_name, ownerBadge: pendingImage })); setPublicTeams(prev => [...prev, { name: t_name, imageUrl: pendingImage }]); setPendingImage(null); setTeamNameInput(''); } catch (err) { alert(t('error_create', 'Erreur.')); } setIsProcessingTeam(false); };
const handleLeaveTeam = async () => { if (!window.confirm("Voulez-vous vraiment quitter votre équipe ?")) return; setIsProcessingTeam(true); try { await updateDoc(doc(db, 'users', user.uid), { teamName: "", ownerBadgeName: "", ownerBadge: "" }); setUserData(prev => ({ ...prev, teamName: "", ownerBadgeName: "", ownerBadge: "" })); } catch (e) { alert("Erreur."); } setIsProcessingTeam(false); };


// ==========================================
// RADAR DUEL
// ==========================================
useEffect(() => {
    if (!duelRoomId || !user) return;
    const unsubscribe = onSnapshot(doc(db, 'duel_rooms', duelRoomId), (docSnap) => {
        if (!docSnap.exists()) return;
        const roomData = docSnap.data();
        setDuelStatus(roomData.status); 

        const isMeP1 = roomData.player1.uid === user.uid;
        const myData = isMeP1 ? roomData.player1 : roomData.player2;
        const oppData = isMeP1 ? roomData.player2 : roomData.player1;

        if (roomData.status === 'playing') {
            const isInitialStart = !duelIntroShownRef.current;
            setIsDuelModalOpen(false);
            if (duelSearchTimerRef.current) clearInterval(duelSearchTimerRef.current);
            setOpponentData(oppData);
            setMyDuelBestDistance((myData?.bestDistance !== undefined && myData.bestDistance < 99999) ? myData.bestDistance : null);
            setCurrentView('map');
            setActiveGameMode('geodetective');
            setIsGameLoading(false);

            // Déclenchement de l'animation d'introduction Face-off "VS"
            if (isInitialStart && roomData.player1 && roomData.player2) {
                duelIntroShownRef.current = true;
                const p1SoloGrade = roomData.player1.soloGrade || (isMeP1 ? userSoloGrade : getOpponentSoloGrade(roomData.player1));
                const p2SoloGrade = roomData.player2.soloGrade || (!isMeP1 ? userSoloGrade : getOpponentSoloGrade(roomData.player2));
                const myCurrentName = userData?.displayName || (user?.displayName ? user.displayName.trim().split(' ')[0] : '') || 'Moi';
                const p1DisplayName = isMeP1 ? myCurrentName : (roomData.player1.displayName || 'Joueur 1');
                const p2DisplayName = !isMeP1 ? myCurrentName : (roomData.player2.displayName || 'Joueur 2');
                setDuelPlayersData({
                    player1: { ...roomData.player1, displayName: p1DisplayName, soloGrade: p1SoloGrade },
                    player2: { ...roomData.player2, displayName: p2DisplayName, soloGrade: p2SoloGrade }
                });
                setShowDuelIntro(true);
            }

            if (roomData.imageUrl) {
                setGameImage(roomData.imageUrl);
                setGameId(duelRoomId);
                setGuessCount(myData?.attemptsUsed || 0);
                setIsRoulettePlaying(false);
                if (isInitialStart) {
                    setGameFeedback(null);
                }
                const img = new Image();
                img.src = roomData.imageUrl;
            } else {
                setIsRoulettePlaying(false);
            }
        }

        if (roomData.status === 'finished') {
            setShowDuelIntro(false);
            if (duelSearchTimerRef.current) clearInterval(duelSearchTimerRef.current);
            setOpponentData(oppData);
            const isTie = !roomData.winnerUid;
            const isWin = roomData.winnerUid === user.uid;
            const isRefund = isTie && (roomData.isRefund === true || (myData.attemptsUsed === 0 && oppData.attemptsUsed === 0));
            const isForfeitWin = isWin && roomData.surrenderedBy && roomData.surrenderedBy !== user.uid;
            
            // On vérifie si le joueur a VRAIMENT trouvé le lieu exact en utilisant la tolérance dynamique (ou 2km par défaut)
            const acceptedTolerance = roomData.tolerance || 2;
            const hasFoundExactLocation = myData.bestDistance !== 99999 && myData.bestDistance <= acceptedTolerance;

            setGameFeedback({
                isDuelFinished: true, 
                isWon: isWin, 
                isLost: !isWin && !isTie && !isRefund, 
                isTie: isTie && !isRefund, 
                isRefund: isRefund,
                isForfeitWin: isForfeitWin,
                myDistance: myData.bestDistance !== 99999 ? myData.bestDistance : null, 
                oppDistance: oppData.bestDistance !== 99999 ? oppData.bestDistance : null, 
                oppAttempts: oppData.attemptsUsed,
                oppData: oppData,
                
                // --- LES INFOS POUR LA COLLECTION (CALQUÉES SUR LE MODE SOLO) ---
                // Si > 2km, tout ça sera 'null', le bouclier du useEffect bloquera l'ajout.
                challengeId: hasFoundExactLocation ? roomData.challengeId : null,
                challengeName: hasFoundExactLocation ? roomData.name : null,
                challengeInfoUrl: hasFoundExactLocation ? roomData.info : null,
                info: hasFoundExactLocation ? roomData.info : null,
                correctLocation: hasFoundExactLocation ? roomData.challengeLocation : null,
                name_FR: hasFoundExactLocation ? roomData.name_FR : null,
                name_ES: hasFoundExactLocation ? roomData.name_ES : null
            });
            setIsRoulettePlaying(false);
        }
    });
    return () => unsubscribe();
}, [duelRoomId, user]);

// Nettoyage automatique en cas de fermeture de l'onglet pendant l'attente d'un adversaire
useEffect(() => {
    const handleBeforeUnload = () => {
        if (duelRoomId && duelStatus === 'waiting') {
            httpsCallable(functions, 'cancelDuelQueue')({ roomId: duelRoomId }).catch(() => {});
        }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
}, [duelRoomId, duelStatus]);

const handleFinishDuelIntro = useCallback(() => {
    setShowDuelIntro(false);
    setTimeLeft(180);
    duelGoalAlertShownRef.current = true;
    triggerHudAlert({
        title: t('duel_alert_title', 'Mode Duel'),
        text: t('duel_5_attempts_goal', '5 tentatives pour te rapprocher le plus possible'),
        urgency: 'normal',
        icon: '⚔️',
        duration: 2000
    });
}, [triggerHudAlert, t]);

// ==========================================
// UI HELPERS & VARIABLES DE LAYOUT
// ==========================================
const getPanelBackground = (rarity) => { if (rarity === 'merveille') return '#FFF38A'; if (rarity === 'rare') return '#CDC1FF'; if (rarity === 'speciale') return '#B8DFFA'; return 'rgb(255, 255, 255)'; };
const getCurrentPotentialReward = (count) => { return Math.max(0, 3000 - (count * 200)); };

let isBonusAvailable = true; if (userData?.lastClaimed) { const lastClaimedDate = userData.lastClaimed.toDate ? userData.lastClaimed.toDate() : new Date(userData.lastClaimed); if (new Date().getTime() - lastClaimedDate.getTime() < 86400000) isBonusAvailable = false; }
const isGameIdle = !gameId && !gameFeedback; 
const hideGameOnMobile = isMobile && currentView === 'account';
const displayChallengeName = gameFeedback?.[`name_${currentLang}`] || gameFeedback?.challengeName; 
const isGameActive = gameId || gameFeedback || isRoulettePlaying;

const containerStyle = { width: '100vw', height: '100vh', position: 'relative', display: isMobile ? 'flex' : 'block', flexDirection: isMobile ? 'column' : 'row', overflow: 'hidden' };
const dGameContainerStyle = isMobile ? (!isGameActive ? { position: 'fixed', bottom: '20px', left: '50%', transform: 'translateX(-50%)', width: '90%', zIndex: 900, backgroundColor: 'white', padding: '15px', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', boxSizing: 'border-box', display: hideGameOnMobile ? 'none' : 'block' } : { position: 'relative', order: 2, width: '100%', maxHeight: '45vh', overflowY: 'auto', backgroundColor: 'white', padding: '8px 8px', borderRadius: '10px 10px 0 0', boxShadow: '0 -4px 20px rgba(0,0,0,0.15)', zIndex: 900, boxSizing: 'border-box', display: hideGameOnMobile ? 'none' : 'block' }) : { position: 'absolute', top: '66px', right: '6px', width: '340px', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: '10px', zIndex: 15, display: 'flex', flexDirection: 'column', gap: '15px' };

const getYouTubeEmbedUrl = (url) => { if (!url) return { embedUrl: null, isShort: false }; try { let videoId = ''; let isShort = false; if (url.includes('youtube.com/shorts/')) { videoId = url.split('shorts/')[1].split('?')[0]; isShort = true; } else if (url.includes('youtube.com/watch')) { videoId = new URLSearchParams(url.split('?')[1]).get('v'); } else if (url.includes('youtu.be/')) { videoId = url.split('youtu.be/')[1].split('?')[0]; } if (videoId) { return { embedUrl: `https://www.youtube.com/embed/${videoId}?rel=0`, isShort }; } } catch (e) { console.error(e); } return { embedUrl: null, isShort: false }; };

// ==========================================
// ELEMENTS RESTAURÉS (Variables et Fonctions manquantes)
// ==========================================
// 1. D'ABORD : Ta ligne actuelle qui déclare la variable
const hasPlayedDailyZoom = userData?.lastDailyZoomDate === new Date().toISOString().slice(0, 10);

// 2. ENSUITE : L'état du compte à rebours
const [countdownText, setCountdownText] = useState('');

// 3. Compte à rebours universel jusqu'à minuit UTC (pour DailyZoom et nouveaux zooms)
useEffect(() => {
    const updateCountdown = () => {
        const now = new Date();
        const tomorrowUTC = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
        const diffMs = Math.max(0, tomorrowUTC - now);
        
        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
        
        const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
        setCountdownText(`${hours}h ${formattedMinutes}m`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
}, []);


const handleClaimCredits = async () => { 
    setClaimStatus({ loading: true, message: "" }); 
    try { 
        const res = await httpsCallable(functions, 'claimWeeklyCredits')(); 
        setClaimStatus({ loading: false, message: res.data.message }); 
    } catch (error) { 
        setClaimStatus({ loading: false, message: error.message }); 
    } 
};

const onMapUnmount = useCallback((mapInstance) => { 
    // La carte se démonte proprement. Plus besoin de nettoyer les grilles et rectangles supprimés.
}, []);

// ==========================================
    // CONNEXION FIREBASE AUTH (LE MOTEUR DE CONNEXION)
    // ==========================================
    

    // 2. L'écouteur qui met à jour l'utilisateur dans l'application
    useEffect(() => { 
        let unsubscribeSnapshot = null; 
        
        const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => { 
            setUser(currentUser); 
            if (currentUser) { 
                const userRef = doc(db, "users", currentUser.uid); 
                let isCreatingUser = false;
                if (unsubscribeSnapshot) {
                    unsubscribeSnapshot();
                    unsubscribeSnapshot = null;
                }
                unsubscribeSnapshot = onSnapshot(userRef, async (snap) => { 
                    if (!snap.exists()) {
                        if (!isCreatingUser) {
                            isCreatingUser = true;
                            try {
                                const defaultFirstName = currentUser.displayName ? currentUser.displayName.trim().split(' ')[0] : 'Explorateur';
                                await setDoc(userRef, { 
                                    displayName: defaultFirstName, 
                                    email: currentUser.email, 
                                    credits: 1000, 
                                    score: 0, 
                                    hasChosenPseudo: false,
                                    createdAt: serverTimestamp() 
                                }); 
                            } catch (err) {
                                console.error("Erreur création profil utilisateur:", err);
                            } finally {
                                isCreatingUser = false;
                            }
                        }
                    } else {
                        setUserData(snap.data()); 
                    }
                }, (error) => {
                    console.error("Erreur écouteur utilisateur:", error);
                }); 
            } else { 
                setUserData(null); 
                if (unsubscribeSnapshot) { 
                    unsubscribeSnapshot(); 
                    unsubscribeSnapshot = null; 
                } 
            } 
        }); 
        
        return () => { 
            unsubscribeAuth(); 
            if (unsubscribeSnapshot) unsubscribeSnapshot(); 
        }; 
    }, []);

    // Gestion de l'affichage de l'onboarding pour choix du pseudo (1ère connexion ou compte sans pseudo personnalisé)
    useEffect(() => {
        if (user && userData) {
            if (userData.hasChosenPseudo !== true) {
                const defaultName = (user.displayName ? user.displayName.trim().split(' ')[0] : '') || userData.displayName || 'Explorateur';
                const initialPseudo = defaultName.length < 4 ? `${defaultName}_` : defaultName;
                setOnboardingPseudo(prev => prev || initialPseudo);
                setIsOnboardingOpen(true);
            } else {
                setIsOnboardingOpen(false);
                // Pour les joueurs existants ayant déjà leur pseudo, proposition du cadeau s'ils ne sont pas premium et ne l'ont jamais reçu
                if (!userData.isPremium && !userData.hasReceivedWelcomeGift) {
                    try {
                        const dismissed = sessionStorage.getItem('zoomap_welcome_gift_dismissed') === 'true';
                        if (!dismissed) {
                            setIsWelcomeGiftOpen(true);
                        }
                    } catch (e) {
                        setIsWelcomeGiftOpen(true);
                    }
                }
            }
        } else {
            setIsOnboardingOpen(false);
        }
    }, [user, userData?.hasChosenPseudo, userData?.isPremium, userData?.hasReceivedWelcomeGift]);

    // 1. Migration automatique pour les comptes ayant déjà le statut Premium sans date d'échéance :
    // On leur accorde 30 jours à partir d'aujourd'hui, sauf s'ils sont explicitement marqués comme permanents / streamers
    useEffect(() => {
        if (user && userData && userData.isPremium === true) {
            const isExplicitlyPermanent = Boolean(
                userData.subscriptionStatus === 'active' ||
                userData.premiumType === 'permanent' ||
                userData.premiumType === 'streamer' ||
                userData.premiumType === 'lifetime' ||
                userData.isLifetime === true ||
                userData.premiumExpiresAt === 'permanent'
            );
            if (!isExplicitlyPermanent && !userData.premiumExpiresAt && !userData.premiumType) {
                const now = new Date();
                const expiresDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
                updateDoc(doc(db, 'users', user.uid), {
                    premiumType: 'trial',
                    premiumExpiresAt: expiresDate.toISOString(),
                    hasReceivedWelcomeGift: true
                }).catch(console.error);
            }
        }
    }, [user, userData?.isPremium, userData?.premiumExpiresAt, userData?.premiumType]);

    // 2. Arrêt automatique du statut Premium dès que la date d'échéance de l'essai est dépassée
    useEffect(() => {
        if (user && userData?.isPremium && isTrialExpired) {
            updateDoc(doc(db, 'users', user.uid), {
                isPremium: false,
                premiumType: 'expired'
            }).catch(console.error);
        }
    }, [user, userData?.isPremium, isTrialExpired]);

    // Effect : Joue le son de victoire quand la modale s'ouvre
        useEffect(() => {
            // ⚠️ Remplace ceci par ta vraie condition de victoire (ex: gameFeedback?.isWin === true)
            const isVictory = true; 

            // Si la modale s'ouvre, qu'il y a un feedback, ET que c'est une victoire
            if (showVideoModal && gameFeedback && isVictory) {
                
                // On va chercher le fichier dans le dossier public
                const victorySound = new Audio('/sounds/victory.mp3');
                
                // Optionnel : Tu peux régler le volume entre 0.0 et 1.0 si le son est trop fort
                victorySound.volume = 0.6; 
                
                // On lance le son
                victorySound.play().catch(error => {
                    console.log("Lecture audio bloquée par le navigateur :", error);
                });
            }
        }, [showVideoModal, gameFeedback]); // Cet effet se déclenche chaque fois que l'un de ces deux états change

    // ==========================================
    // DÉTECTION DU MODE MOBILE (RESPONSIVE)
    // ==========================================
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768); // Assure-toi que cette valeur correspond à ton ancien point de rupture
        };

        // Ajoute l'écouteur au montage
        window.addEventListener('resize', handleResize);
        
        // Force une vérification initiale au cas où
        handleResize();

        // Nettoie l'écouteur au démontage du composant
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

   // ==========================================
    // LA CARTE PROTÉGÉE
    // ==========================================
    const memoizedGoogleMap = useMemo(() => (
            <GoogleMap 
                mapContainerStyle={{ width: '100%', height: '100%', flex: 1, order: 1 }} 
                mapContainerClassName={activeGameMode === 'geodetective' ? 'mode-geodetective' : ''} 
                center={mapCenter} 
                zoom={mapZoom} 
                onUnmount={onMapUnmount} 
                onIdle={handleMapIdle}
                onLoad={(mapInstance) => {
                    setMap(mapInstance);
                    const minZ = window.innerWidth <= 768 ? 2 : 3;
                    mapInstance.setCenter(initialCenter);
                    mapInstance.setZoom(minZ);
                }} 
                onClick={handleMapClick} 
                options={{ 
                    mapTypeId: 'satellite', 
                    gestureHandling: 'greedy', 
                    styles: mapStyles,
                    scaleControl: true, 
                    keyboardShortcuts: true, 
                    minZoom: isMobile ? 2 : 3, 
                    disableDefaultUI: true, 
                    tilt: 0, 
                    zoomControl: false, 
                    // REMPLACE PAR CETTE LIGNE :
zoomControlOptions: { position: (window.google && window.google.maps && window.google.maps.ControlPosition) ? window.google.maps.ControlPosition[isMobile ? 'RIGHT_TOP' : 'RIGHT_BOTTOM'] : undefined }
                }}
            >
                {/* Les Marqueurs en mode Solo, Daily Zoom et Duel avec historique des tentatives et kilomètres */}
                {guessHistory.map((guess, index) => (
                    <OverlayView 
                        key={`guess-${index}-${guess.lat}-${guess.lng}`} 
                        position={{ lat: guess.lat, lng: guess.lng }} 
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                    >
                        <div style={{ ...guessLogoMarkerStyle, zIndex: index + 1 }}>
                            <img 
                                src="/logoprocercle.svg" 
                                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = logo; }}
                                alt={`Tentative ${index + 1}`} 
                                style={guessLogoImageStyle} 
                            />
                            {guess.distance !== null && guess.distance !== undefined && (
                                <div style={guessDistanceBadgeStyle}>
                                    {formatDist(guess.distance, 0, true)}
                                </div>
                            )}
                        </div>
                    </OverlayView>
                ))}
                
                {/* PHASE 2 & 3 : La Zone de Recherche (Couleur Cyan) */}
                {gameFeedback?.hasRadar && 
                ((gameFeedback.guessCount >= 6 && gameFeedback.guessCount <= 10) || (ENABLE_PHASE3_CIRCLES && gameFeedback.guessCount >= 11)) && 
                lastGuessLocation && (
                    <Circle 
                        center={lastGuessLocation} 
                        radius={gameFeedback.radarRadius * 1000} 
                        options={{
                            strokeColor: "#4CE0D2", // Cyan maison
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#4CE0D2", // Remplissage Cyan
                            fillOpacity: 0.15,
                            clickable: false 
                        }}
                    />
                )}

                {/* --- NOUVEAU : LA COLLECTION (Pings sur la carte) --- */}
                {/* On vérifie que la liste existe bien avant de boucler dessus */}
                {!gameId && !duelRoomId && !isRoulettePlaying && mesZoomsTrouves && mesZoomsTrouves.map((zoom) => (
                    <MapPing 
                        key={zoom.id}
                        lat={zoom.latitude} 
                        lng={zoom.longitude}
                        onClick={() => {
                            // C'EST ICI ! Ça ouvre la fenêtre détaillée :
                            setSelectedDisplayZoom(zoom);
                        }}
                        showWave={mapZoom >= 7}
                    />
                ))}

            </GoogleMap>
    ), [mapCenter, mapZoom, activeGameMode, lastGuessLocation, guessHistory, duelRoomId, isMobile, handleMapIdle, handleMapClick, onMapUnmount, gameFeedback, mesZoomsTrouves]); 
        // ^ MAGIE : J'ai ajouté 'mesZoomsTrouves' ici. Comme ça, dès que le joueur gagne, un nouveau ping apparaît sans recharger la page.
        // J'ai ajouté gameFeedback dans les dépendances pour que le cercle se mette à jour !

     


        // LE "RETURN" D'ATTENTE TOUJOURS EN TOUT DERNIER AVANT LE VRAI RETURN !
        if (!isLoaded) return <div>Chargement...</div>;

    // Vérification si au moins une modale ou vue secondaire est ouverte
    const isAnyModalOpen = Boolean(
        isOnboardingOpen ||
        isWelcomeGiftOpen ||
        showVideoModal ||
        selectedDisplayZoom ||
        showScoreAnimation ||
        showDuelIntro ||
        Boolean(gameFeedback?.isDuelFinished) ||
        isPokedexOpen ||
        isLeaderboardOpen ||
        isInfoModalOpen ||
        isBadgeModalOpen ||
        showPremiumModal ||
        isCompareModalOpen ||
        isCancelModalOpen ||
        isInstallModalOpen ||
        showAbandonConfirm ||
        dailyLimitReached ||
        isFullscreenImage ||
        premiumAlert ||
        currentView !== 'map'
    );

// Point d'entrée du rendu JSX de l'interface utilisateur globale
return (
    <>
        {/* Conteneur principal englobant la carte et les calques de l'interface HUD */}
        <div style={containerStyle}>
            

           {/* --- BANDEAU SUPÉRIEUR (HUD GAMING UNIFIÉ) --- */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, width: '100%', height: isMobile ? '42px' : '57px', zIndex: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(20, 27, 38, 0.7)', borderBottom: '1px solid rgb(76, 224, 209)', backdropFilter: 'blur(10px)', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', padding: isMobile ? '0 6px' : '0 4px 0 10px', boxSizing: 'border-box', pointerEvents: 'auto' }}>
                
               {/* BLOC JEUX (Ordinateur) */}
                {!isMobile && (
                    <div style={{ flex: 1, position: 'relative', height: '100%' }}>
                        <div style={{ 
                            position: 'absolute', 
                            top: '0px', 
                            left: '0px', 
                            width: (typeof window !== 'undefined' && window.innerWidth <= 1050) ? '350px' : '420px',
                            height: '100%', 
                            background: 'transparent', 
                            borderRadius: '4px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            overflow: 'hidden' 
                        }}>
                            <img 
                                src="/logotest.svg" 
                                alt="Logo Geo Detective" 
                                style={{ width: (typeof window !== 'undefined' && window.innerWidth <= 1050) ? '130px' : '130px', height: 'auto', objectFit: 'contain', transition: 'width 0.3s' }} 
                            />
                        </div>
                    </div>
                )}

                {/* BLOC COLLECTION / PROGRESSION */}
                {user && (
                    <div 
                        onClick={() => setIsPokedexOpen(true)}
                        style={{
                            position: isMobile ? 'relative' : 'absolute',
                            left: isMobile ? '0' : '50%',
                            transform: isMobile ? 'none' : 'translateX(-50%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: isMobile ? '6px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '10px' : '16px'),
                            background: 'rgba(20, 27, 38, 0.8)',
                            border: isMobile ? '1px solid #4CE0D2' : '2px solid #4CE0D2',
                            borderRadius: '30px',
                            padding: isMobile ? '0 8px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '6px 10px' : '8px 32px'),
                            height: isMobile ? '32px' : 'auto', 
                            flex: isMobile ? 4 : 'none', // Prend ~40% de l'espace sur mobile
                            maxWidth: isMobile ? 'none' : 'none', 
                            marginRight: isMobile ? '8px' : '0',
                            boxShadow: '0 0 10px rgba(76, 224, 209, 0.2)',
                            cursor: 'pointer',
                            zIndex: 5,
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.boxShadow = '0 0 20px rgba(76, 224, 209, 0.7)'}
                        onMouseLeave={(e) => e.currentTarget.style.boxShadow = '0 0 10px rgba(76, 224, 209, 0.2)'}
                    >
                        
                        <span style={{ 
                            color: '#4CE0D2', 
                            fontWeight: 'bold', 
                            fontSize: isMobile ? '16px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '15px' : '18px'), 
                            letterSpacing: isMobile ? '0' : '1px',
                            whiteSpace: 'nowrap',
                            position: 'relative',
                            top: '1px' 
                        }}>
                            {isMobile ? `${mesZoomsTrouves.length} / ${TOTAL_ZOOMS}` : `${mesZoomsTrouves.length} ${t('zooms_found')} ${TOTAL_ZOOMS}`}
                        </span>
                    </div>
                )}

                {/* BLOC UTILISATEUR & CONNEXION */}
                <div style={{ flex: isMobile ? 6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', height: '100%' }}> {/* Prend ~60% de l'espace sur mobile */}
                    {!user ? ( 
                        <button style={{ height: isMobile ? '34px' : '46px', padding: '0 20px', backgroundColor: 'rgb(255, 255, 255)', color: 'black', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: isMobile ? '14px' : '16px', fontWeight: 'bold', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }} onClick={signInWithGoogle}>
                            <svg width={isMobile ? "18" : "22"} height={isMobile ? "18" : "22"} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.7 17.74 9.5 24 9.5z"/><path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/><path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/><path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/><path fill="none" d="M0 0h48v48H0z"/></svg>
                            {t('Connect')}
                        </button> 
                    ) : (
                        <div style={{ height: '100%', padding: '0', display: 'flex', alignItems: 'center', gap: isMobile ? '4px' : '4px', boxSizing: 'border-box', width: isMobile ? '100%' : 'auto' }}>
    
                            {/* BOUTON INFO SEUL */}
                            <button onClick={() => setIsInfoModalOpen(true)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', flexShrink: 0, alignItems: 'center', justifyContent: 'center' }}>
                                <svg width={isMobile ? '30' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '38' : '44')} height={isMobile ? '30' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '38' : '44')} viewBox="0 0 24 24" fill="none" stroke="#4CE0D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <line x1="12" y1="16" x2="12" y2="12"></line>
                                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                </svg>
                            </button>
                            
                            {/* BOUTON CLASSEMENT */}
                            <div style={{ display: 'flex', alignItems: 'center', flexShrink: 1, overflow: 'hidden', flex: isMobile ? 1.2 : 'none' }}>
                                <button 
                                    onClick={() => { fetchLeaderboard(); setIsLeaderboardOpen(true); }}
                                    title={t('view_leaderboard', 'Voir le classement')} 
                                    style={{ 
                                        height: isMobile ? '32px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '36px' : '40px'),
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        background: 'linear-gradient(135deg, #1e3c72 0%, rgb(123, 30, 154) 100%)', 
                                        padding: isMobile ? '0 8px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '0 10px' : '0 14px'), 
                                        borderRadius: isMobile ? '6px' : '8px', 
                                        color: '#ffffff', 
                                        fontWeight: 'bold', 
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '12px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '13.5px' : '15.5px'), 
                                        border: '1px solid rgba(255, 255, 255, 0.4)', 
                                        cursor: 'pointer', 
                                        flexShrink: 0, 
                                        width: isMobile ? '100%' : 'auto', 
                                        boxSizing: 'border-box',
                                        transition: 'all 0.2s ease',
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                    }}
                                    onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'} 
                                    onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
                                >
                                    <span style={{ whiteSpace: 'nowrap', fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.3px' }}>
                                        {t('leaderboard', 'Classement')}
                                    </span>
                                </button>
                            </div>

                            {/* BOUTON MON COMPTE */}
                            <button 
                                onClick={() => { setCurrentView('account'); setIsInfoModalOpen(false); setIsBadgeModalOpen(false); }} 
                                style={{ 
                                    height: isMobile ? '32px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '36px' : '40px'),
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    padding: isMobile ? '0 6px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '0 8px' : '0 12px'), 
                                    cursor: 'pointer', 
                                    background: 'linear-gradient(135deg, #4CE0D2 0%, #4CE0D2 100%)', 
                                    color: '#141B26', 
                                    border: 'none', 
                                    borderRadius: isMobile ? '6px' : '8px', 
                                    fontSize: isMobile ? '11px' : ((typeof window !== 'undefined' && window.innerWidth <= 1050) ? '13px' : '15px'), 
                                    fontWeight: 'bold', 
                                    flexShrink: 0, 
                                    flex: isMobile ? 1 : 'none', // S'allonge aussi sur mobile
                                    width: isMobile ? '100%' : 'auto',
                                    boxSizing: 'border-box',
                                    transition: 'all 0.2s' 
                                }} 
                                onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'} 
                                onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}
                            >
                                {isMobile ? <div style={{display:'flex', flexDirection:'column', lineHeight:'1.1', textAlign:'center'}}><span>{t('MonM')}</span><span>{t('CompteM')}</span></div> : t('Compte')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

           {/* --- CONTENEUR DE JEUX (Geodrop) --- */}
           <div style={isMobile ? { position: 'relative', order: 2, width: '100%', zIndex: 900, display: hideGameOnMobile ? 'none' : 'flex', flexDirection: 'column', boxShadow: '0 -4px 20px rgba(0,0,0,0.2)', backgroundColor: 'rgb(20, 27, 38)' } : { position: 'absolute', top: '57px', left: '0px', width: '392px', backgroundColor: 'rgba(20, 27, 38, 0.9)', borderRadius: '0 0 12px 0', boxShadow: '4px 4px 15px rgba(0,0,0,0.15)', padding: '15px', zIndex: 15, display: 'flex', flexDirection: 'column', gap: '15px' }}>
                
                <div style={{ backgroundColor: 'rgba(56, 96, 160, 0)', padding: isMobile ? '8px 10px' : '0px', maxHeight: isMobile ? '40vh' : 'none', overflow: isMobile ? 'auto' : 'visible', display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '15px' }}>
                    
                    {/* MODALE IMAGE PLEIN ÉCRAN */}
                    {isFullscreenImage && gameImage && (
                        <div onClick={() => setIsFullscreenImage(false)} style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.92)', zIndex: 10000, display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'zoom-out', animation: 'fadeIn 0.2s ease-out', touchAction: 'none' }}>
                            <img src={gameImage} alt="Cible plein écran" onClick={e => { if (parseFloat(e.currentTarget.dataset.scale || 1) > 1) e.stopPropagation(); }} onTouchStart={e => { const d = e.currentTarget.dataset, t = e.touches; if (t.length === 2) d.dist = Math.hypot(t[0].pageX - t[1].pageX, t[0].pageY - t[1].pageY); else if (t.length === 1 && parseFloat(d.scale || 1) > 1) { d.startX = t[0].pageX - parseFloat(d.x || 0); d.startY = t[0].pageY - parseFloat(d.y || 0); } }} onTouchMove={e => { const d = e.currentTarget.dataset, t = e.touches; let s = parseFloat(d.scale || 1), x = parseFloat(d.x || 0), y = parseFloat(d.y || 0); if (t.length === 2) { let dist = Math.hypot(t[0].pageX - t[1].pageX, t[0].pageY - t[1].pageY); s = Math.max(1, Math.min(s * (dist / parseFloat(d.dist)), 5)); d.scale = s; d.dist = dist; } else if (t.length === 1 && s > 1) { x = t[0].pageX - parseFloat(d.startX); y = t[0].pageY - parseFloat(d.startY); d.x = x; d.y = y; } e.currentTarget.style.transform = `translate(${x}px, ${y}px) scale(${s})`; }} onTouchEnd={e => { const d = e.currentTarget.dataset; if (parseFloat(d.scale || 1) <= 1.05) { d.scale = 1; d.x = 0; d.y = 0; e.currentTarget.style.transform = 'translate(0px, 0px) scale(1)'; } }} style={{ maxWidth: '95%', maxHeight: '95%', objectFit: 'contain', borderRadius: '8px', boxShadow: '0 0 30px rgba(0,0,0,0.8)', transition: 'transform 0.05s linear', willChange: 'transform' }} />
                            <div style={{ position: 'absolute', top: '20px', right: '20px', color: 'white', fontSize: '18px', background: 'rgba(255,255,255,0.2)', width: '30px', height: '30px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>✖</div>
                        </div>
                    )}

                   
                    {/* MODALE VIDÉO - Remplacée par DisplayZoomModal */}
                    {showVideoModal && gameFeedback && (
                        <>
                            {/* 1. INJECTION DES ANIMATIONS CSS */}
                            <style>
                                {`
                                @keyframes victorySplash {
                                    0% { transform: translate(-50%, -50%) scale(3) translateZ(0); opacity: 0; }
                                    20% { transform: translate(-50%, -50%) scale(1.3) translateZ(0); opacity: 1; }
                                    75% { transform: translate(-50%, -50%) scale(1.1) translateZ(0); opacity: 1; }
                                    100% { transform: translate(-50%, -50%) scale(0.7) translateZ(0); opacity: 0; }
                                }
                                @keyframes victorySplashMobile {
                                    0% { transform: translate(-50%, -50%) scale(2.5) translateZ(0); opacity: 0; }
                                    20% { transform: translate(-50%, -50%) scale(1.2) translateZ(0); opacity: 1; }
                                    75% { transform: translate(-50%, -50%) scale(1) translateZ(0); opacity: 1; }
                                    100% { transform: translate(-50%, -50%) scale(0.7) translateZ(0); opacity: 0; }
                                }
                                @keyframes victoryGlowCyan {
                                    0% { filter: drop-shadow(0 0 10px rgba(76, 224, 209, 0.4)); }
                                    50% { filter: drop-shadow(0 0 25px rgba(76, 224, 209, 0.9)); }
                                    100% { filter: drop-shadow(0 0 10px rgba(76, 224, 209, 0.4)); }
                                }
                                `}
                            </style>

                            {/* 2. ANIMATION DE VICTOIRE (Flotte au-dessus de tout) */}
                            <div style={{
                                position: 'fixed', 
                                top: '50%',
                                left: '50%',
                                zIndex: 99999, 
                                pointerEvents: 'none', 
                                willChange: 'transform, opacity',
                                backfaceVisibility: 'hidden',
                                WebkitFontSmoothing: 'antialiased',
                                animation: `${isMobile ? 'victorySplashMobile' : 'victorySplash'} 2.5s cubic-bezier(0.1, 0.7, 0.1, 1) forwards`
                            }}>
                                <span style={{
                                    display: 'block',
                                    color: '#ffffff',
                                    fontSize: isMobile ? '25px' : '70px',
                                    fontWeight: '900',
                                    letterSpacing: isMobile ? '5px' : '12px',
                                    textTransform: 'uppercase',
                                    textAlign: 'center',
                                    lineHeight: '1',
                                    animation: 'victoryGlowCyan 2.5s infinite alternate',
                                    willChange: 'filter',
                                    fontFamily: 'Inter, system-ui, sans-serif'
                                }}>
                                    {t('victory_title', 'VICTOIRE !')}
                                </span>
                            </div>

                            {/* 3. LA NOUVELLE MODALE */}
                            <DisplayZoomModal 
                                zoom={{
                                    ...gameFeedback,
                                    // On force l'ID en allant le chercher là où il se cache dans gameFeedback
                                    id: gameFeedback.id || gameFeedback.challengeId || gameFeedback.index || gameFeedback.zoomId,
                                    info: gameFeedback.challengeInfoUrl || gameFeedback.info,
                                    imageUrl: (typeof gameImage !== 'undefined' ? gameImage : null) || gameFeedback.image
                                }} 
                                onClose={() => {
                                    setShowVideoModal(false);
                                    if (isPostGameZoomRef.current) {
                                        isPostGameZoomRef.current = false;
                                        setShowScoreAnimation(true);
                                    }
                                }}
                                showContinueButton={isPostGameZoomRef.current}
                                isMobile={isMobile}
                                t={t}
                            />
                        </>
                    )}

                    {/* JEU 1 : GÉO-DÉTECTIVE */}
                    {activeGameMode === 'geodetective' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '2px' : '15px' }}>
                            {!gameId && !gameFeedback && !isRoulettePlaying ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '18px', padding: '5px' }}>
                                    
                                    {/* EN-TÊTE : RÈGLES RAPIDES */}
                                    <div style={{ textAlign: 'center' }}>
                                        {!isMobile && (
                                            <h3 style={{ margin: '0 0 5px 0', color: '#EAEDF2', fontSize: '18px' }}>
                                                {t('Pret')}
                                            </h3>
                                        )}
                                        <p style={{ fontSize: '17px', color: '#4CE0D2', margin: '0 0 0px 0', fontWeight: 'bold' }}>
                                            {t('game_rules_summary', '5 minutes. 15 essais. 6000 points en jeu.')}
                                        </p>
                                    </div>

                                    {/* BOUTON DAILY ZOOM */}
                                    <button 
                                        onClick={() => handleStartGame('daily')} 
                                        disabled={hasPlayedDailyZoom} 
                                        style={{ 
                                            width: '100%', 
                                            padding: isMobile ? '10px' : '16px', 
                                            background: hasPlayedDailyZoom ? '#0B0E14' : 'linear-gradient(135deg, #4CE0D2 0%, #5C6675 100%)', 
                                            color: hasPlayedDailyZoom ? '#5C6675' : '#EAEDF2', 
                                            border: hasPlayedDailyZoom ? '1px solid #5C6675' : 'none', 
                                            borderRadius: '8px', 
                                            fontSize: isMobile ? '16px' : '18px', 
                                            fontWeight: 'bold', 
                                            cursor: hasPlayedDailyZoom ? 'not-allowed' : 'pointer', 
                                            boxShadow: hasPlayedDailyZoom ? 'none' : '0 4px 15px rgba(76, 224, 209, 0.3)', 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            alignItems: 'center', 
                                            justifyContent: 'center', 
                                            gap: '4px', 
                                            transition: 'transform 0.1s ease' 
                                        }} 
                                        onMouseDown={e => !hasPlayedDailyZoom && (e.currentTarget.style.transform = 'scale(0.98)')} 
                                        onMouseUp={e => !hasPlayedDailyZoom && (e.currentTarget.style.transform = 'scale(1)')}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <IconDaily size={isMobile ? '18px' : '22px'} color={hasPlayedDailyZoom ? "#5C6675" : "#EAEDF2"} />
                                            <span>{t('daily_zoom', 'DAILY ZOOM')}</span>
                                        </div>
                                        
                                        {/* AFFICHAGE DU COMPTE À REBOURS SI DÉJÀ JOUÉ */}
                                        {hasPlayedDailyZoom && (
                                            <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#5C6675' }}>
                                                {t('next_daily_in', 'Prochain défi dans')} <span style={{fontWeight: 'bold', color: '#4CE0D2'}}>{countdownText}</span>
                                            </span>
                                        )}
                                    </button>

                                    {/* LES 5 SLOTS DE JEUX QUOTIDIENS */}
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '6px' : '8px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px' }}>
                                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#EAEDF2', textTransform: 'uppercase' }}>{t('free_missions', 'Missions Libres')}</span>
                                            <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#4CE0D2' }}>
                                                {isPremiumUser 
                                                    ? `${5 - Math.min(gamesPlayedCount, 5)} / 5` 
                                                    : `${2 - Math.min(gamesPlayedCount, 2)} / 2`}
                                            </span>
                                        </div>
                                        
                                        {/* NOUVEAU CONTENEUR SCROLLABLE (Uniquement sur mobile) */}
                                        <div style={{ 
                                            display: 'flex', 
                                            flexDirection: 'column', 
                                            gap: isMobile ? '6px' : '8px',
                                            // La hauteur max est de 3 slots (36px) + 2 espaces (6px) = 120px sur mobile
                                            maxHeight: isMobile ? '120px' : 'none', 
                                            overflowY: isMobile ? 'auto' : 'visible',
                                            paddingRight: isMobile ? '2px' : '0' // Petite marge pour la barre de scroll
                                        }}>
                                            
                                            {/* GESTION DE L'ÉTAT VIDE PREMIUM SUR MOBILE */}
                                            {isMobile && isPremiumUser && gamesPlayedCount >= 5 ? (
                                                <div style={{ display: 'flex', height: '36px', alignItems: 'center', justifyContent: 'center', background: '#0B0E14', borderRadius: '6px', border: '1px solid #5C6675' }}>
                                                    <span style={{ fontSize: '13px', color: '#5C6675', fontSize: '12px' }}>
                                                        {t('new_zooms_in', 'nouveaux zooms disponibles dans')} <span style={{ fontWeight: 'bold', color: '#4CE0D2' }}>{countdownText}</span>
                                                    </span>
                                                </div>
                                            ) : (
                                                /* PASSAGE À 5 SLOTS ICI : [1, 2, 3, 4, 5] */
                                                [1, 2, 3, 4, 5].map((slot) => {
                                                    // Condition de verrouillage Premium : à partir du 3ème slot
                                                    const isPremiumLocked = slot > 2 && !isPremiumUser;
                                                    // Condition pour savoir si le slot a déjà été joué
                                                    const isPlayed = slot <= gamesPlayedCount;

                                                    // LA MAGIE DU MOBILE : Si c'est joué, le slot disparaît de la liste
                                                    if (isMobile && isPlayed) {
                                                        return null;
                                                    }

                                                    return (
                                                        <div key={slot} style={{ display: 'flex', flexShrink: 0, height: isMobile ? '36px' : '46px', borderRadius: '6px', overflow: 'hidden', border: '1px solid #5C6675', opacity: isPlayed ? 0.6 : 1 }}>
                                                            {isPremiumLocked ? (
                                                            <button onClick={() => setCurrentView('account')} style={{ flex: 1, background: '#0B0E14', border: 'none', color: '#5C6675', fontSize: isMobile ? '13px' : '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                <span style={{ fontSize: isMobile ? '14px' : '16px' }}>🔒</span> {t('unlock_premium', 'Débloquer Premium')}
                                                            </button>
                                                        ) : isPlayed ? (
                                                                /* BOUTONS JOUÉS : Désactivés (disabled) et grisés (grayscale) - Affichés uniquement sur PC désormais */
                                                                <>
                                                                    <button disabled style={{ flex: 1, background: '#1A1E26', border: 'none', borderRight: '1px solid #0B0E14', color: '#5C6675', fontSize: isMobile ? '13px' : '15px', fontWeight: 'bold', cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                                        <span style={{ fontSize: isMobile ? '15px' : '18px', filter: 'grayscale(100%)' }}><IconSolo size={isMobile ? '18px' : '22px'} color="#5C6675" /></span> {t('solo_mode', 'Solo')}
                                                                    </button>
                                                                    <button disabled style={{ flex: 1, background: '#1A1E26', border: 'none', color: '#5C6675', fontSize: isMobile ? '13px' : '15px', fontWeight: 'bold', cursor: 'not-allowed', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                                                        <span style={{ fontSize: isMobile ? '15px' : '18px', filter: 'grayscale(100%)' }}><IconDuel size={isMobile ? '18px' : '22px'} color="#5C6675" /></span> {t('duel_mode', 'Duel')}
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                /* BOUTONS NORMAUX : Solo actif, Duel réservé à Premium */
                                                                <>
                                                                    <button onClick={() => handleStartGame('solo')} style={{ flex: 1, background: 'linear-gradient(135deg, #4CE0D2 0%, #5C6675 100%)', border: 'none', borderRight: '1px solid #0B0E14', color: '#EAEDF2', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'filter 0.2s' }} onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'} onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}>
                                                                        <span style={{ display: 'flex', alignItems: 'center' }}><IconSolo size={isMobile ? '18px' : '22px'} color="#EAEDF2" /></span> {t('solo_mode', 'Solo')}
                                                                    </button>
                                                                    
                                                                    {isPremiumUser ? (
                                                                        <button onClick={() => handleStartGame('duel')} style={{ flex: 1, background: 'linear-gradient(135deg, #4CE0D2 0%, #5C6675 100%)', border: 'none', color: '#EAEDF2', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'filter 0.2s' }} onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'} onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}>
                                                                            <span style={{ display: 'flex', alignItems: 'center' }}><IconDuel size={isMobile ? '18px' : '22px'} color="#EAEDF2" /></span> {t('duel_mode', 'Duel')}
                                                                        </button>
                                                                    ) : (
                                                                        <button onClick={() => setCurrentView('account')} title={t('unlock_premium', 'Débloquer Premium')} style={{ flex: 1, background: '#1A1E26', border: 'none', color: '#8A95A5', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255, 215, 0, 0.12)'; e.currentTarget.style.color = '#FFD700'; }} onMouseLeave={e => { e.currentTarget.style.background = '#1A1E26'; e.currentTarget.style.color = '#8A95A5'; }}>
                                                                            <span style={{ fontSize: isMobile ? '12px' : '14px' }}>🔒</span>
                                                                            <span style={{ display: 'flex', alignItems: 'center' }}><IconDuel size={isMobile ? '16px' : '20px'} color="#8A95A5" /></span>
                                                                            <span>{t('duel_mode', 'Duel')}</span>
                                                                        </button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    );
                                                })
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                               <>
                                    <div style={{ marginBottom: '0px' }}>
                                        {isMobile ? (
                                            <>
                                                 {/* --- HUD MOBILE --- */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0px' }}>
                                                    <div style={{ flex: 1, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px', fontSize: '15px', fontWeight: 'bold', color: '#4CE0D2', borderBottom: '1px solid #5C6675', paddingBottom: '3px', marginRight: duelStatus === 'waiting' ? '0px' : '10px' }}>
                                                        {duelRoomId ? t('duel_in_progress') : t('investigation_in_progress')}
                                                    </div>
                                                    {duelStatus !== 'waiting' && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <button 
                                                                onClick={toggleMusicMuted} 
                                                                title={isMusicMuted ? t('unmute_music', 'Activer la musique') : t('mute_music', 'Couper la musique')} 
                                                                style={{ background: 'transparent', border: 'none', color: isMusicMuted ? '#5C6675' : '#4CE0D2', fontSize: '15px', cursor: 'pointer', padding: 0 }}
                                                            >
                                                                {isMusicMuted ? '🔇' : '🎵'}
                                                            </button>
                                                            <button onClick={() => { if (gameFeedback?.isWon || gameFeedback?.isLost) { setGameFeedback(null); setGameId(null); setDuelRoomId(null); setLastGuessLocation(null); setGuessHistory([]); resetMapToInitialView(); } else { setShowAbandonConfirm(true); } }} style={{ background: 'transparent', border: 'none', color: '#5C6675', fontSize: '18px', cursor: 'pointer', fontWeight: 'bold', padding: 0, position: 'relative', top: '-2px' }}>✖</button>
                                                        </div>
                                                    )}
                                                </div>

                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', marginTop: '4px' }}>
                                                    <span style={{ fontSize: '13px', color: timeLeft <= 60 ? '#dc3545' : '#EAEDF2', fontFamily: '"Courier New", monospace', fontWeight: 'bold', width: '45px' }}>
                                                        {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                                                    </span>
                                                    <div style={{ flex: 1, height: '4px', background: '#0B0E14', borderRadius: '2px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${(timeLeft / (duelRoomId ? 180 : 300)) * 100}%`, height: '100%', backgroundColor: timeLeft <= 60 ? '#dc3545' : '#4CE0D2', transition: 'width 1s linear' }}></div>
                                                    </div>
                                                </div>

                                                <div style={{ display: 'flex', flexDirection: 'row', gap: '6px', marginBottom: '2px', marginTop: '-1px', alignItems: 'stretch' }}>
                                                    <div style={{ flex: duelRoomId ? '0.64' : '0.72', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        {isRoulettePlaying ? (
                                                            <div style={{ position: 'relative', width: '100%', aspectRatio: '4/3', maxHeight: '210px', borderRadius: '8px', border: '2px solid #5C6675', overflow: 'hidden' }}>
                                                                {rouletteImages.map((src) => <img key={src} src={src} alt="Roulette" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'contain', opacity: currentRouletteImage === src ? 1 : 0, filter: 'blur(2px) grayscale(50%)' }} />)}
                                                                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'rgba(11, 14, 20, 0.85)', color: '#4CE0D2', border: '1px solid #4CE0D2', padding: '5px 10px', borderRadius: '4px', fontSize: '11px', fontWeight: 'bold', whiteSpace: 'nowrap', animation: 'pulse 1.5s infinite', zIndex: 10 }}>
                                                                    {duelStatus === 'waiting' ? `${t('searching_rival')} (${duelSearchTimeLeft}s)` : t('satellite_connection')}
                                                                </div>
                                                                {duelStatus === 'waiting' && (
                                                                    <button 
                                                                        onClick={() => handleCancelDuelSearch(false)}
                                                                        style={{
                                                                            position: 'absolute',
                                                                            bottom: '8px',
                                                                            left: '50%',
                                                                            transform: 'translateX(-50%)',
                                                                            zIndex: 12,
                                                                            background: 'linear-gradient(135deg, #E03030 0%, #A81B1B 100%)',
                                                                            border: '1px solid #FF6B6B',
                                                                            borderRadius: '8px',
                                                                            color: '#ffffff',
                                                                            padding: '6px 14px',
                                                                            fontSize: '11px',
                                                                            fontWeight: 'bold',
                                                                            cursor: 'pointer',
                                                                            whiteSpace: 'nowrap',
                                                                            boxShadow: '0 3px 12px rgba(224, 48, 48, 0.5)',
                                                                            fontFamily: 'Inter, system-ui, sans-serif'
                                                                        }}
                                                                    >
                                                                        ✖ {t('duel_cancel_search', 'Annuler la recherche')}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        ) : (gameImage && <img src={gameImage} alt="Défi" onClick={() => setIsFullscreenImage(true)} style={{ width: '100%', borderRadius: '8px', border: '2px solid #4CE0D2', boxShadow: '0 0 10px rgba(76, 224, 209, 0.2)', display: 'block', maxHeight: '210px', objectFit: 'contain', cursor: 'zoom-in' }} />)}
                                                    </div>
                                                    
                                                    {duelRoomId ? (
                                                        /* --- FACE-À-FACE DUEL MOBILE (Empilé à droite de l'image) --- */
                                                        <div style={{ flex: '0.36', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '4px', minWidth: 0 }}>
                                                            {/* Carte JOUEUR (VOUS) */}
                                                            <div style={{
                                                                background: duelRaceStatus === 'leading' ? 'rgba(61, 220, 151, 0.1)' : '#0B0E14',
                                                                border: duelRaceStatus === 'leading' ? '1px solid #3DDC97' : '1px solid rgba(76, 224, 209, 0.45)',
                                                                borderRadius: '7px',
                                                                padding: '4px 6px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '3px'
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '10.5px', fontWeight: '800', color: '#4CE0D2', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {t('duel_you', 'VOUS')}
                                                                    </span>
                                                                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#A0AEC0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {myDuelAttempts}/5
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '3px' }}>
                                                                    {[1, 2, 3, 4, 5].map(i => (
                                                                        <div key={i} style={{
                                                                            flex: 1,
                                                                            height: '5px',
                                                                            borderRadius: '3px',
                                                                            background: i <= myDuelAttempts ? '#4CE0D2' : '#1A2230',
                                                                            border: i <= myDuelAttempts ? 'none' : '1px solid #3A4659',
                                                                            boxShadow: i <= myDuelAttempts ? '0 0 5px rgba(76, 224, 209, 0.7)' : 'none',
                                                                            transition: 'all 0.25s ease'
                                                                        }} />
                                                                    ))}
                                                                </div>
                                                                <div style={{ textAlign: 'center', fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', fontSize: '12px', color: duelRaceStatus === 'leading' ? '#3DDC97' : '#EAEDF2', lineHeight: '1.1' }}>
                                                                    {effectiveMyBestDist !== null ? formatDist(effectiveMyBestDist, 0, true) : '---'}
                                                                </div>
                                                            </div>

                                                            {/* Statut Central (1er / 2nd / VS) */}
                                                            <div style={{
                                                                textAlign: 'center',
                                                                fontSize: '10.5px',
                                                                fontWeight: 'bold',
                                                                fontFamily: 'Inter, system-ui, sans-serif',
                                                                padding: '2px 4px',
                                                                borderRadius: '10px',
                                                                background: duelRaceStatus === 'leading'
                                                                    ? 'rgba(61, 220, 151, 0.15)'
                                                                    : (duelRaceStatus === 'behind' ? 'rgba(224, 48, 48, 0.18)' : 'rgba(255, 255, 255, 0.06)'),
                                                                color: duelRaceStatus === 'leading'
                                                                    ? '#3DDC97'
                                                                    : (duelRaceStatus === 'behind' ? '#FF6B6B' : (duelRaceStatus === 'tied' ? '#FFD166' : '#A0AEC0')),
                                                                whiteSpace: 'nowrap',
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis'
                                                            }}>
                                                                {duelRaceStatus === 'leading'
                                                                    ? `🟢 ${t('duel_status_leading', '1er')}`
                                                                    : duelRaceStatus === 'behind'
                                                                        ? `🔴 ${t('duel_status_behind', '2nd')}`
                                                                        : duelRaceStatus === 'tied'
                                                                            ? `⚡ ${t('duel_status_tied', 'Égalité')}`
                                                                            : '⚔️ VS'}
                                                            </div>

                                                            {/* Carte RIVAL */}
                                                            <div style={{
                                                                background: duelRaceStatus === 'behind' ? 'rgba(224, 48, 48, 0.1)' : '#0B0E14',
                                                                border: duelRaceStatus === 'behind' ? '1px solid #FF5A5F' : '1px solid rgba(255, 107, 107, 0.45)',
                                                                borderRadius: '7px',
                                                                padding: '4px 6px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                gap: '3px',
                                                                minWidth: 0
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
                                                                    <span style={{ fontSize: '10.5px', fontWeight: '800', color: '#FF6B6B', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {oppDisplayName}
                                                                    </span>
                                                                    <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#A0AEC0', fontFamily: 'Inter, system-ui, sans-serif', flexShrink: 0 }}>
                                                                        {oppDuelAttempts}/5
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '3px' }}>
                                                                    {[1, 2, 3, 4, 5].map(i => (
                                                                        <div key={i} style={{
                                                                            flex: 1,
                                                                            height: '5px',
                                                                            borderRadius: '3px',
                                                                            background: i <= oppDuelAttempts ? '#FF5A5F' : '#1A2230',
                                                                            border: i <= oppDuelAttempts ? 'none' : '1px solid #3A4659',
                                                                            boxShadow: i <= oppDuelAttempts ? '0 0 5px rgba(255, 90, 95, 0.7)' : 'none',
                                                                            transition: 'all 0.25s ease'
                                                                        }} />
                                                                    ))}
                                                                </div>
                                                                <div style={{ textAlign: 'center', fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', fontSize: '12px', color: duelRaceStatus === 'behind' ? '#FF6B6B' : '#EAEDF2', lineHeight: '1.1' }}>
                                                                    {effectiveOppBestDist !== null ? formatDist(effectiveOppBestDist, 0, true) : '---'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div style={{ flex: '0.28', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', gap: '5px', padding: '0 2px' }}>
                                                            <div style={{ textAlign: 'center', marginBottom: '0px' }}>
                                                                <div style={{ fontSize: '13px', color: '#EAEDF2', marginBottom: '2px', lineHeight: '1', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('attempts_used')}</div>
                                                                <div style={{ fontSize: '11px', color: '#4CE0D2', fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' }}>{gameFeedback?.guessCount || guessCount} / 15</div>
                                                            </div>
                                                            
                                                            <div style={{ width: '20px', height: '60%', minHeight: '50px', flex: 'none', background: '#0B0E14', border: '1px solid #5C6675', borderRadius: '4px', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', marginBottom: '0px' }}>
                                                                <div style={{ width: '100%', height: `${((gameFeedback?.guessCount || guessCount) / 15) * 100}%`, backgroundColor: (gameFeedback?.guessCount || guessCount) > 12 ? '#dc3545' : '#4CE0D2', transition: 'height 0.5s ease-in-out', boxShadow: '0 0 5px rgba(76,224,209,0.5)' }}></div>
                                                            </div>

                                                            {!gameFeedback?.isWon && (
                                                                <div style={{ textAlign: 'center', lineHeight: '1.2', width: '100%', marginTop: '-2px' }}>
                                                                    <div style={{ fontSize: '13px', color: '#EAEDF2', marginBottom: '2px', lineHeight: '1', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('potential_reward')}</div>
                                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', alignItems: 'center', marginTop: '2px' }}>
                                                                        <BadgePoints text={`${getCurrentPotentialReward(gameFeedback?.guessCount || guessCount) + (timeLeft * 10)} PTS`} iconSize="12px" fontSize="11px" padding="2px 6px" iconFirst={true} />
                                                                    </div>
                                                                </div> 
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                {/* --- HUD BUREAU --- */}
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0px' }}>
                                                    <div style={{ flex: 1, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '2px', fontSize: '17px', fontWeight: 'bold', color: '#4CE0D2', borderBottom: '1px solid #5C6675', paddingBottom: '5px', marginRight: duelStatus === 'waiting' ? '0px' : '10px' }}>
                                                        {duelRoomId ? t('duel_in_progress') : t('investigation_in_progress')}
                                                    </div>
                                                    {duelStatus !== 'waiting' && (
                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                            <button 
                                                                onClick={toggleMusicMuted} 
                                                                title={isMusicMuted ? t('unmute_music', 'Activer la musique') : t('mute_music', 'Couper la musique')} 
                                                                style={{ background: 'transparent', border: 'none', color: isMusicMuted ? '#5C6675' : '#4CE0D2', fontSize: '18px', cursor: 'pointer', padding: '0 4px', transition: 'transform 0.15s ease' }}
                                                                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.15)'}
                                                                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                                                            >
                                                                {isMusicMuted ? '🔇' : '🎵'}
                                                            </button>
                                                            <button onClick={() => { if (gameFeedback?.isWon || gameFeedback?.isLost) { setGameFeedback(null); setGameId(null); setDuelRoomId(null); setLastGuessLocation(null); setGuessHistory([]); resetMapToInitialView(); } else { setShowAbandonConfirm(true); } }} style={{ background: 'transparent', border: 'none', color: '#5C6675', fontSize: '20px', cursor: 'pointer', fontWeight: 'bold', padding: '0 5px' }}>✖</button>
                                                        </div>
                                                    )}
                                                </div>
                                                
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', marginTop: '6px' }}>
                                                    <span style={{ fontSize: '15px', color: timeLeft <= 60 ? '#dc3545' : '#EAEDF2', fontFamily: '"Courier New", monospace', fontWeight: 'bold', width: '50px' }}>
                                                        {Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}
                                                    </span>
                                                    <div style={{ flex: 1, height: '6px', background: '#0B0E14', borderRadius: '3px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${(timeLeft / (duelRoomId ? 180 : 300)) * 100}%`, height: '100%', backgroundColor: timeLeft <= 60 ? '#dc3545' : '#4CE0D2', transition: 'width 1s linear' }}></div>
                                                    </div>
                                                </div>

                                                <div style={{ position: 'relative', marginBottom: '10px' }}>
                                                    {isRoulettePlaying ? (
                                                        <div style={{ position: 'relative', width: '99%', aspectRatio: '4/3', maxHeight: '300px', borderRadius: '8px', border: '2px solid #5C6675', overflow: 'hidden' }}>
                                                            {rouletteImages.map((src) => <img key={src} src={src} alt="Roulette" style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: currentRouletteImage === src ? 1 : 0, filter: 'blur(2px) grayscale(50%)' }} />)}
                                                            <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', backgroundColor: 'rgba(11, 14, 20, 0.85)', color: '#4CE0D2', border: '1px solid #4CE0D2', padding: '8px 15px', borderRadius: '4px', fontSize: '14px', fontWeight: 'bold', whiteSpace: 'nowrap', animation: 'pulse 1.5s infinite', zIndex: 10 }}>
                                                                {duelStatus === 'waiting' ? `${t('searching_rival')} (${duelSearchTimeLeft}s)` : t('satellite_connection')}
                                                            </div>
                                                            {duelStatus === 'waiting' && (
                                                                <button 
                                                                    onClick={() => handleCancelDuelSearch(false)}
                                                                    style={{
                                                                        position: 'absolute',
                                                                        bottom: '16px',
                                                                        left: '50%',
                                                                        transform: 'translateX(-50%)',
                                                                        zIndex: 12,
                                                                        background: 'linear-gradient(135deg, #E03030 0%, #A81B1B 100%)',
                                                                        border: '1px solid #FF6B6B',
                                                                        borderRadius: '10px',
                                                                        color: '#ffffff',
                                                                        padding: '10px 22px',
                                                                        fontSize: '14px',
                                                                        fontWeight: 'bold',
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap',
                                                                        boxShadow: '0 4px 16px rgba(224, 48, 48, 0.55)',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: '8px',
                                                                        transition: 'all 0.2s ease',
                                                                        fontFamily: 'Inter, system-ui, sans-serif'
                                                                    }}
                                                                    onMouseEnter={e => { e.currentTarget.style.transform = 'translateX(-50%) scale(1.04)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(224, 48, 48, 0.7)'; }}
                                                                    onMouseLeave={e => { e.currentTarget.style.transform = 'translateX(-50%) scale(1)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(224, 48, 48, 0.55)'; }}
                                                                >
                                                                    ✖ {t('duel_cancel_search', 'Annuler la recherche')}
                                                                </button>
                                                            )}
                                                        </div>
                                                    ) : (gameImage && <img src={gameImage} alt="Défi" onClick={() => setIsFullscreenImage(true)} style={{ width: '99%', borderRadius: '8px', border: '2px solid #4CE0D2', boxShadow: '0 0 15px rgba(76, 224, 209, 0.15)', display: 'block', maxHeight: '300px', objectFit: 'cover', cursor: 'zoom-in' }} />)}
                                                </div>
                                                <div style={{ marginBottom: '0px' }}>
                                                    {duelRoomId ? (
                                                        /* --- BANDEAU FACE-À-FACE (VS) SUR PC --- */
                                                        <div style={{
                                                            display: 'grid',
                                                            gridTemplateColumns: '1fr auto 1fr',
                                                            alignItems: 'stretch',
                                                            gap: '8px',
                                                            background: 'rgba(11, 14, 20, 0.85)',
                                                            border: '1px solid #2D3748',
                                                            borderRadius: '10px',
                                                            padding: '8px'
                                                        }}>
                                                            {/* Colonne Gauche : VOUS */}
                                                            <div style={{
                                                                background: duelRaceStatus === 'leading' ? 'rgba(61, 220, 151, 0.08)' : 'rgba(76, 224, 209, 0.04)',
                                                                border: duelRaceStatus === 'leading' ? '1px solid #3DDC97' : '1px solid rgba(76, 224, 209, 0.35)',
                                                                borderRadius: '8px',
                                                                padding: '7px 9px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                justifyContent: 'space-between',
                                                                gap: '6px',
                                                                minWidth: 0
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                                    <span style={{ fontSize: '12.5px', fontWeight: '800', color: '#4CE0D2', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {t('duel_you', 'VOUS')}
                                                                    </span>
                                                                    <span style={{ fontSize: '11.5px', fontWeight: 'bold', color: '#A0AEC0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {myDuelAttempts}/5
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                                    {[1, 2, 3, 4, 5].map(i => (
                                                                        <div key={i} style={{
                                                                            flex: 1,
                                                                            height: '6px',
                                                                            borderRadius: '3px',
                                                                            background: i <= myDuelAttempts ? '#4CE0D2' : '#1A2230',
                                                                            border: i <= myDuelAttempts ? 'none' : '1px solid #3A4659',
                                                                            boxShadow: i <= myDuelAttempts ? '0 0 6px rgba(76, 224, 209, 0.7)' : 'none',
                                                                            transition: 'all 0.25s ease'
                                                                        }} />
                                                                    ))}
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '1px' }}>
                                                                    <span style={{ fontSize: '10.5px', color: '#A0AEC0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {t('duel_best_shot', 'Meilleur')}
                                                                    </span>
                                                                    <span style={{ fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', fontSize: '14.5px', color: duelRaceStatus === 'leading' ? '#3DDC97' : '#EAEDF2' }}>
                                                                        {effectiveMyBestDist !== null ? formatDist(effectiveMyBestDist, 0, false) : '---'}
                                                                    </span>
                                                                </div>
                                                            </div>

                                                            {/* Colonne Centrale : VS & Indicateur */}
                                                            <div style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '5px',
                                                                padding: '0 2px'
                                                            }}>
                                                                <div style={{
                                                                    width: '28px',
                                                                    height: '28px',
                                                                    borderRadius: '50%',
                                                                    background: 'radial-gradient(circle, #E03030 0%, #1A1E26 100%)',
                                                                    border: '1.5px solid #FF6B6B',
                                                                    boxShadow: '0 0 10px rgba(224, 48, 48, 0.5)',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontFamily: 'Orbitron, sans-serif',
                                                                    fontWeight: '900',
                                                                    fontSize: '10px',
                                                                    color: '#FFFFFF'
                                                                }}>
                                                                    VS
                                                                </div>
                                                                {duelRaceStatus !== 'neutral' && (
                                                                    <div style={{
                                                                        fontSize: '11px',
                                                                        fontWeight: 'bold',
                                                                        fontFamily: 'Inter, system-ui, sans-serif',
                                                                        padding: '2px 7px',
                                                                        borderRadius: '8px',
                                                                        background: duelRaceStatus === 'leading'
                                                                            ? 'rgba(61, 220, 151, 0.16)'
                                                                            : (duelRaceStatus === 'behind' ? 'rgba(224, 48, 48, 0.18)' : 'rgba(255, 209, 102, 0.15)'),
                                                                        color: duelRaceStatus === 'leading'
                                                                            ? '#3DDC97'
                                                                            : (duelRaceStatus === 'behind' ? '#FF6B6B' : '#FFD166'),
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        {duelRaceStatus === 'leading'
                                                                            ? `🟢 ${t('duel_status_leading', '1er')}`
                                                                            : duelRaceStatus === 'behind'
                                                                                ? `🔴 ${t('duel_status_behind', '2nd')}`
                                                                                : `⚡ ${t('duel_status_tied', 'Égalité')}`}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {/* Colonne Droite : RIVAL */}
                                                            <div style={{
                                                                background: duelRaceStatus === 'behind' ? 'rgba(224, 48, 48, 0.08)' : 'rgba(255, 107, 107, 0.04)',
                                                                border: duelRaceStatus === 'behind' ? '1px solid #FF5A5F' : '1px solid rgba(255, 107, 107, 0.35)',
                                                                borderRadius: '8px',
                                                                padding: '7px 9px',
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                justifyContent: 'space-between',
                                                                gap: '6px',
                                                                minWidth: 0
                                                            }}>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '4px' }}>
                                                                    <span style={{ fontSize: '12.5px', fontWeight: '800', color: '#FF6B6B', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {oppDisplayName}
                                                                    </span>
                                                                    <span style={{ fontSize: '11.5px', fontWeight: 'bold', color: '#A0AEC0', fontFamily: 'Inter, system-ui, sans-serif', flexShrink: 0 }}>
                                                                        {oppDuelAttempts}/5
                                                                    </span>
                                                                </div>
                                                                <div style={{ display: 'flex', gap: '4px' }}>
                                                                    {[1, 2, 3, 4, 5].map(i => (
                                                                        <div key={i} style={{
                                                                            flex: 1,
                                                                            height: '6px',
                                                                            borderRadius: '3px',
                                                                            background: i <= oppDuelAttempts ? '#FF5A5F' : '#1A2230',
                                                                            border: i <= oppDuelAttempts ? 'none' : '1px solid #3A4659',
                                                                            boxShadow: i <= oppDuelAttempts ? '0 0 6px rgba(255, 90, 95, 0.7)' : 'none',
                                                                            transition: 'all 0.25s ease'
                                                                        }} />
                                                                    ))}
                                                                </div>
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: '1px' }}>
                                                                    <span style={{ fontSize: '10.5px', color: '#A0AEC0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {t('duel_best_shot', 'Meilleur')}
                                                                    </span>
                                                                    <span style={{ fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', fontSize: '14.5px', color: duelRaceStatus === 'behind' ? '#FF6B6B' : '#EAEDF2' }}>
                                                                        {effectiveOppBestDist !== null ? formatDist(effectiveOppBestDist, 0, false) : '---'}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: isMobile ? '12px' : '16px', marginBottom: '6px', color: '#EAEDF2' }}>
                                                                <span style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>{t('attempts_used')}</span>
                                                                <strong style={{ color: '#4CE0D2', fontFamily: 'Inter, system-ui, sans-serif' }}>{gameFeedback?.guessCount || guessCount} / 15</strong>
                                                            </div>
                                                            <div style={{ width: '100%', height: '8px', background: '#0B0E14', border: '1px solid #5C6675', borderRadius: '4px', overflow: 'hidden' }}>
                                                                <div style={{ width: `${((gameFeedback?.guessCount || guessCount) / 15) * 100}%`, height: '100%', backgroundColor: (gameFeedback?.guessCount || guessCount) > 12 ? '#dc3545' : '#4CE0D2', transition: 'width 0.5s ease-in-out', boxShadow: '0 0 5px rgba(76,224,209,0.5)' }}></div>
                                                            </div>
                                                            
                                                            {!gameFeedback?.isWon && (
                                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginTop: '10px', fontSize: isMobile ? '13px' : '16px', color: '#EAEDF2' }}>
                                                                    <span style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>{t('potential_reward')}</span>
                                                                    <div style={{ display: 'flex', gap: '8px' }}>
                                                                        <BadgePoints 
                                                                            text={`${getCurrentPotentialReward(gameFeedback?.guessCount || guessCount) + (timeLeft * 10)} PTS`} 
                                                                            iconSize="16px" 
                                                                            fontSize="14px" 
                                                                            padding="4px 8px" 
                                                                            iconFirst={true} 
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div style={{ borderTop: '1px solid #5C6675', paddingTop: isMobile ? '0px' : '10px', minHeight: isMobile ? '50px' : '80px', display: 'flex', flexDirection: 'column', justifyContent: 'center', marginTop: isMobile ? '-4px' : '0px' }}>
                                        {isAnalyzingGuess ? (
                                           <div style={{ textAlign: 'center', animation: 'fadeIn 0.3s' }}>
                                                
                                                <strong style={{ color: '#4CE0D2', fontFamily: 'Inter, system-ui, sans-serif', fontSize: isMobile ? '16px' : '20px' }}>{t('analysis_in_progress')}</strong>
                                                {!isMobile && <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('calculating_distance')}</p>}
                                            </div>
                                        ) : gameFeedback ? (
                                            <div style={{ textAlign: 'center', animation: 'fadeIn 0.3s', width: '100%' }}>
                                                
                                                {/* --- ÉCRAN DE FIN : MODE DUEL --- */}
                                                {gameFeedback.isDuelFinished ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                                                        
                                                        {gameFeedback.isRefund ? (
                                                            <>
                                                                {/* Titre inchangé */}
                                                                <h3 style={{ color: '#4CE0D2', margin: '0', fontSize: isMobile ? '18px' : '20px' }}>{t('draw')}</h3>
                                                                {/* Texte en Inter */}
                                                                <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#EAEDF2', margin: '0', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('draw_desc')}</p>
                                                            </>
                                                        ) : gameFeedback.isWon ? (
                                                            <>
                                                                {/* Titre inchangé */}
                                                                <h3 style={{ color: '#3DDC97', margin: '0', fontSize: isMobile ? '18px' : '20px' }}>{t('victory_caps')}</h3>
                                                                {gameFeedback.isForfeitWin && (
                                                                    <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#4CE0D2', margin: '2px 0 4px 0', fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {t('duel_forfeit_win', 'Votre adversaire a déclaré forfait !')}
                                                                    </p>
                                                                )}
                                                                <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'center', gap: isMobile ? '12px' : '4px' }}>
                                                                    {/* Textes en Inter avec .toFixed(0) */}
                                                                    
                                                                    {gameFeedback.myDistance !== null && (
                                                                       <div style={{ fontSize: '15px', color: '#EAEDF2', fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                            {t('your_best_distance')} <span style={{ color: '#3DDC97', fontFamily: 'Orbitron, sans-serif' }}>{formatDist(gameFeedback.myDistance, 0, false)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        ) : gameFeedback.isTie ? (
                                                            <>
                                                                {/* Titre inchangé */}
                                                                <h3 style={{ color: '#4CE0D2', margin: '0', fontSize: isMobile ? '18px' : '20px' }}>{t('perfect_tie')}</h3>
                                                                {/* Textes en Inter avec .toFixed(0) */}
                                                                <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#EAEDF2', margin: '0', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('tie_desc')}</p>
                                                                {gameFeedback.myDistance !== null && (
                                                                    <div style={{ fontSize: '11px', color: '#5C6675', marginTop: '2px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {t('your_best_distance')} {formatDist(gameFeedback.myDistance, 0, false)}
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <>
                                                                {/* Titre inchangé */}
                                                                <h3 style={{ color: '#E03030', margin: '0', fontSize: isMobile ? '18px' : '20px' }}>{t('defeat_caps')}</h3>
                                                                <div style={{ display: 'flex', flexDirection: isMobile ? 'row' : 'column', alignItems: 'center', gap: isMobile ? '12px' : '4px' }}>
                                                                    {/* Textes en Inter avec .toFixed(0) */}
                                                                    
                                                                    {gameFeedback.myDistance !== null && (
                                                                        <div style={{ fontSize: '15px', color: '#EAEDF2', fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                            {t('your_best_distance')} <span style={{ color: '#E03030', fontFamily: 'Orbitron, sans-serif' }}>{formatDist(gameFeedback.myDistance, 0, false)}</span>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </>
                                                        )}

                                                        {/* BOUTON VIDÉO : Texte en Inter */}
                                                        {(gameFeedback.challengeInfoUrl || gameFeedback.info) && !gameFeedback.isRefund && (
                                                            <div onClick={() => setShowVideoModal(true)} style={{ fontSize: isMobile ? '12px' : '14px', color: '#4CE0D2', textDecoration: 'underline', cursor: 'pointer', fontWeight: 'bold', margin: '4px 0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                ▶ {t('click_more_info')}
                                                            </div>
                                                        )}

                                                        {/* BOUTON TERMINER : Texte en Inter */}
                                                        <button onClick={() => { setGameFeedback(null); setGameId(null); setDuelRoomId(null); setLastGuessLocation(null); setGuessHistory([]); resetMapToInitialView(); }} style={{ width: '100%', padding: isMobile ? '6px' : '8px', background: '#0B0E14', color: '#EAEDF2', border:'1px solid #5C6675', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', marginTop: '8px' }}>
                                                            {t('finish_btn')}
                                                        </button>
                                                    </div>
                                                
                                                /* ÉCRAN D'ATTENTE QUAND LES 5 ESSAIS SONT ÉPUISÉS MAIS QUE LE DUEL CONTINUE */
                                                ) : duelRoomId && ((gameFeedback?.guessCount || guessCount) >= 5) ? (
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px', width: '100%', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                        <h3 style={{ color: '#4CE0D2', margin: '0', fontSize: isMobile ? '16px' : '18px', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('waiting_for_rival')}    ⏳</h3>
                                                        <p style={{ fontSize: isMobile ? '12px' : '13px', color: '#EAEDF2', margin: '0', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('attempts_exhausted')}</p>
                                                    </div>
                                                
                                                ) : (
                                                /* --- ÉCRAN DE FIN : MODE SOLO / DAILY --- */
                                                gameFeedback.isWon ? (
                                                    <>
                                                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', gap: '4px', marginBottom: isMobile ? '0px' : '12px', width: '100%' }}>
                                                            <div style={{ flex: '0.72', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center', padding: '0 4px' }}>
                                                                <h3 style={{ color: '#EAEDF2', margin: isMobile ? '0 0 2px 0' : '0 0 5px 0', fontSize: isMobile ? '15.5px' : '18px' }}>{t('victory')}</h3>
                                                                <div style={{ marginBottom: '2px', fontSize: isMobile ? '10.5px' : '13px', color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                    <span style={{ color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('you_found')} </span>
                                                                    {(gameFeedback.challengeInfoUrl || gameFeedback.info) ? (
                                                                        <span onClick={() => setShowVideoModal(true)} style={{ color: '#4CE0D2', textDecoration: 'underline', fontWeight: 'bold', cursor: 'pointer', fontFamily: 'Inter, system-ui, sans-serif' }}>{displayChallengeName}</span>
                                                                    ) : (
                                                                        <span style={{ fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' }}>{displayChallengeName}</span>
                                                                    )}
                                                                </div>
                                                                {(gameFeedback.challengeInfoUrl || gameFeedback.info) && <div style={{ fontSize: isMobile ? '9.5px' : '12px', fontStyle: 'italic', fontFamily: 'Inter, system-ui, sans-serif',color: '#EAEDF2', cursor: 'pointer' }} onClick={() => setShowVideoModal(true)}>▶ {t('click_more_info')}</div>}
                                                            </div>
                                                            <div style={{ flex: '0.28', background: 'rgba(61, 220, 151, 0.15)', border: '1px solid #3DDC97', padding: '2px 2px', borderRadius: '6px', color: '#EAEDF2', fontSize: isMobile ? '12.5px' : '13px', margin: 0, whiteSpace: 'nowrap', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', boxSizing: 'border-box' }}>
                                                                <span style={{ fontWeight: 'bold', color: '#3DDC97' }}>{t('reward_label')}</span>
                                                                {gameFeedback.rewardScore && <BadgePoints text={`${gameFeedback.rewardScore} PTS`} fontSize="12px" padding="2px 6px" iconSize="14px" iconFirst={false} />}
                                                            </div>
                                                        </div>
                                                        <button onClick={() => { setGameFeedback(null); setGameId(null); setLastGuessLocation(null); setGuessHistory([]); resetMapToInitialView(); }} style={{ width: '100%', padding: isMobile ? '4px' : '6px', background: '#0B0E14', color: '#EAEDF2', border:'1px solid #5C6675', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', marginTop: isMobile ? '0px' : '2px', marginBottom: isMobile ? '-8px' : '0px' }}>{t('finish_btn')}</button>
                                                    </>
                                                ) : gameFeedback.isLost ? (
                                                    <div style={{display:'flex', flexDirection:isMobile?'row':'column', alignItems:'center', width:'100%', gap:isMobile?'4px':'0'}}>
                                                        <div style={{flex:isMobile?'0.72':'1', display:'flex', flexDirection:'column', justifyContent:'center', alignItems:'center', width:'100%'}}>
                                                            <h3 style={{ color: '#E03030', margin: isMobile ? '0 0 2px 0' : '0 0 5px 0', fontSize: isMobile ? '15.5px' : '18px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                {duelRoomId ? t('defeat_caps') : (gameFeedback.isTimeOut ? t('time_out') : t('game_over'))}
                                                            </h3>
                                                            <div style={{display:'flex', justifyContent:'center', alignItems:'center', gap:isMobile?'4px':'6px', marginBottom:isMobile?'0px':'10px', flexWrap:'wrap', fontFamily: 'Inter, system-ui, sans-serif'}}>
                                                                <p style={{fontSize:isMobile?'11.5px':'14px', margin:0, color:'#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif'}}>{t('target_at')} {formatDist(gameFeedback.distance, 2, false)}</p>
                                                                {gameFeedback.directionEmoji && (
                                                                    <>
                                                                        <span style={{fontSize:isMobile?'11.5px':'14px', fontWeight:'bold', color:'#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif'}}>{t('in_direction')}</span>
                                                                        <span style={{fontSize:'20px', filter:'drop-shadow(0 1px 2px rgba(0,0,0,0.5))'}}>{gameFeedback.directionEmoji}</span>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                        <div style={{flex:isMobile?'0.28':'1', width:'100%', display:'flex', alignItems:'center', justifyContent:'center'}}>
                                                            <button onClick={()=>{setGameFeedback(null); setGameId(null); setLastGuessLocation(null); setGuessHistory([]); resetMapToInitialView();}} style={{width:'100%', padding:isMobile?'8px 2px':'8px', background:'#0B0E14', color:'#EAEDF2', border:'1px solid #5C6675', borderRadius:'6px', cursor:'pointer', marginBottom:'0px', fontWeight:'bold', fontSize:isMobile?'12px':'inherit', minHeight:isMobile?'38px':'auto'}}>{t('close_btn')}</button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        {gameFeedback.isRadarPhase ? (
                                                            /* --- PHASE 2 : BLACKOUT RADAR (Essais 6 à 10) --- */
                                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', marginBottom: '4px', animation: 'fadeIn 0.4s' }}>
                                                                <div style={{ fontSize: '26px', animation: 'pulse 1.5s infinite' }}>📡</div>
                                                                <div style={{ fontSize: isMobile ? '12.5px' : '14px', fontWeight: 'bold', color: '#4CE0D2', textAlign: 'center', fontStyle: 'italic' }}>
                                                                    {/* Pense à ajouter la clé "radar_building_zone" dans tes fichiers json */}
                                                                    {t('radar_building_zone', 'Radar: Construction de la zone de recherche...')}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            /* --- PHASES 1 & 3 : DISTANCE EXACTE (Essais 1-5 et 11-14) --- */
                                                            <>
                                                                {/* 0. INJECTION SÉCURISÉE DE L'ANIMATION (Durée : 1 seconde exacte) */}
                                                                <style>{`
                                                                    @keyframes arrowSplashEffect {
                                                                        0% { transform: translate(-50%, -50%) scale(0); opacity: 0; }
                                                                        10% { transform: translate(-50%, -50%) scale(1.2); opacity: 1; }
                                                                        /* On maintient la flèche visible et stable beaucoup plus longtemps (de 10% à 85% du temps) */
                                                                        85% { transform: translate(-50%, -50%) scale(1); opacity: 1; }
                                                                        100% { transform: translate(-50%, -50%) scale(0.5); opacity: 0; visibility: hidden; }
                                                                    }
                                                                `}</style>

                                                                {/* 1. ANIMATION DE LA FLÈCHE (Centrée sur le clic avec Smart Clamping) */}
                                                                    {gameFeedback.directionEmoji && (
                                                                        <div style={{ 
                                                                            position: 'fixed', 
                                                                            /* Calcul de sécurité (Smart Clamping) : 
                                                                            Si pas de clic => centre par défaut.
                                                                            Sinon, contraint entre 80px du bord gauche/haut et 80px (ou 150px en bas pour le HUD) du bord droit/bas */
                                                                            left: clickPixel ? Math.max(80, Math.min(clickPixel.x, window.innerWidth - 80)) + 'px' : '50%',
                                                                            top: clickPixel ? Math.max(80, Math.min(clickPixel.y, window.innerHeight - (isMobile ? 180 : 100))) + 'px' : (isMobile ? '33%' : '50%'), 
                                                                            zIndex: 10010, 
                                                                            pointerEvents: 'none' 
                                                                        }}>
                                                                            <div key={`splash-${gameFeedback.guessCount}`} style={{
                                                                                animation: 'arrowSplashEffect 2s forwards cubic-bezier(0.1, 0.7, 0.1, 1)'
                                                                            }}>
                                                                                <DirectionArrow emoji={gameFeedback.directionEmoji} size={isMobile ? "100px" : "150px"} isSplash={true} />
                                                                            </div>
                                                                        </div>
                                                                    )}

                                                                {/* 2. AFFICHAGE CLASSIQUE HUD (Distance + Nouvelle Flèche miniature) */}
                                                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: isMobile ? '6px' : '10px', marginBottom: isMobile ? '2px' : '5px', animation: 'fadeIn 0.4s' }}>
                                                                    <div style={{ fontSize: isMobile ? '17.5px' : '19px', fontWeight: 'bold', color: gameFeedback.distance < 10 ? '#FF8A3D' : '#4CE0D2', whiteSpace: 'nowrap', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                        {duelRoomId ? `${t('duel_last_shot', 'Dernier tir :')} ` : ''}<span style={{ fontFamily: 'Orbitron, sans-serif' }}>{formatDist(gameFeedback.distance, 0, false)}</span>
                                                                    </div>
                                                                    
                                                                    {/* L'affichage permanent en Phase 3 dans le HUD */}
                                                                    {gameFeedback.directionEmoji && (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap' }}>
                                                                            <div style={{ fontSize: isMobile ? '12.5px' : '13px', fontWeight: 'bold', color: '#EAEDF2' }}>{t('in_direction')}</div>
                                                                            <div>
                                                                                <DirectionArrow emoji={gameFeedback.directionEmoji} size={isMobile ? "22px" : "28px"} isSplash={false} />
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                                
                                                                {/* 3. TEXTES : On garde TOUJOURS le texte d'origine */}
                                                                <p style={{ fontSize: isMobile ? '12px' : '15px', color: '#EAEDF2', margin: '0', fontStyle: 'italic', textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                                    {t('from_target_closer')}
                                                                </p>

                                                                {/* 4. TEXTES : Info Zone de recherche */}
                                                                {gameFeedback?.hasRadar && ((gameFeedback.guessCount >= 6 && gameFeedback.guessCount <= 10) || (ENABLE_PHASE3_CIRCLES && gameFeedback.guessCount >= 11)) && (
                                                                    <p style={{ fontSize: isMobile ? '11.5px' : '12px', color: '#4CE0D2', margin: '4px 0 0 0', fontWeight: 'bold', textAlign: 'center', animation: 'fadeIn 0.3s' }}>
                                                                        {t('target_in_zone')}
                                                                    </p>
                                                                )}
                                                            </>
                                                        )}
                                                    </>
                                                )
                                                )}
                                            </div>
                                        ) : (
                                            <div style={{ textAlign: 'center', color: '#EAEDF2', fontSize: isMobile ? '14px' : '16px', fontStyle: 'italic', fontWeight: 'bold', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                                {t('click_map_to_guess')}
                                            </div>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
            
            {memoizedGoogleMap}

            {/* Boutons d'ajustements de zooms manuels positionnés de façon ergonomique */}
            <div style={{ position: 'absolute', right: '10px', top: isMobile ? '25%' : '75%', transform: 'translateY(-50%)', display: 'flex', flexDirection: 'column', gap: '12px', zIndex: 500 }}>
                <button onClick={handleZoomIn} style={{ width: '42px', height: '42px', backgroundColor: 'rgba(255, 255, 255, 0.46)', border: 'none', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', cursor: 'pointer', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1, color: '#333', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>+</button>
                <button onClick={handleZoomOut} style={{ width: '42px', height: '42px', backgroundColor: 'rgba(255, 255, 255, 0.46)', border: 'none', borderRadius: '8px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', cursor: 'pointer', fontSize: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1, color: '#333', boxSizing: 'border-box', fontFamily: 'Arial, sans-serif' }}>−</button>
            </div>
        </div>

        {/* Fenêtre modale d'affichage des détails du compte, langues et options */}
{currentView === 'account' && user && (
    <div style={{ ...accountOverlayStyle, zIndex: 10000, backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(5px)' }}>
        <div style={{ ...accountModalBoxStyle, backgroundColor: '#0B0E14', border: '1px solid #5C6675', borderRadius: '16px', color: '#EAEDF2', width: window.innerWidth < 768 ? '95%' : '60%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', padding: isMobile ? '15px' : '20px', boxShadow: '0 15px 35px rgba(0,0,0,0.6)' }}>
            
            {/* --- HEADER DU COMPTE --- */}
            <div style={{ position: 'relative', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #1A1E26', paddingBottom: '15px', flexShrink: 0 }}>
                
                {/* Colonne 1 : Profil et Pseudo */}
                <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '12px', flex: 1, minWidth: 0 }}>
                    
                    {/* Badge de Rang Dynamique (remplace la photo de profil Google) */}
                    <RankBadge rank={userSoloGrade} size={isMobile ? 46 : 56} />
                    
                    {isEditingName ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                <input 
                                    type="text" 
                                    value={newDisplayName} 
                                    onChange={e => { setNewDisplayName(e.target.value); if (pseudoError) setPseudoError(''); }} 
                                    maxLength={15} 
                                    style={{ 
                                        padding: '4px 8px', 
                                        borderRadius: '6px', 
                                        border: pseudoError ? '1px solid #E03030' : '1px solid #4CE0D2', 
                                        background: '#1A1E26', 
                                        color: '#EAEDF2', 
                                        fontSize: isMobile ? '13px' : '15px', 
                                        outline: 'none', 
                                        width: isMobile ? '110px' : '130px',
                                        fontFamily: 'Inter, system-ui, sans-serif'
                                    }} 
                                    placeholder="4-15 car." 
                                    autoFocus 
                                />
                                <button 
                                    onClick={handleUpdatePseudo} 
                                    style={{ background: '#4CE0D2', color: '#0B0E14', border: 'none', borderRadius: '6px', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                                    title={t('confirm', 'Valider')}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                    </svg>
                                </button>
                                <button 
                                    onClick={() => { setIsEditingName(false); setPseudoError(''); }} 
                                    style={{ background: '#1A1E26', border: '1px solid #5C6675', color: '#EAEDF2', borderRadius: '6px', width: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0 }}
                                    title={t('cancel', 'Annuler')}
                                >
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="18" y1="6" x2="6" y2="18"></line>
                                        <line x1="6" y1="6" x2="18" y2="18"></line>
                                    </svg>
                                </button>
                            </div>
                            {pseudoError && (
                                <div style={{ color: '#E03030', fontSize: '10px', fontWeight: 'bold' }}>{pseudoError}</div>
                            )}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0, justifyContent: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', overflow: 'hidden' }}>
                                <h3 style={{ margin: 0, fontSize: isMobile ? '15px' : '18px', color: '#EAEDF2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                    {userData?.displayName || user?.displayName || t('anonymous')}
                                </h3>
                                <button 
                                    onClick={() => { setIsEditingName(true); setNewDisplayName(userData?.displayName || user?.displayName || ''); setPseudoError(''); }} 
                                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px', color: '#4CE0D2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'color 0.2s' }} 
                                    title={t('edit_pseudo', 'Modifier le pseudo')}
                                    onMouseEnter={e => e.currentTarget.style.color = '#EAEDF2'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#4CE0D2'}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                </button>
                            </div>
                            <div style={{ fontSize: isMobile ? '10.5px' : '12px', color: getRankData(userSoloGrade).color, fontWeight: 'bold', fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.5px', marginTop: '2px' }}>
                                {userSoloGrade}
                            </div>
                            {/* Échelle de progression des 5 rangs */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px' }}>
                                {['Touriste', 'Voyageur', 'Aventurier', 'Explorateur', 'Légende'].map((tier) => {
                                    const isCurrent = getRankData(userSoloGrade).key === getRankData(tier).key;
                                    return (
                                        <div 
                                            key={tier} 
                                            title={`${tier}${isCurrent ? ` (${t('current_rank', 'Rang actuel')})` : ''}`}
                                            style={{ 
                                                opacity: isCurrent ? 1 : 0.35, 
                                                transform: isCurrent ? 'scale(1.18)' : 'scale(1)',
                                                transition: 'all 0.2s ease',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}
                                        >
                                            <RankBadge rank={tier} size={isMobile ? 20 : 24} />
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>

                {/* Colonne 2 : Options (Langues & Unités de mesure) */}
                <div style={{ 
                    display: 'flex', 
                    flexDirection: isMobile ? 'column' : 'row', 
                    gap: isMobile ? '6px' : '12px', 
                    alignItems: 'center', 
                    justifyContent: isMobile ? 'flex-end' : 'center', 
                    flex: isMobile ? 'none' : 1 
                }}>
                    {/* Sélecteur de Langues */}
                    <div style={{ display: 'flex', gap: isMobile ? '5px' : '8px', alignItems: 'center' }}>
                        {[{code:'fr',svg:<svg viewBox="0 0 3 2" style={{width:'100%',height:'100%',display:'block'}}><rect width="1" height="2" fill="#0055A4"/><rect x="1" width="1" height="2" fill="#ffffff"/><rect x="2" width="1" height="2" fill="#EF4135"/></svg>},{code:'en',svg:<svg viewBox="0 0 60 40" style={{width:'100%',height:'100%',display:'block'}}><rect width="60" height="40" fill="#012169"/><path d="M0,0 L60,40 M60,0 L0,40" stroke="#ffffff" strokeWidth="8"/><path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="3"/><path d="M30,0 L30,40 M0,20 L60,20" stroke="#ffffff" strokeWidth="12"/><path d="M30,0 L30,40 M0,20 L60,20" stroke="#C8102E" strokeWidth="6"/></svg>},{code:'es',svg:<svg viewBox="0 0 3 2" style={{width:'100%',height:'100%',display:'block'}}><rect width="3" height="2" fill="#AA151B"/><rect y="0.5" width="3" height="1" fill="#F1BF00"/></svg>}].map(lang=>(
                            <button key={lang.code} onClick={()=>i18n.changeLanguage(lang.code)} title={`Changer pour ${lang.code}`} style={{background:i18n.language?.startsWith(lang.code)?'#4CE0D2':'#1A1E26',border:i18n.language?.startsWith(lang.code)?'2px solid #4CE0D2':'1px solid #5C6675',borderRadius:'50%',width:isMobile?'26px':'34px',height:isMobile?'26px':'34px',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',padding:0,transition:'all 0.2s ease',boxShadow:i18n.language?.startsWith(lang.code)?'0 0 8px rgba(76, 224, 209, 0.4)':'none',overflow:'hidden',opacity:i18n.language?.startsWith(lang.code)?1:0.6}}>{lang.svg}</button>
                        ))}
                    </div>

                    {/* Sélecteur d'Unité (KM / MILES) */}
                    <div style={{
                        display: 'inline-flex',
                        background: '#141A23',
                        border: '1px solid #3A4659',
                        borderRadius: '20px',
                        padding: '2px',
                        gap: '2px',
                        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.4)'
                    }}>
                        <button
                            onClick={() => handleSelectDistanceUnit('km')}
                            title={t('unit_km_desc', 'Kilomètres')}
                            style={{
                                background: distanceUnit === 'km' ? '#4CE0D2' : 'transparent',
                                color: distanceUnit === 'km' ? '#0B0E14' : '#8A95A5',
                                border: 'none',
                                borderRadius: '16px',
                                padding: isMobile ? '2px 8px' : '3px 10px',
                                fontSize: isMobile ? '10px' : '11px',
                                fontWeight: 'bold',
                                fontFamily: 'Orbitron, sans-serif',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: distanceUnit === 'km' ? '0 0 8px rgba(76, 224, 209, 0.5)' : 'none'
                            }}
                        >
                            KM
                        </button>
                        <button
                            onClick={() => handleSelectDistanceUnit('miles')}
                            title={t('unit_miles_desc', 'Miles')}
                            style={{
                                background: distanceUnit === 'miles' ? '#4CE0D2' : 'transparent',
                                color: distanceUnit === 'miles' ? '#0B0E14' : '#8A95A5',
                                border: 'none',
                                borderRadius: '16px',
                                padding: isMobile ? '2px 8px' : '3px 10px',
                                fontSize: isMobile ? '10px' : '11px',
                                fontWeight: 'bold',
                                fontFamily: 'Orbitron, sans-serif',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                boxShadow: distanceUnit === 'miles' ? '0 0 8px rgba(76, 224, 209, 0.5)' : 'none'
                            }}
                        >
                            MILES
                        </button>
                    </div>
                </div>

                {/* Colonne 3 : Espace vide (Tampon pour la croix de fermeture) */}
                <div style={{ flex: isMobile ? 'none' : 1, width: isMobile ? '40px' : 'auto' }}></div>
                
                {/* Bouton Fermer */}
                <div style={{ position: 'absolute', top: '1px', right: '-5px' }}>
                    <button onClick={() => setCurrentView('map')} style={{ background: 'transparent', border: 'none', fontSize: isMobile ? '24px' : '28px', cursor: 'pointer', color: '#5C6675', padding: 0, transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = '#EAEDF2'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'}>✖</button>
                </div>
            </div>

            {/* --- 1. ESPACE PREMIUM (Remonté + Espace réduit) --- */}
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(20, 27, 38, 0.8) 100%)', border: '1px solid #D4AF37', borderRadius: '12px', padding: isMobile ? '14px 10px' : '15px 14px', marginTop: '2px', textAlign: 'center', position: 'relative', overflow: 'hidden', boxShadow: '0 4px 15px rgba(255, 215, 0, 0.1)', flexShrink: 0 }}>
                <div style={{ position: 'absolute', top: '-10px', right: '-10px', fontSize: '60px', opacity: 0.1, transform: 'rotate(15deg)', pointerEvents: 'none' }}>🌟</div>
                
                {isMobile ? (
                    /* --- OPTION 2 SUR MOBILE : Ligne 1 Titre en vedette | Ligne 2 Statut & Comparateur --- */
                    <>
                        {/* Ligne 1 : Titre en vedette */}
                        <h4 style={{
                            color: '#FFD700',
                            margin: '0 0 10px 0',
                            fontSize: '16px',
                            fontFamily: 'Orbitron, sans-serif',
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                            textAlign: 'center',
                            letterSpacing: '0.5px'
                        }}>
                            {t('premium_title', 'Géo-Détective Premium')}
                        </h4>

                        {/* Ligne 2 : Statut (gauche) & Bouton Comparer (droite) */}
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            marginBottom: '12px',
                            zIndex: 1,
                            position: 'relative'
                        }}>
                            {/* Statut actuel du joueur */}
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: isPremiumUser ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                                border: `1px solid ${isPremiumUser ? '#FFD700' : 'rgba(255, 255, 255, 0.3)'}`,
                                borderRadius: '20px',
                                padding: '3px 8px',
                                boxShadow: isPremiumUser ? '0 0 10px rgba(255, 215, 0, 0.25)' : 'none',
                                flexShrink: 0
                            }}>
                                <span style={{
                                    fontSize: '10px',
                                    color: isPremiumUser ? '#D4AF37' : '#8A95A5',
                                    fontFamily: 'Orbitron, sans-serif',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.3px'
                                }}>
                                    {t('status_label', 'Statut')} :
                                </span>
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    fontFamily: 'Orbitron, sans-serif',
                                    color: isPremiumUser ? '#FFD700' : '#FFFFFF',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    textShadow: isPremiumUser ? '0 0 8px rgba(255, 215, 0, 0.5)' : 'none'
                                }}>
                                    {isPremiumUser ? (
                                        isVipUser
                                            ? `★ ${t('status_premium_vip', 'Premium VIP')}`
                                            : isLifetimeUser 
                                                ? `★ ${t('status_premium', 'Premium')} (${t('status_permanent', 'Permanent')})`
                                            : userData?.premiumPlan === 'annual'
                                                ? `★ ${t('status_premium', 'Premium')} (${t('plan_annual_badge', 'Annuel')}${userData?.premiumExpiresAt ? ` - ${t('status_until', "Jusqu'au {{date}}", { date: formatShortDate(userData.premiumExpiresAt, i18n.language) })}` : ''})`
                                                : userData?.premiumPlan === 'monthly'
                                                    ? `★ ${t('status_premium', 'Premium')} (${t('plan_monthly_badge', 'Mensuel')}${userData?.premiumExpiresAt ? ` - ${t('status_until', "Jusqu'au {{date}}", { date: formatShortDate(userData.premiumExpiresAt, i18n.language) })}` : ''})`
                                                    : userData?.premiumExpiresAt 
                                                        ? `★ ${t('status_premium', 'Premium')} (${t('status_until', "Jusqu'au {{date}}", { date: formatShortDate(userData.premiumExpiresAt, i18n.language) })})`
                                                        : `★ ${t('status_premium', 'Premium')}`
                                    ) : t('status_standard', 'Standard')}
                                </span>
                            </div>

                            {/* Bouton Comparer les offres */}
                            <button
                                onClick={() => setIsCompareModalOpen(true)}
                                style={{
                                    background: 'rgba(20, 27, 38, 0.9)',
                                    border: '1px solid #FFD700',
                                    borderRadius: '20px',
                                    padding: '4px 10px',
                                    color: '#FFD700',
                                    fontSize: '11px',
                                    fontWeight: 'bold',
                                    fontFamily: 'Orbitron, sans-serif',
                                    cursor: 'pointer',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)',
                                    transition: 'all 0.2s ease',
                                    whiteSpace: 'nowrap',
                                    flexShrink: 0
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.04)';
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.4)';
                                    e.currentTarget.style.background = 'rgba(255, 215, 0, 0.15)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.2)';
                                    e.currentTarget.style.background = 'rgba(20, 27, 38, 0.9)';
                                }}
                            >
                                <span>{t('compare_offers_btn', 'Comparer les offres')}</span>
                            </button>
                        </div>
                    </>
                ) : (
                    /* --- SUR DESKTOP : 1 seule ligne unifiée --- */
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        gap: '12px',
                        marginBottom: '12px',
                        zIndex: 1,
                        position: 'relative'
                    }}>
                        {/* En haut à gauche : Statut actuel du joueur */}
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: isPremiumUser ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                            border: `1px solid ${isPremiumUser ? '#FFD700' : 'rgba(255, 255, 255, 0.3)'}`,
                            borderRadius: '20px',
                            padding: '4px 12px',
                            boxShadow: isPremiumUser ? '0 0 10px rgba(255, 215, 0, 0.25)' : 'none',
                            flexShrink: 0
                        }}>
                            <span style={{
                                fontSize: '11px',
                                color: isPremiumUser ? '#D4AF37' : '#8A95A5',
                                fontFamily: 'Orbitron, sans-serif',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px'
                            }}>
                                {t('status_label', 'Statut')} :
                            </span>
                            <span style={{
                                fontSize: '12px',
                                fontWeight: 'bold',
                                fontFamily: 'Orbitron, sans-serif',
                                color: isPremiumUser ? '#FFD700' : '#FFFFFF',
                                letterSpacing: '0.8px',
                                textTransform: 'uppercase',
                                textShadow: isPremiumUser ? '0 0 8px rgba(255, 215, 0, 0.5)' : 'none'
                            }}>
                                {isPremiumUser ? (
                                    isVipUser
                                        ? `★ ${t('status_premium_vip', 'Premium VIP')}`
                                        : isLifetimeUser 
                                            ? `★ ${t('status_premium', 'Premium')} (${t('status_permanent', 'Permanent')})`
                                        : userData?.premiumPlan === 'annual'
                                            ? `★ ${t('status_premium', 'Premium')} (${t('plan_annual_badge', 'Annuel')}${userData?.premiumExpiresAt ? ` - ${t('status_until', "Jusqu'au {{date}}", { date: formatShortDate(userData.premiumExpiresAt, i18n.language) })}` : ''})`
                                            : userData?.premiumPlan === 'monthly'
                                                ? `★ ${t('status_premium', 'Premium')} (${t('plan_monthly_badge', 'Mensuel')}${userData?.premiumExpiresAt ? ` - ${t('status_until', "Jusqu'au {{date}}", { date: formatShortDate(userData.premiumExpiresAt, i18n.language) })}` : ''})`
                                                : userData?.premiumExpiresAt 
                                                    ? `★ ${t('status_premium', 'Premium')} (${t('status_until', "Jusqu'au {{date}}", { date: formatShortDate(userData.premiumExpiresAt, i18n.language) })})`
                                                    : `★ ${t('status_premium', 'Premium')}`
                                ) : t('status_standard', 'Standard')}
                            </span>
                        </div>

                        {/* Centre : Titre Premium */}
                        <h4 style={{
                            color: '#FFD700',
                            margin: 0,
                            fontSize: '17px',
                            fontFamily: 'Orbitron, sans-serif',
                            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1
                        }}>
                            {t('premium_title', 'Géo-Détective Premium')}
                        </h4>

                        {/* En haut à droite : Bouton Comparer les offres */}
                        <button
                            onClick={() => setIsCompareModalOpen(true)}
                            style={{
                                background: 'rgba(20, 27, 38, 0.9)',
                                border: '1px solid #FFD700',
                                borderRadius: '20px',
                                padding: '5px 14px',
                                color: '#FFD700',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                fontFamily: 'Orbitron, sans-serif',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                boxShadow: '0 2px 8px rgba(255, 215, 0, 0.2)',
                                transition: 'all 0.2s ease',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.04)';
                                e.currentTarget.style.boxShadow = '0 4px 15px rgba(255, 215, 0, 0.4)';
                                e.currentTarget.style.background = 'rgba(255, 215, 0, 0.15)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'scale(1)';
                                e.currentTarget.style.boxShadow = '0 2px 8px rgba(255, 215, 0, 0.2)';
                                e.currentTarget.style.background = 'rgba(20, 27, 38, 0.9)';
                            }}
                        >
                            <span>{t('compare_offers_btn', 'Comparer les offres')}</span>
                        </button>
                    </div>
                )}
                
                {isPremiumUser ? (
                    <div style={{
                        background: 'linear-gradient(180deg, rgba(26, 30, 38, 0.85) 0%, rgba(14, 19, 28, 0.95) 100%)',
                        border: '1.5px solid rgba(255, 215, 0, 0.4)',
                        borderRadius: '12px',
                        padding: isMobile ? '12px 10px' : '16px 18px',
                        marginBottom: isMobile ? '10px' : '12px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                        width: '100%',
                        boxSizing: 'border-box'
                    }}>
                        <div style={{
                            fontFamily: 'Orbitron, sans-serif',
                            fontSize: isMobile ? '12px' : '14px',
                            fontWeight: '700',
                            color: '#FFD700',
                            marginBottom: '6px',
                            letterSpacing: '0.5px'
                        }}>
                            ★ {t('premium_active_title', 'Votre abonnement Premium est actif !')}
                        </div>
                        <div style={{
                            fontFamily: 'Inter, sans-serif',
                            fontSize: isMobile ? '11px' : '12px',
                            color: '#EAEDF2',
                            lineHeight: '1.4',
                            marginBottom: '12px',
                            maxWidth: '520px'
                        }}>
                            {t('premium_active_desc', 'Vous profitez des 5 zooms quotidiens, des duels en temps réel et du replay de votre collection.')}
                        </div>
                        
                        {/* Bouton Se désabonner */}
                        <button
                            onClick={() => setIsCancelModalOpen(true)}
                            style={{
                                background: 'rgba(255, 76, 76, 0.1)',
                                border: '1px solid rgba(255, 76, 76, 0.4)',
                                color: '#FF6B6B',
                                borderRadius: '8px',
                                padding: isMobile ? '8px 14px' : '9px 18px',
                                fontFamily: 'Orbitron, sans-serif',
                                fontSize: isMobile ? '11px' : '12px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 76, 76, 0.2)';
                                e.currentTarget.style.borderColor = '#FF6B6B';
                                e.currentTarget.style.transform = 'scale(1.02)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(255, 76, 76, 0.1)';
                                e.currentTarget.style.borderColor = 'rgba(255, 76, 76, 0.4)';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            <span>✕</span>
                            <span>{t('unsubscribe_premium', 'Se désabonner de Premium')}</span>
                        </button>
                    </div>
                ) : (
                    <>
                        <p style={{ color: '#EAEDF2', fontSize: isMobile ? '13px' : '15px', fontFamily: 'Inter, sans-serif', margin: '0 0 12px 0', lineHeight: '1.4' }}>
                            {t('welcome_offer', 'Offre de bienvenue :')} <strong style={{color: '#FFD700', fontFamily: 'Inter, sans-serif'}}>{t('free_30_days', 'Gratuit (30 jours)')}</strong> {t('for_first_50_players', 'pour les 50 premiers joueurs !')}
                        </p>

                        {/* --- CHOIX DES TARIFS PREMIUM (Mensuel vs Annuel en Cyan) --- */}
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: isMobile ? '8px' : '14px',
                                marginBottom: isMobile ? '10px' : '12px',
                                width: '100%',
                                boxSizing: 'border-box'
                            }}
                        >
                            {/* Option 1 : Premium Mensuel */}
                            <div
                                onClick={() => handleSubscribe('monthly')}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: 'linear-gradient(180deg, rgba(26, 30, 38, 0.75) 0%, rgba(14, 19, 28, 0.9) 100%)',
                                    border: '1.5px solid #FFD700',
                                    borderRadius: '12px',
                                    padding: isMobile ? '6px 5px 5px 5px' : '8px 10px 7px 10px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    position: 'relative',
                                    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                                    boxSizing: 'border-box',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.35)';
                                    e.currentTarget.style.borderColor = '#FFE55C';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.4)';
                                    e.currentTarget.style.borderColor = '#FFD700';
                                }}
                            >
                                {/* 1. Titre option (aligné) */}
                                <div style={{
                                    fontFamily: 'Orbitron, sans-serif',
                                    fontSize: isMobile ? '10px' : '12px',
                                    fontWeight: '700',
                                    color: '#FFD700',
                                    letterSpacing: isMobile ? '0.5px' : '0.8px',
                                    textTransform: 'uppercase',
                                    minHeight: isMobile ? '16px' : '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    lineHeight: '1.15'
                                }}>
                                    {t('plan_monthly', 'Premium Mensuel')}
                                </div>

                                {/* 2. Prix (aligné, même taille de police) */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'center',
                                    gap: '3px',
                                    marginTop: '1px',
                                    marginBottom: '1px',
                                    minHeight: isMobile ? '22px' : '26px'
                                }}>
                                    <span style={{
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '20px' : '23px',
                                        fontWeight: '800',
                                        color: '#EAEDF2',
                                        letterSpacing: '0.5px'
                                    }}>
                                        3,99 €
                                    </span>
                                    <span style={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: isMobile ? '9.5px' : '11.5px',
                                        color: '#8A95A5',
                                        fontWeight: '500'
                                    }}>
                                        {t('per_month', '/ mois')}
                                    </span>
                                </div>

                                {/* 3. Moyen de facturation (aligné) */}
                                <div style={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: isMobile ? '10px' : '12px',
                                    color: '#8A95A5',
                                    lineHeight: '1.15',
                                    textAlign: 'center',
                                    minHeight: isMobile ? '22px' : '26px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {t('monthly_billing_desc', 'Facturé mensuellement. Annulable à tout moment.')}
                                </div>

                                {/* 4. Espace réservé pour alignement strict avec le badge annuel */}
                                <div style={{
                                    minHeight: isMobile ? '16px' : '18px',
                                    marginTop: '2px'
                                }} />
                            </div>

                            {/* Option 2 : Premium Annuel */}
                            <div
                                onClick={() => handleSubscribe('annual')}
                                style={{
                                    flex: 1,
                                    minWidth: 0,
                                    background: 'linear-gradient(180deg, rgba(255, 215, 0, 0.08) 0%, rgba(18, 16, 12, 0.95) 100%)',
                                    border: '2px solid #FFD700',
                                    borderRadius: '12px',
                                    padding: isMobile ? '6px 5px 5px 5px' : '8px 10px 7px 10px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'flex-start',
                                    alignItems: 'center',
                                    position: 'relative',
                                    boxShadow: '0 0 20px rgba(255, 215, 0, 0.2), 0 4px 15px rgba(0, 0, 0, 0.5)',
                                    boxSizing: 'border-box',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                    e.currentTarget.style.boxShadow = '0 0 25px rgba(255, 215, 0, 0.4), 0 6px 20px rgba(0, 0, 0, 0.6)';
                                    e.currentTarget.style.borderColor = '#FFF08A';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.2), 0 4px 15px rgba(0, 0, 0, 0.5)';
                                    e.currentTarget.style.borderColor = '#FFD700';
                                }}
                            >
                                {/* 1. Titre option (aligné) */}
                                <div style={{
                                    fontFamily: 'Orbitron, sans-serif',
                                    fontSize: isMobile ? '10px' : '12px',
                                    fontWeight: '700',
                                    color: '#FFD700',
                                    letterSpacing: isMobile ? '0.5px' : '0.8px',
                                    textTransform: 'uppercase',
                                    minHeight: isMobile ? '16px' : '18px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    lineHeight: '1.15'
                                }}>
                                    {t('plan_annual', 'Premium Annuel')}
                                </div>

                                {/* 2. Prix (aligné, même taille de police) */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    justifyContent: 'center',
                                    gap: '3px',
                                    marginTop: '1px',
                                    marginBottom: '1px',
                                    minHeight: isMobile ? '22px' : '26px'
                                }}>
                                    <span style={{
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '20px' : '23px',
                                        fontWeight: '800',
                                        color: '#FFD700',
                                        letterSpacing: '0.5px',
                                        textShadow: '0 0 12px rgba(255, 215, 0, 0.4)'
                                    }}>
                                        2,08 €
                                    </span>
                                    <span style={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: isMobile ? '9.5px' : '11.5px',
                                        color: '#FFD700',
                                        fontWeight: '600'
                                    }}>
                                        {t('per_month', '/ mois')}
                                    </span>
                                </div>

                                {/* 3. Moyen de facturation (aligné) */}
                                <div style={{
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: isMobile ? '10px' : '12px',
                                    color: '#EAEDF2',
                                    fontWeight: '500',
                                    lineHeight: '1.15',
                                    textAlign: 'center',
                                    minHeight: isMobile ? '22px' : '26px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                }}>
                                    {t('annual_billing_desc', 'Facturé 24,99 € une fois par an.')}
                                </div>

                                {/* 4. Badge Économisez 48 % */}
                                <div style={{
                                    minHeight: isMobile ? '16px' : '18px',
                                    marginTop: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%'
                                }}>
                                    <div style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '3px',
                                        background: 'rgba(255, 215, 0, 0.12)',
                                        border: '1px solid rgba(255, 215, 0, 0.35)',
                                        borderRadius: '5px',
                                        padding: isMobile ? '1.5px 5px' : '2px 7px',
                                        color: '#FFD700',
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: isMobile ? '8.5px' : '10.5px',
                                        fontWeight: '700',
                                        textAlign: 'center',
                                        lineHeight: '1.2'
                                    }}>
                                        <span>🎁</span>
                                        <span>{t('annual_savings_badge', 'Économisez 48 %')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Bouton d'action promotionnel */}
                        <div style={{ width: '100%', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px' }}>
                            <button 
                                onClick={handleTestPremium} 
                                style={{ width: '100%', background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)', color: '#0B0E14', border: 'none', borderRadius: '8px', padding: isMobile ? '11px 15px' : '12px 20px', fontWeight: 'bold', fontFamily: 'Orbitron, sans-serif', cursor: 'pointer', fontSize: isMobile ? '12.5px' : '14px', boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)', transition: 'transform 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }} 
                                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.02)'} 
                                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                            >
                                <span>★</span>
                                <span>{t('btn_promo_free', 'Obtenir 30 jours gratuits (Aucun engagement)')}</span>
                            </button>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '11px', color: '#8A95A5', fontFamily: 'Inter, sans-serif' }}>
                                <IconShield size="14px" color="#4CE0D2" />
                                <span>{t('welcome_gift_reassurance', '100% gratuit • Aucune carte bancaire requise • Aucun engagement')}</span>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* --- 2. ESPACE CLASSEMENT (Descendu + Titre Orbitron + Espace réduit) --- */}
            <div style={{ background: 'linear-gradient(135deg, #1e3c72 0%, rgb(123, 30, 154) 100%)', borderRadius: '12px', padding: isMobile ? '10px 8px' : '15px', marginTop: '2px', color: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.4)', border: '1px solid rgba(255, 255, 255, 0.2)', flexShrink: 0, boxSizing: 'border-box' }}>
                {/* Titre mis à jour avec le style de la collection (Orbitron, cyan) */}
                <h3 style={{ 
                    color: '#EAEDF2', 
                    margin: '0 0 12px 0', 
                    fontFamily: 'Orbitron, sans-serif', 
                    letterSpacing: '1px', 
                    fontSize: isMobile ? '15px' : '18px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', /* <-- L'AJOUT EST ICI */
                    gap: '8px' 
                }}>
                    {t('leaderboard_section', 'Mon Classement')}
                </h3>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: isMobile ? '4px' : '10px' }}>
                    <div style={{ flex: 1, background: 'rgba(0, 0, 0, 0.25)', borderRadius: '8px', padding: isMobile ? '6px 2px' : '8px', textAlign: 'center', minWidth: 0 }}>
                        {/* LIGNE CORRIGÉE (Flexbox, alignItems center) */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', color: '#4CE0D2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <IconDaily size={isMobile ? '18px' : '22px'} color="#EAEDF2" /> 
                            {t('daily_zoom', 'Daily')}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '15px', fontFamily: 'Inter, system-ui, sans-serif', marginTop: '2px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                            #{myDaily?.rank || '-'} <span style={{opacity:0.5, margin:'0 2px'}}>|</span> {myDaily?.displayScore || '0'}
                        </div>
                    </div>
                    
                    <div style={{ flex: 1, background: 'rgba(0, 0, 0, 0.25)', borderRadius: '8px', padding: isMobile ? '6px 2px' : '8px', textAlign: 'center', minWidth: 0 }}>
                        {/* LIGNE CORRIGÉE */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', color: '#4CE0D2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <IconSolo size={isMobile ? '18px' : '22px'} color="#EAEDF2" /> 
                            {t('solo_mode', 'Solo')}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '15px', fontFamily: 'Inter, system-ui, sans-serif', marginTop: '2px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                            #{mySolo?.rank || '-'} <span style={{opacity:0.5, margin:'0 2px'}}>|</span> {mySolo?.displayScore || '0'}
                        </div>
                    </div>
                    
                    <div style={{ flex: 1, background: 'rgba(0, 0, 0, 0.25)', borderRadius: '8px', padding: isMobile ? '6px 2px' : '8px', textAlign: 'center', minWidth: 0 }}>
                        {/* LIGNE CORRIGÉE */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', color: '#4CE0D2', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            <IconDuel size={isMobile ? '18px' : '22px'} color="#EAEDF2" /> 
                            {t('duel_mode', 'Duel')}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '15px', fontFamily: 'Inter, system-ui, sans-serif', marginTop: '2px', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                            #{myDuel?.rank || '-'} <span style={{opacity:0.5, margin:'0 2px'}}>|</span> {myDuel?.displayScore || '0'}
                        </div>
                    </div>
                </div>

                {/* BOUTON CLASSEMENT (Aligné à 100%) */}
                <button 
                onClick={() => { fetchLeaderboard(); setIsLeaderboardOpen(true); }}
                style={{
                    width: '50%',
                    display: 'block', // Indispensable pour que le centrage fonctionne
                    margin: '15px auto 0 auto', // Centre le bouton (gauche/droite auto) et met l'espace au-dessus
                    background: 'linear-gradient(135deg, #B24BF3 0%, #7E22CE 100%)',
                    color: '#EAEDF2',
                    border: 'none',
                    borderRadius: '8px',
                    padding: isMobile ? '8px 12px' : '10px 24px',
                    fontSize: isMobile ? '13px' : '15px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    boxShadow: '0 4px 15px rgba(178, 75, 243, 0.3)',
                    transition: 'all 0.2s ease',
                    fontFamily: 'Orbitron, sans-serif'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
                {t('view_full_leaderboard', 'Voir le classement complet')}
            </button>
            </div>

            {/* --- 3. BLOC PROGRESSION & COLLECTION (Espace réduit) --- */}
            <div style={{ 
                background: 'rgba(255,255,255,0.05)', 
                border: '1px solid rgba(76, 224, 209, 0.3)', 
                borderRadius: '12px', 
                padding: '15px',
                marginTop: '2px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
                gap: '10px',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <h3 style={{ color: '#4CE0D2', margin: 0, fontFamily: 'Orbitron, sans-serif', letterSpacing: '1px' }}>
                    {t('collection_title_upper')}
                </h3>

                {/* MODIFICATION ICI : margin: '-2px 0 4px 0' annule l'espace par défaut du navigateur et rapproche le texte du titre */}
                <p style={{ fontSize: '15px', color: '#EAEDF2', fontFamily: 'Inter, sans-serif', margin: '-2px 0 4px 0', lineHeight: '1.4' }}>
                    {t('collection_desc', 'Les lieux que vous avez trouvés avec précision (victoires) sont ajoutés ici. Rejouez-les à volonté pour vous entraîner !')}
                </p>

                <p style={{ color: '#4CE0D2', margin: 0, fontSize: isMobile ? '16px' : '18px', }}>
                    <strong>{mesZoomsTrouves.length}</strong> {t('zooms_found')} {TOTAL_ZOOMS}
                </p>
                
                {/* BOUTON COLLECTION (Aligné à 100%) */}
                <button 
                    onClick={() => setIsPokedexOpen(true)}
                    style={{
                        width: '50%', // NOUVEAU : Largeur complète
                        background: 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)',
                        color: '#0A0F18',
                        border: 'none',
                        borderRadius: '8px',
                        padding: isMobile ? '8px 12px' : '10px 24px',
                        fontSize: isMobile ? '13px' : '15px',
                        fontWeight: 'bold',
                        cursor: 'pointer',
                        marginTop: '8px',
                        boxShadow: '0 4px 15px rgba(76, 224, 209, 0.3)',
                        transition: 'all 0.2s ease',
                        fontFamily: 'Orbitron, sans-serif'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                    onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                    {t('collection_view_btn')}
                </button>
            </div>
            
            {/* --- BLOC ADMIN : EXPORT STATS DES COLLECTIONS (Réservé à l'adresse e-mail admin) --- */}
            {isAdmin && (
                <div style={{ 
                    background: 'rgba(255, 215, 0, 0.06)', 
                    border: '1px dashed rgba(255, 215, 0, 0.45)', 
                    borderRadius: '10px', 
                    padding: isMobile ? '8px 10px' : '10px 14px', 
                    marginTop: '4px',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between', 
                    gap: '10px',
                    flexWrap: 'wrap',
                    boxSizing: 'border-box',
                    flexShrink: 0
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '15px' }}>👑</span>
                        <span style={{ fontSize: '11px', color: '#FFD700', fontFamily: 'Orbitron, sans-serif', fontWeight: 'bold', letterSpacing: '0.5px' }}>
                            ADMIN ANALYTICS
                        </span>
                    </div>
                    <button
                        onClick={handleExportCollectionStats}
                        disabled={isExportingStats}
                        style={{
                            background: isExportingStats ? '#3A4659' : 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                            color: '#0B0E14',
                            border: 'none',
                            borderRadius: '6px',
                            padding: isMobile ? '6px 10px' : '7px 14px',
                            fontSize: isMobile ? '11px' : '12px',
                            fontWeight: 'bold',
                            fontFamily: 'Orbitron, sans-serif',
                            cursor: isExportingStats ? 'not-allowed' : 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            boxShadow: '0 2px 8px rgba(255, 215, 0, 0.25)',
                            transition: 'all 0.2s ease',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        {isExportingStats ? '⏳ Extraction...' : '📊 Exporter Stats Collections (CSV)'}
                    </button>
                </div>
            )}
            
            {/* --- FOOTER (Espace réduit) --- */}
            <div style={{ borderTop: '1px solid #1A1E26', paddingTop: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '2px', flexShrink: 0 }}>
                <button onClick={handleSignOut} style={{ padding: '10px 16px', background: 'transparent', color: '#5C6675', border: '1px solid #5C6675', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', transition: 'all 0.2s' }} onMouseEnter={e => { e.currentTarget.style.background = '#5C6675'; e.currentTarget.style.color = '#EAEDF2'; }} onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#5C6675'; }}>
                    {t('logout', 'Déconnexion')}
                </button>
                
                <button onClick={() => setCurrentView('map')} style={{ padding: '10px 24px', background: 'linear-gradient(135deg, #4CE0D2 0%, #5C6675 100%)', color: '#0B0E14', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px', transition: 'filter 0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }} onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'} onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}>
                    {t('close_btn', 'Fermer')}
                </button>
            </div>
            
        </div>
    </div>
)}


       {/* Modale d'interruption et validation de l'abandon de l'enquête cartographique */}
        {showAbandonConfirm && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <div style={{ background: 'white', padding: '25px', borderRadius: '12px', textAlign: 'center', width: '300px', boxShadow: '0 4px 15px rgba(0,0,0,0.2)' }}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '18px' }}> {t('abandon_title')}</h3>
                    <p style={{ fontSize: '16px', color: '#666', marginBottom: '25px', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('abandon_desc')}</p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button 
                            onClick={() => setShowAbandonConfirm(false)} 
                            style={{ flex: 1, padding: '10px', background: '#f1f3f5', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', color: '#333' }}
                        >
                            {t('no')}
                        </button>
                        <button 
                            onClick={async () => { 
                                // --- PRÉVIENT LE SERVEUR DE L'ABANDON ---
                                if (duelRoomId) {
                                    await httpsCallable(functions, 'resolveDuelState')({ roomId: duelRoomId, reason: 'surrender' });
                                } else if (gameId) {
                                    if (currentGameMode === 'daily') {
                                        const todayStr = new Date().toISOString().slice(0, 10);
                                        setUserData(prev => prev ? { ...prev, lastDailyZoomDate: todayStr } : prev);
                                        if (user?.uid) updateDoc(doc(db, "users", user.uid), { lastDailyZoomDate: todayStr }).catch(console.error);
                                    }
                                    httpsCallable(functions, 'surrenderGeoDetectiveGame')({ gameId }).catch(console.error);
                                }
                                
                                handleStopGame(); 
                                setShowAbandonConfirm(false); 
                                setLastGuessLocation(null); 
                                setGuessHistory([]); 
                                setDuelRoomId(null);
                                setDuelStatus(null);
                                setTimeLeft(0);
                                setIsRoulettePlaying(false);
                            }} 
                            style={{ flex: 1, padding: '10px', background: '#dc3545', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', color: 'white' }}
                        >
                            {t('yes')}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* MODALE DUEL : Choix Aléatoire vs Ami (Partie Privée) */}
        {isDuelModalOpen && (
            <div style={{ ...accountOverlayStyle, zIndex: 10001, backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(6px)' }}>
                <div style={{ ...accountModalBoxStyle, backgroundColor: '#0B0E14', border: '1px solid #5C6675', borderRadius: '16px', color: '#EAEDF2', width: isMobile ? '92%' : '440px', maxWidth: '440px', padding: isMobile ? '20px' : '26px', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', textAlign: 'center' }}>
                    
                    {/* 1. ÉCRAN PRINCIPAL : CHOIX DU TYPE DE DUEL */}
                    {duelModalStep === 'choice' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
                                <div style={{ width: '24px' }}></div>
                                <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', fontFamily: 'Orbitron, sans-serif', color: '#4CE0D2', letterSpacing: '1px' }}>
                                    {t('duel_choice_title', 'MODE DUEL')}
                                </h2>
                                <button onClick={() => setIsDuelModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#5C6675', fontSize: '20px', cursor: 'pointer', fontWeight: 'bold' }}>✖</button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                {/* Option 1 : Rival aléatoire */}
                                <button 
                                    onClick={startRandomDuelSearch}
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(76, 224, 209, 0.15) 0%, rgba(11, 14, 20, 0.8) 100%)',
                                        border: '1px solid #4CE0D2',
                                        borderRadius: '10px',
                                        padding: isMobile ? '14px' : '16px',
                                        color: '#EAEDF2',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '14px',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 4px 12px rgba(76, 224, 209, 0.15)'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#4CE0D2'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#4CE0D2'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <span style={{ fontSize: '28px' }}>🎲</span>
                                    <div style={{ flex: 1, fontSize: '15px', fontWeight: 'bold', color: '#4CE0D2', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                        {t('duel_random_rival', 'Rival aléatoire')}
                                    </div>
                                    <span style={{ color: '#4CE0D2', fontSize: '18px' }}>➔</span>
                                </button>

                                {/* Option 2 : Défier un ami */}
                                <button 
                                    onClick={() => { setDuelModalStep('friend_options'); setPrivateCodeError(''); }}
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(92, 102, 117, 0.15) 0%, rgba(11, 14, 20, 0.8) 100%)',
                                        border: '1px solid #5C6675',
                                        borderRadius: '10px',
                                        padding: isMobile ? '14px' : '16px',
                                        color: '#EAEDF2',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '14px',
                                        transition: 'all 0.2s'
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#4CE0D2'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                    onMouseLeave={e => { e.currentTarget.style.borderColor = '#5C6675'; e.currentTarget.style.transform = 'translateY(0)'; }}
                                >
                                    <span style={{ fontSize: '28px' }}>👥</span>
                                    <div style={{ flex: 1, fontSize: '15px', fontWeight: 'bold', color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                        {t('duel_friend_private', 'Défier un ami')}
                                    </div>
                                    <span style={{ color: '#5C6675', fontSize: '18px' }}>➔</span>
                                </button>
                            </div>
                        </>
                    )}

                    {/* 2. ÉCRAN PARTIE PRIVÉE : CHOIX CRÉER OU REJOINDRE */}
                    {duelModalStep === 'friend_options' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' }}>
                                <button onClick={() => setDuelModalStep('choice')} style={{ background: 'transparent', border: 'none', color: '#4CE0D2', fontSize: '18px', cursor: 'pointer', fontWeight: 'bold' }}>←</button>
                                <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', fontFamily: 'Orbitron, sans-serif', color: '#4CE0D2', letterSpacing: '1px' }}>
                                    {t('duel_friend_private', 'DÉFIER UN AMI')}
                                </h2>
                                <button onClick={() => setIsDuelModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#5C6675', fontSize: '20px', cursor: 'pointer', fontWeight: 'bold' }}>✖</button>
                            </div>

                            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                                {/* Bouton Créer */}
                                <button 
                                    onClick={handleCreatePrivateDuel}
                                    disabled={isCreatingPrivate}
                                    style={{
                                        background: 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)',
                                        border: 'none',
                                        borderRadius: '10px',
                                        padding: isMobile ? '14px' : '16px',
                                        color: '#0B0E14',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        cursor: isCreatingPrivate ? 'wait' : 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px',
                                        fontFamily: 'Inter, system-ui, sans-serif',
                                        boxShadow: '0 4px 15px rgba(76, 224, 209, 0.3)'
                                    }}
                                >
                                    <span style={{ fontSize: '18px' }}>➕</span>
                                    {isCreatingPrivate ? "Création..." : t('duel_create_private', 'Créer une partie privée')}
                                </button>

                                {/* Bouton Rejoindre */}
                                <button 
                                    onClick={() => { setDuelModalStep('join_private'); setInputPrivateCode(''); setPrivateCodeError(''); }}
                                    style={{
                                        background: '#1A1E26',
                                        border: '1px solid #5C6675',
                                        borderRadius: '10px',
                                        padding: isMobile ? '14px' : '16px',
                                        color: '#EAEDF2',
                                        fontSize: '15px',
                                        fontWeight: 'bold',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px',
                                        fontFamily: 'Inter, system-ui, sans-serif'
                                    }}
                                >
                                    <span style={{ fontSize: '18px' }}>🔑</span>
                                    {t('duel_join_private', 'Rejoindre une partie')}
                                </button>
                            </div>
                        </>
                    )}

                    {/* 3. ÉCRAN CRÉATEUR : AFFICHAGE DU CODE À 2 CHIFFRES */}
                    {duelModalStep === 'create_private' && (
                        <>
                            <h2 style={{ margin: '0 0 10px 0', fontSize: isMobile ? '18px' : '20px', fontFamily: 'Orbitron, sans-serif', color: '#4CE0D2', letterSpacing: '1px' }}>
                                {t('duel_private_code_title', 'PARTIE PRIVÉE')}
                            </h2>
                            <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#EAEDF2', margin: '0 0 12px 0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                {t('duel_share_code', 'Partagez ce code avec votre ami :')}
                            </p>

                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: isMobile ? '46px' : '54px',
                                fontWeight: '900',
                                fontFamily: 'Orbitron, monospace',
                                color: '#4CE0D2',
                                letterSpacing: '10px',
                                padding: '14px 28px',
                                background: 'rgba(76, 224, 209, 0.08)',
                                border: '2px solid #4CE0D2',
                                borderRadius: '14px',
                                boxShadow: '0 0 25px rgba(76, 224, 209, 0.45)',
                                margin: '10px 0 20px 0'
                            }}>
                                {privateDuelCode || "--"}
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#8E9AA8', fontSize: '13px', marginBottom: '20px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                <span style={{ animation: 'pulse 1.5s infinite', color: '#4CE0D2' }}>📡</span>
                                <span>{t('duel_waiting_friend', 'En attente de votre ami...')}</span>
                            </div>

                            <button 
                                onClick={handleCancelPrivateDuel}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    background: '#1A1E26',
                                    border: '1px solid #5C6675',
                                    borderRadius: '8px',
                                    color: '#FF6B6B',
                                    fontWeight: 'bold',
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    fontFamily: 'Inter, system-ui, sans-serif'
                                }}
                            >
                                ✖ {t('cancel', 'Annuler')}
                            </button>
                        </>
                    )}

                    {/* 4. ÉCRAN REJOINDRE : SAISIE DU CODE À 2 CHIFFRES */}
                    {duelModalStep === 'join_private' && (
                        <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                                <button onClick={() => setDuelModalStep('friend_options')} style={{ background: 'transparent', border: 'none', color: '#4CE0D2', fontSize: '18px', cursor: 'pointer', fontWeight: 'bold' }}>←</button>
                                <h2 style={{ margin: 0, fontSize: isMobile ? '18px' : '20px', fontFamily: 'Orbitron, sans-serif', color: '#4CE0D2', letterSpacing: '1px' }}>
                                    {t('duel_join_private', 'REJOINDRE UNE PARTIE')}
                                </h2>
                                <button onClick={() => setIsDuelModalOpen(false)} style={{ background: 'transparent', border: 'none', color: '#5C6675', fontSize: '20px', cursor: 'pointer', fontWeight: 'bold' }}>✖</button>
                            </div>

                            <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#EAEDF2', margin: '0 0 15px 0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                {t('duel_enter_code', 'Entrez le code à 2 chiffres :')}
                            </p>

                            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '15px' }}>
                                <input 
                                    type="text" 
                                    inputMode="numeric" 
                                    pattern="[0-9]*" 
                                    maxLength={2} 
                                    value={inputPrivateCode} 
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
                                        setInputPrivateCode(val);
                                        setPrivateCodeError('');
                                    }}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' && inputPrivateCode.length === 2 && !isJoiningPrivate) {
                                            handleJoinPrivateDuel();
                                        }
                                    }}
                                    placeholder="--" 
                                    autoFocus
                                    style={{
                                        fontSize: '42px',
                                        fontWeight: 'bold',
                                        fontFamily: 'Orbitron, monospace',
                                        color: '#4CE0D2',
                                        textAlign: 'center',
                                        letterSpacing: '10px',
                                        width: '130px',
                                        padding: '10px',
                                        background: '#0B0E14',
                                        border: '2px solid #4CE0D2',
                                        borderRadius: '12px',
                                        outline: 'none',
                                        boxShadow: '0 0 15px rgba(76, 224, 209, 0.3)'
                                    }}
                                />
                            </div>

                            {privateCodeError && (
                                <div style={{ color: '#FF6B6B', fontSize: '13px', fontWeight: 'bold', marginBottom: '15px', fontFamily: 'Inter, system-ui, sans-serif' }}>
                                    {privateCodeError}
                                </div>
                            )}

                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button 
                                    onClick={() => setDuelModalStep('friend_options')}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        background: '#1A1E26',
                                        border: '1px solid #5C6675',
                                        borderRadius: '8px',
                                        color: '#EAEDF2',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        cursor: 'pointer',
                                        fontFamily: 'Inter, system-ui, sans-serif'
                                    }}
                                >
                                    {t('duel_back_btn', 'Retour')}
                                </button>
                                <button 
                                    onClick={handleJoinPrivateDuel}
                                    disabled={inputPrivateCode.length !== 2 || isJoiningPrivate}
                                    style={{
                                        flex: 2,
                                        padding: '12px',
                                        background: inputPrivateCode.length === 2 ? 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)' : '#2A303C',
                                        border: 'none',
                                        borderRadius: '8px',
                                        color: inputPrivateCode.length === 2 ? '#0B0E14' : '#5C6675',
                                        fontWeight: 'bold',
                                        fontSize: '14px',
                                        cursor: inputPrivateCode.length === 2 && !isJoiningPrivate ? 'pointer' : 'not-allowed',
                                        fontFamily: 'Inter, system-ui, sans-serif',
                                        boxShadow: inputPrivateCode.length === 2 ? '0 4px 15px rgba(76, 224, 209, 0.35)' : 'none'
                                    }}
                                >
                                    {isJoiningPrivate ? "Connexion..." : t('duel_join_btn', 'Rejoindre')}
                                </button>
                            </div>
                        </>
                    )}

                </div>
            </div>
        )}

        {/* MODALE NOTIFICATION : AUCUN RIVAL TROUVÉ (TIMEOUT 30s) */}
        {duelTimeoutNotice && (
            <div style={{ ...accountOverlayStyle, zIndex: 10002, backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(5px)' }}>
                <div style={{ ...accountModalBoxStyle, backgroundColor: '#0B0E14', border: '1px solid #5C6675', borderRadius: '16px', color: '#EAEDF2', width: isMobile ? '90%' : '380px', maxWidth: '380px', padding: '24px', textAlign: 'center', boxShadow: '0 15px 35px rgba(0,0,0,0.6)' }}>
                    <div style={{ fontSize: '36px', marginBottom: '10px' }}>⏳</div>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: '#4CE0D2', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {t('timeout', 'Temps écoulé')}
                    </h3>
                    <p style={{ fontSize: '14px', color: '#EAEDF2', marginBottom: '20px', fontFamily: 'Inter, system-ui, sans-serif', lineHeight: 1.4 }}>
                        {t('duel_no_rival_found', 'Aucun rival trouvé en ligne pour le moment. Réessayez dans quelques instants !')}
                    </p>
                    <button 
                        onClick={() => setDuelTimeoutNotice(false)}
                        style={{
                            width: '100%',
                            padding: '10px',
                            background: 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)',
                            border: 'none',
                            borderRadius: '8px',
                            color: '#0B0E14',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            cursor: 'pointer',
                            fontFamily: 'Inter, system-ui, sans-serif'
                        }}
                    >
                        {t('close_btn', 'Fermer')}
                    </button>
                </div>
            </div>
        )}

        {/* Panneau modale affichant de façon ordonnée le tableau des scores Zoomap */}
        {isLeaderboardOpen && (
            <div style={{ ...accountOverlayStyle, backgroundColor: 'rgba(0, 0, 0, 0.82)', backdropFilter: 'blur(8px)', zIndex: 99999 }}>
                <div style={{ ...accountModalBoxStyle, backgroundColor: '#0B0E14', border: '1px solid #5C6675', color: '#EAEDF2', maxWidth: isMobile ? '100vw' : '450px', width: isMobile ? '100%' : '95%', height: isMobile ? '100vh' : 'auto', maxHeight: isMobile ? '100vh' : '85vh', borderRadius: isMobile ? '0px' : '16px', margin: 0, display: 'flex', flexDirection: 'column', boxShadow: '0 15px 35px rgba(0,0,0,0.6)' }}>
                    
                    {/* En-tête */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #1A1E26', paddingBottom: isMobile ? '10px' : '15px', marginBottom: '10px' }}>
                        <div style={{ width: '24px' }}></div> 
                        <h2 style={{ margin: 0, color: '#EAEDF2', fontSize: isMobile ? '18px' : '22px', textAlign: 'center', flex: 1 }}>
                            {t('leaderboard_title')}
                        </h2>
                        <button onClick={() => setIsLeaderboardOpen(false)} style={{ background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#5C6675', transition: 'color 0.2s', padding: 0, width: '24px', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onMouseEnter={e => e.currentTarget.style.color = '#EAEDF2'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'}>✖</button>
                    </div>
                    
                    {/* Les 3 Onglets */}
                    <div style={{display:'flex',gap:'6px',marginBottom:'12px',borderBottom:'1px solid #1A1E26',paddingBottom:'8px'}}>
                        <button 
                            onClick={()=>setLeaderboardTab('daily')} 
                            style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flex:1,padding:'8px 4px',background:leaderboardTab==='daily'?'#4CE0D2':'#1A1E26',color:leaderboardTab==='daily'?'#0B0E14':'#5C6675',border:leaderboardTab==='daily'?'none':'1px solid #1A1E26',borderRadius:'8px',fontWeight:'bold',cursor:'pointer',fontSize:'13px', whiteSpace:'nowrap', transition: 'all 0.2s'}}
                        >
                            <IconDaily size={isMobile ? '18px' : '22px'} color={leaderboardTab === 'daily' ? '#0B0E14' : '#EAEDF2'} /> 
                            {t('daily_zoom', 'Daily Zoom')}
                        </button>
                        
                        <button 
                            onClick={()=>setLeaderboardTab('solo')} 
                            style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flex:1,padding:'8px 4px',background:leaderboardTab==='solo'?'#4CE0D2':'#1A1E26',color:leaderboardTab==='solo'?'#0B0E14':'#5C6675',border:leaderboardTab==='solo'?'none':'1px solid #1A1E26',borderRadius:'8px',fontWeight:'bold',cursor:'pointer',fontSize:'13px', whiteSpace:'nowrap', transition: 'all 0.2s'}}
                        >
                            <IconSolo size={isMobile ? '18px' : '22px'} color={leaderboardTab === 'solo' ? '#0B0E14' : '#EAEDF2'} /> 
                            {t('solo_mode', 'Solo Elo')}
                        </button>
                        
                        <button 
                            onClick={()=>setLeaderboardTab('duel')} 
                            style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', flex:1,padding:'8px 4px',background:leaderboardTab==='duel'?'#4CE0D2':'#1A1E26',color:leaderboardTab==='duel'?'#0B0E14':'#5C6675',border:leaderboardTab==='duel'?'none':'1px solid #1A1E26',borderRadius:'8px',fontWeight:'bold',cursor:'pointer',fontSize:'13px', whiteSpace:'nowrap', transition: 'all 0.2s'}}
                        >
                            <IconDuel size={isMobile ? '18px' : '22px'} color={leaderboardTab === 'duel' ? '#0B0E14' : '#EAEDF2'} /> 
                            {t('duel_mode', 'Duel (Win%)')}
                        </button>
                    </div>

                    {/* --- NOUVEAU : SÉLECTEUR DE DATE (Uniquement pour le Daily) --- */}
                    {leaderboardTab === 'daily' && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#1A1E26', borderRadius: '8px', padding: '6px 12px', marginBottom: '12px' }}>
                            <button
                                onClick={() => setDailyDateOffset(prev => prev - 1)}
                                style={{ background: 'transparent', border: 'none', color: '#4CE0D2', cursor: 'pointer', fontSize: '18px', padding: '0 10px', display: 'flex', alignItems: 'center' }}
                            >
                                ◀
                            </button>
                            <span style={{ color: '#EAEDF2', fontSize: '13px', fontWeight: 'bold' }}>
                                {dailyDateOffset === 0 ? "Aujourd'hui" : 
                                 dailyDateOffset === -1 ? "Hier" : 
                                 new Date(Date.now() + dailyDateOffset * 86400000).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' })}
                            </span>
                            <button
                                onClick={() => setDailyDateOffset(prev => prev + 1)}
                                disabled={dailyDateOffset >= 0}
                                style={{ background: 'transparent', border: 'none', color: dailyDateOffset >= 0 ? '#313846' : '#4CE0D2', cursor: dailyDateOffset >= 0 ? 'default' : 'pointer', fontSize: '18px', padding: '0 10px', display: 'flex', alignItems: 'center' }}
                            >
                                ▶
                            </button>
                        </div>
                    )}

                    {/* Liste des joueurs selon l'onglet actif */}
                    <div style={{overflowY:'auto',flex:1,paddingRight:'5px'}}>
                        {leaderboardData[leaderboardTab].length === 0 ? (
                            <p style={{textAlign:'center',color:'#5C6675', fontStyle:'italic', marginTop:'20px'}}>
                                {leaderboardTab === 'daily' && dailyDateOffset < 0 
                                    ? "Aucun score enregistré à cette date." 
                                    : (leaderboardTab === 'daily'
                                        ? t('no_daily_data', "Aucun score enregistré pour cette journée.")
                                        : t('no_data_yet', 'Aucun joueur classé (5 parties min.)'))}
                            </p> 
                        ) : (
                            <>
                                {leaderboardData[leaderboardTab].map(p => {
                                    const isMe = Boolean(user?.uid && (p.id === user.uid || p.uid === user.uid));
                                    const playerGrade = getPlayerGrade(p, leaderboardTab);
                                    const rankInfo = getRankData(playerGrade);
                                    return (
                                        <div key={p.id} style={{display:'flex',alignItems:'center',padding:'10px 10px',borderBottom:isMe?'none':'1px solid #1A1E26',backgroundColor:isMe?'rgba(76, 224, 209, 0.1)':'transparent', border: isMe?'1px solid #4CE0D2':'none', borderRadius:'8px',marginBottom:'4px', gap: '8px'}}>
                                            
                                            <div style={{fontWeight:'bold',width:'36px',fontSize:'15px',color:p.rank===1?'#FFD700':(p.rank===2?'#C0C0C0':(p.rank===3?'#CD7F32':'#5C6675')),textShadow:p.rank<=3?'0 1px 4px rgba(0,0,0,0.6)':'none', flexShrink: 0}}>#{p.rank}</div>
                                            
                                            {/* Badge de Rang selon le mode actif (Daily / Solo / Duel) */}
                                            <RankBadge rank={playerGrade} size={isMobile ? 32 : 36} />

                                            <div style={{flex:1, minWidth: 0, paddingLeft:'2px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                                                <div style={{minWidth: 0, flex: 1}}>
                                                    <div style={{fontWeight:'bold',fontSize:'14px',color:isMe?'#4CE0D2':'#EAEDF2', display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden'}}>
                                                        <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                            {isMe ? (userData?.pseudo || userData?.displayName || p.displayName || t('anonymous')) : (p.displayName || t('anonymous'))}
                                                        </span>
                                                        {isMe && <span style={{fontSize:'10px',color:'#4CE0D2', flexShrink: 0}}>({t('its_you')})</span>}
                                                    </div>
                                                    <div style={{fontSize:'11px', color: rankInfo.color, fontWeight:'600'}}>
                                                        {playerGrade}
                                                    </div>
                                                </div>

                                                {/* Badge VIP Créateur : Twitch, YouTube, TikTok ou Instagram aligné verticalement */}
                                                <VipSocialLink user={p} isMobile={isMobile} />
                                            </div>
                                            
                                            <div style={{fontWeight:'bold',color:'#fff',background:'linear-gradient(135deg, #1e3c72 0%, rgb(123, 30, 154) 100%)',padding:'4px 8px',borderRadius:'20px',fontSize:'12px',boxShadow:'0 2px 4px rgba(0,0,0,0.2)', border: '1px solid rgba(255, 255, 255, 0.4)', textShadow: '0px 1px 2px rgba(0,0,0,0.3)', whiteSpace:'nowrap', flexShrink: 0, minWidth: isMobile ? '70px' : '78px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                                                {p.displayScore}
                                            </div>
                                        </div>
                                    );
                                })}

                                {/* Si le joueur connecté est classé au-delà du Top 50 (rang > 50) */}
                                {leaderboardData.myRank?.[leaderboardTab] && leaderboardData.myRank[leaderboardTab].rank > 50 && (() => {
                                    const p = leaderboardData.myRank[leaderboardTab];
                                    const playerGrade = getPlayerGrade(p, leaderboardTab);
                                    const rankInfo = getRankData(playerGrade);
                                    return (
                                        <>
                                            {/* Séparateur pour marquer le saut de classement */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                padding: '6px 0',
                                                color: '#5C6675',
                                                fontSize: '14px',
                                                letterSpacing: '4px',
                                                userSelect: 'none'
                                            }}>
                                                •••
                                            </div>

                                            {/* Carte du joueur connecté au-delà du Top 50 (exactement le même modèle) */}
                                            <div style={{
                                                display:'flex',
                                                alignItems:'center',
                                                padding:'10px 10px',
                                                backgroundColor:'rgba(76, 224, 209, 0.1)',
                                                border: '1px solid #4CE0D2',
                                                borderRadius:'8px',
                                                marginBottom:'4px',
                                                gap: '8px'
                                            }}>
                                                <div style={{
                                                    fontWeight:'bold',
                                                    width:'36px',
                                                    fontSize:'15px',
                                                    color: '#5C6675',
                                                    flexShrink: 0
                                                }}>
                                                    #{p.rank}
                                                </div>

                                                <RankBadge rank={playerGrade} size={isMobile ? 32 : 36} />

                                                <div style={{flex:1, minWidth: 0, paddingLeft:'2px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px'}}>
                                                    <div style={{minWidth: 0, flex: 1}}>
                                                        <div style={{fontWeight:'bold',fontSize:'14px',color:'#4CE0D2', display: 'flex', alignItems: 'center', gap: '4px', overflow: 'hidden'}}>
                                                            <span style={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>
                                                                {userData?.pseudo || userData?.displayName || p.displayName || t('anonymous')}
                                                            </span>
                                                            <span style={{fontSize:'10px',color:'#4CE0D2', flexShrink: 0}}>({t('its_you')})</span>
                                                        </div>
                                                        <div style={{fontSize:'11px', color: rankInfo.color, fontWeight:'600'}}>
                                                            {playerGrade}
                                                        </div>
                                                    </div>

                                                    {/* Badge VIP Créateur : Twitch, YouTube, TikTok ou Instagram aligné verticalement */}
                                                    <VipSocialLink user={p} isMobile={isMobile} />
                                                </div>

                                                <div style={{
                                                    fontWeight:'bold',
                                                    color:'#fff',
                                                    background:'linear-gradient(135deg, #1e3c72 0%, rgb(123, 30, 154) 100%)',
                                                    padding:'4px 8px',
                                                    borderRadius:'20px',
                                                    fontSize:'12px',
                                                    boxShadow:'0 2px 4px rgba(0,0,0,0.2)',
                                                    border: '1px solid rgba(255, 255, 255, 0.4)',
                                                    textShadow: '0px 1px 2px rgba(0,0,0,0.3)',
                                                    whiteSpace:'nowrap',
                                                    flexShrink: 0,
                                                    minWidth: isMobile ? '70px' : '78px',
                                                    textAlign: 'center',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center'
                                                }}>
                                                    {p.displayScore}
                                                </div>
                                            </div>
                                        </>
                                    );
                                })()}
                            </>
                        )}
                    </div>
                    
                    {/* Footer conditionnel */}
                    <div style={{marginTop:'10px',paddingTop:'10px',borderTop:'1px solid #1A1E26',backgroundColor:'transparent'}}>
                        <div style={{fontSize:'11px', color:'#5C6675', textAlign:'center', fontStyle:'italic', fontFamily: 'Inter, system-ui, sans-serif'}}>
                            {leaderboardTab === 'solo' || leaderboardTab === 'duel' ? 
                                t('min_games_info', '* Il faut avoir joué au minimum 5 parties pour apparaître dans ce classement.') : 
                                t('daily_reset_info', '* Le classement Daily Zoom est mis à jour quotidiennement.')
                            }
                        </div>
                    </div>
                </div>
            </div>
        )}


       {/* Modale d'informations détaillées présentant les règles de Géo-Détective */}
        {isInfoModalOpen && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.8)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', backdropFilter: 'blur(8px)' }} onClick={() => setIsInfoModalOpen(false)}>
                <div style={{ width: '90%', maxWidth: '800px', maxHeight: '95vh', backgroundColor: '#0B0E14', border: '1px solid #5C6675', borderRadius: '16px', display: 'flex', flexDirection: 'column', boxShadow: '0 15px 35px rgba(0,0,0,0.6)', overflow: 'hidden', color: '#EAEDF2', fontFamily: 'Arial, sans-serif' }} onClick={e => e.stopPropagation()}>
                    
                    {/* Header de la modale */}
                    <div style={{ padding: '15px', borderBottom: '1px solid #1A1E26', textAlign: 'center', backgroundColor: '#1A1E26', position: 'relative' }}>
                        <button onClick={() => setIsInfoModalOpen(false)} style={{ position: 'absolute', top: '15px', right: '15px', background: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#5C6675', transition: 'color 0.2s' }} onMouseEnter={e => e.currentTarget.style.color = '#EAEDF2'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'}>✖</button>
                        
                        <img src="/logotest.svg" alt="Geo Detective" style={{ maxWidth: '180px', height: 'auto', display: 'block', margin: '0 auto' }} />

                       {/* Réseaux Sociaux - En bas à droite */}
                        <div style={{ position: 'absolute', bottom: isMobile ? '5px' : '12px', right: isMobile ? '5px' : '15px', display: 'flex', gap: '10px' }}>
                            
                            {/* Instagram */}
                            <a href="https://www.instagram.com/zoomap.world/" target="_blank" rel="noopener noreferrer" style={{ color: '#5C6675', transition: 'color 0.2s', display: 'flex' }} onMouseEnter={e => e.currentTarget.style.color = '#E1306C'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'} title="Instagram">
                                <svg width={isMobile ? "20" : "30"} height={isMobile ? "20" : "30"} viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                                </svg>
                            </a>

                            {/* TikTok */}
                            <a href="https://www.tiktok.com/@zoomap.world" target="_blank" rel="noopener noreferrer" style={{ color: '#5C6675', transition: 'color 0.2s', display: 'flex' }} onMouseEnter={e => e.currentTarget.style.color = '#EAEDF2'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'} title="TikTok">
                                <svg width={isMobile ? "20" : "30"} height={isMobile ? "20" : "30"} viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.12-3.44-3.17-3.64-5.46-.22-2.58.91-5.17 2.97-6.55 1.51-.95 3.32-1.22 5.06-1.07V9.92c-1.24-.12-2.53.05-3.65.61-1.14.54-2.01 1.51-2.42 2.68-.42 1.25-.26 2.67.34 3.82.68 1.35 2.12 2.27 3.65 2.37 1.5.09 3.01-.34 4.19-1.25 1.09-.85 1.71-2.14 1.72-3.55 0-4.99-.02-9.98.02-14.96z"/>
                                </svg>
                            </a>

                            {/* YouTube */}
                            <a href="https://www.youtube.com/@zoomap_world" target="_blank" rel="noopener noreferrer" style={{ color: '#5C6675', transition: 'color 0.2s', display: 'flex' }} onMouseEnter={e => e.currentTarget.style.color = '#FF0000'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'} title="YouTube">
                                <svg width={isMobile ? "20" : "30"} height={isMobile ? "20" : "30"} viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                </svg>
                            </a>

                            {/* Discord */}
                            <a href="https://discord.gg/sFUBfCMuXv" target="_blank" rel="noopener noreferrer" style={{ color: '#5C6675', transition: 'color 0.2s', display: 'flex' }} onMouseEnter={e => e.currentTarget.style.color = '#5865F2'} onMouseLeave={e => e.currentTarget.style.color = '#5C6675'} title="Discord">
                                <svg width={isMobile ? "20" : "30"} height={isMobile ? "20" : "30"} viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6081 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189Z"/>
                                </svg>
                            </a>

                        </div>
                    </div>
                    
                    {/* Contenu - Les Modes de Jeu */}
                    <div style={{ padding: '20px', overflowY: 'auto', lineHeight: '1.5' }}>
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <h3 style={{ color: '#EAEDF2', fontSize: isMobile ? '18px' : '22px', margin: '0 0 5px 0' }}>{t('welcome_explorer', 'Bienvenue Détective !')}</h3>
                            
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                            
                            {/* Mode Daily Zoom */}
                            <div 
                                onMouseEnter={() => setHoveredCard('daily')}
                                onMouseLeave={() => setHoveredCard(null)}
                                style={{ 
                                    background: '#1A1E26', 
                                    border: `1px solid ${hoveredCard === 'daily' ? '#4CE0D2' : '#5C6675'}`, 
                                    borderRadius: '12px', 
                                    padding: '15px', 
                                    transition: 'all 0.3s ease',
                                    boxShadow: hoveredCard === 'daily' ? '0 4px 15px rgba(76, 224, 209, 0.15)' : 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '24px' }}><IconDaily size={isMobile ? '18px' : '22px'} color="#EAEDF2" /></span>
                                    <h4 style={{ margin: 0, color: hoveredCard === 'daily' ? '#4CE0D2' : '#EAEDF2', fontSize: '18px', fontWeight: 'bold', transition: 'color 0.3s ease' }}>{t('daily_zoom', 'DAILY ZOOM')}</h4>
                                </div>
                                <p style={{ fontSize: '15px', color: '#EAEDF2', fontFamily: 'Inter, sans-serif', margin: '0 0 12px 0' }}>{t('daily_zoom_desc', 'Une image unique chaque jour, identique pour tous les joueurs. Tentez de trouver la localisation exacte pour dominer le classement quotidien !')}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#5C6675', textTransform: 'uppercase' }}>{t('leaderboard', 'Classement')} :</span>
                                    <button 
                                        onClick={() => { fetchLeaderboard(); setLeaderboardTab('daily'); setIsLeaderboardOpen(true); }}
                                        style={{ background: '#0B0E14', border: '1px solid #4CE0D2', color: '#4CE0D2', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', textDecoration: 'none', transition: 'all 0.2s ease', cursor: 'pointer' }} 
                                        onMouseEnter={e => { e.currentTarget.style.background = '#4CE0D2'; e.currentTarget.style.color = '#0B0E14'; }} 
                                        onMouseLeave={e => { e.currentTarget.style.background = '#0B0E14'; e.currentTarget.style.color = '#4CE0D2'; }}
                                    >
                                        {t('daily_leaderboard_link', 'Journalier ↗')}
                                    </button>
                                </div>
                            </div>

                            {/* Mode Solo */}
                            <div 
                                onMouseEnter={() => setHoveredCard('solo')}
                                onMouseLeave={() => setHoveredCard(null)}
                                style={{ 
                                    background: '#1A1E26', 
                                    border: `1px solid ${hoveredCard === 'solo' ? '#4CE0D2' : '#5C6675'}`, 
                                    borderRadius: '12px', 
                                    padding: '15px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: hoveredCard === 'solo' ? '0 4px 15px rgba(76, 224, 209, 0.15)' : 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '24px' }}><IconSolo size={isMobile ? '18px' : '22px'} color="#EAEDF2" /></span>
                                    <h4 style={{ margin: 0, color: hoveredCard === 'solo' ? '#4CE0D2' : '#EAEDF2', fontSize: '18px', fontWeight: 'bold', transition: 'color 0.3s ease' }}>{t('solo_mode', 'SOLO')}</h4>
                                </div>
                                <p style={{ fontSize: '15px', color: '#EAEDF2', fontFamily: 'Inter, sans-serif', margin: '0 0 12px 0' }}>{t('solo_desc', "Entraînez-vous sur des lieux aléatoires. Accumulez des points pour le classement général. Limité à 2 missions libres par jour (ou 5 avec Premium).")}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#5C6675', textTransform: 'uppercase' }}>{t('leaderboard', 'Classement')} :</span>
                                    <button 
                                        onClick={() => { fetchLeaderboard(); setLeaderboardTab('solo'); setIsLeaderboardOpen(true); }}
                                        style={{ background: '#0B0E14', border: '1px solid #4CE0D2', color: '#4CE0D2', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', textDecoration: 'none', transition: 'all 0.2s ease', cursor: 'pointer' }} 
                                        onMouseEnter={e => { e.currentTarget.style.background = '#4CE0D2'; e.currentTarget.style.color = '#0B0E14'; }} 
                                        onMouseLeave={e => { e.currentTarget.style.background = '#0B0E14'; e.currentTarget.style.color = '#4CE0D2'; }}
                                    >
                                        {t('solo_leaderboard_link', 'Général ↗')}
                                    </button>
                                </div>
                            </div>

                            {/* Mode Duel */}
                            <div 
                                onMouseEnter={() => setHoveredCard('duel')}
                                onMouseLeave={() => setHoveredCard(null)}
                                style={{ 
                                    background: '#1A1E26', 
                                    border: `1px solid ${hoveredCard === 'duel' ? '#4CE0D2' : '#5C6675'}`, 
                                    borderRadius: '12px', 
                                    padding: '15px',
                                    transition: 'all 0.3s ease',
                                    boxShadow: hoveredCard === 'duel' ? '0 4px 15px rgba(76, 224, 209, 0.15)' : 'none'
                                }}
                            >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                    <span style={{ fontSize: '24px' }}><IconDuel size={isMobile ? '18px' : '22px'} color="#EAEDF2" /></span>
                                    <h4 style={{ margin: 0, color: hoveredCard === 'duel' ? '#4CE0D2' : '#EAEDF2', fontSize: '18px', fontWeight: 'bold', transition: 'color 0.3s ease' }}>{t('duel_mode', 'DUEL')}</h4>
                                </div>
                                <p style={{ fontSize: '15px', color: '#EAEDF2', fontFamily: 'Inter, sans-serif', margin: '0 0 12px 0' }}>{t('duel_desc', "Affrontez un autre joueur en temps réel sur la même image. Le détective le plus proche de la cible remporte la victoire ! (Utilise vos missions libres).")}</p>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#5C6675', textTransform: 'uppercase' }}>{t('leaderboard', 'Classement')} :</span>
                                    <button 
                                        onClick={() => { fetchLeaderboard(); setLeaderboardTab('duel'); setIsLeaderboardOpen(true); }}
                                        style={{ background: '#0B0E14', border: '1px solid #4CE0D2', color: '#4CE0D2', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', textDecoration: 'none', transition: 'all 0.2s ease', cursor: 'pointer' }} 
                                        onMouseEnter={e => { e.currentTarget.style.background = '#4CE0D2'; e.currentTarget.style.color = '#0B0E14'; }} 
                                        onMouseLeave={e => { e.currentTarget.style.background = '#0B0E14'; e.currentTarget.style.color = '#4CE0D2'; }}
                                    >
                                        {t('duel_leaderboard_link', 'Duels ↗')}
                                    </button>
                                </div>
                            </div>
                            
                        </div>
                    </div>
                    
                    {/* Footer */}
                    <div style={{ padding: '15px', borderTop: '1px solid #1A1E26', backgroundColor: '#1A1E26', fontSize: '12px', textAlign: 'center', color: '#5C6675' }}>
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                            
                            <span style={{ fontSize: '12px' }}>{t('contact', 'Contact')} : <strong style={{color: '#EAEDF2'}}>contact@zoomap.world</strong></span>
                        </div>
                        <div style={{marginBottom: '10px'}}>
                            <a href="/terms.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#4CE0D2' }}>{t('tos', 'CGU')}</a> 
                            <span style={{ margin: '0 8px' }}>&middot;</span> 
                            <a href="/privacy.html" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#4CE0D2' }}>{t('privacy', 'Confidentialité')}</a> 
                            <span style={{ margin: '0 8px' }}>&middot;</span> 
                            <a href="mailto:contact@zoomap.world" style={{ textDecoration: 'none', color: '#4CE0D2' }}>{t('report_bug', 'Signaler un bug')}</a>
                        </div>
                        <p style={{ margin: '0 0 15px 0', fontSize: '11px', color: '#5C6675' }}>Map powered by Google Maps Platform ©</p>
                        
                        <button onClick={() => setIsInfoModalOpen(false)} style={{ display: 'block', width: '100%', padding: '12px', background: 'linear-gradient(135deg, #4CE0D2 0%, #5C6675 100%)', color: '#EAEDF2', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', transition: 'filter 0.2s' }} onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'} onMouseLeave={e => e.currentTarget.style.filter = 'brightness(1)'}>
                            {t('close_modal_btn', 'Fermer')}
                        </button>
                    </div>
                    
                </div>
            </div>
        )}

        {/* MODALE ALERTE PREMIUM */}
        {premiumAlert && (
            <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.85)', backdropFilter: 'blur(5px)', zIndex: 100000, display: 'flex', justifyContent: 'center', alignItems: 'center', animation: 'fadeIn 0.2s ease-out' }}>
                <div style={{ 
                    background: 'linear-gradient(180deg, rgba(20, 27, 38, 0.95) 0%, rgba(12, 17, 26, 0.98) 100%)', 
                    border: `1px solid ${premiumAlert === 'error' ? '#dc3545' : '#4CE0D2'}`, 
                    borderRadius: '12px', 
                    padding: '24px', 
                    maxWidth: '320px', 
                    textAlign: 'center', 
                    boxShadow: `0 0 25px ${premiumAlert === 'error' ? 'rgba(220, 53, 69, 0.2)' : 'rgba(76, 224, 209, 0.2)'}` 
                }}>
                    
                    <h3 style={{ margin: '0 0 12px 0', color: premiumAlert === 'error' ? '#dc3545' : '#4CE0D2', fontFamily: 'Orbitron, sans-serif', fontSize: '20px' }}>
                        {premiumAlert === 'success' ? t('premium_success_title') : t('premium_error_title')}
                    </h3>
                    
                    <p style={{ color: '#EAEDF2', fontSize: '14px', lineHeight: '1.5', margin: '0 0 24px 0', fontFamily: 'Inter, system-ui, sans-serif' }}>
                        {premiumAlert === 'success' ? t('premium_success_msg') : t('premium_error_msg')}
                    </p>
                    
                    <button 
                        onClick={() => setPremiumAlert(null)}
                        style={{ 
                            background: premiumAlert === 'error' ? '#dc3545' : '#4CE0D2', 
                            color: '#0B0E14', 
                            border: 'none', 
                            padding: '10px 30px', 
                            borderRadius: '8px', 
                            fontSize: '15px', 
                            fontWeight: 'bold', 
                            cursor: 'pointer', 
                            fontFamily: 'Inter, system-ui, sans-serif',
                            transition: 'transform 0.1s'
                        }}
                        onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.95)'}
                        onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                        {t('ok_btn', 'OK')}
                    </button>
                </div>
            </div>
        )}

        {/* MODALE D'ONBOARDING : CHOIX OBLIGATOIRE DU PSEUDO ET BADGE DE DÉPART */}
        {isOnboardingOpen && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(11, 14, 20, 0.95)',
                backdropFilter: 'blur(10px)',
                zIndex: 100000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    backgroundColor: '#0B0E14',
                    border: '1px solid #4CE0D2',
                    borderRadius: '16px',
                    padding: isMobile ? '24px 18px' : '32px 28px',
                    maxWidth: '440px',
                    width: '100%',
                    boxShadow: '0 0 35px rgba(76, 224, 209, 0.25)',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px'
                }}>
                    {/* Badge de départ : Touriste */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                        <RankBadge rank="Touriste" size={isMobile ? 64 : 76} />
                        <div style={{ 
                            fontSize: '12px', 
                            fontFamily: 'Orbitron, sans-serif', 
                            color: '#5C6675', 
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            fontWeight: 'bold' 
                        }}>
                            {t('onboarding_starting_rank', 'Rang de départ')} : <span style={{ color: '#8A95A5' }}>{t('rank_tourist', 'Touriste')}</span>
                        </div>
                    </div>

                    {/* Titre et Explication */}
                    <div>
                        <h2 style={{
                            margin: '4px 0 8px 0',
                            fontSize: isMobile ? '20px' : '23px',
                            color: '#EAEDF2',
                            fontFamily: 'Orbitron, sans-serif'
                        }}>
                            {t('onboarding_title', 'Bienvenue sur ZOOMAP !')}
                        </h2>
                        <p style={{
                            margin: 0,
                            fontSize: isMobile ? '13px' : '14px',
                            color: '#8A95A5',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            lineHeight: '1.45'
                        }}>
                            {t('onboarding_subtitle', 'Choisis ton pseudo de jeu. Il sera le seul nom visible par les autres joueurs dans les classements et les duels.')}
                        </p>
                    </div>

                    {/* Formulaire de saisie du pseudo */}
                    <form onSubmit={handleValidateOnboarding} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        <div style={{ position: 'relative', width: '100%' }}>
                            <input
                                type="text"
                                value={onboardingPseudo}
                                onChange={(e) => {
                                    setOnboardingPseudo(e.target.value);
                                    if (onboardingError) setOnboardingError('');
                                }}
                                maxLength={15}
                                placeholder={t('enter_pseudo', 'Ton pseudo (4 à 15 car.)')}
                                style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '12px 16px',
                                    backgroundColor: '#141B26',
                                    border: onboardingError ? '1px solid #E03030' : '1px solid #4CE0D2',
                                    borderRadius: '10px',
                                    color: '#EAEDF2',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    textAlign: 'center',
                                    fontFamily: 'Inter, system-ui, sans-serif',
                                    outline: 'none',
                                    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
                                    transition: 'border-color 0.2s'
                                }}
                                autoFocus
                            />
                            <div style={{
                                position: 'absolute',
                                right: '12px',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                fontSize: '11px',
                                color: onboardingPseudo.length < 4 || onboardingPseudo.length > 15 ? '#E03030' : '#5C6675',
                                fontWeight: 'bold'
                            }}>
                                {onboardingPseudo.length}/15
                            </div>
                        </div>

                        {onboardingError && (
                            <div style={{ color: '#E03030', fontSize: '12px', fontWeight: 'bold' }}>
                                {onboardingError}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={isOnboardingSaving}
                            style={{
                                width: '100%',
                                padding: '13px 20px',
                                marginTop: '6px',
                                background: 'linear-gradient(135deg, #4CE0D2 0%, #2ba89c 100%)',
                                color: '#0A0F18',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: isMobile ? '14px' : '15px',
                                fontWeight: 'bold',
                                fontFamily: 'Orbitron, sans-serif',
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                cursor: isOnboardingSaving ? 'wait' : 'pointer',
                                boxShadow: '0 4px 15px rgba(76, 224, 209, 0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                opacity: isOnboardingSaving ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => !isOnboardingSaving && (e.currentTarget.style.filter = 'brightness(1.1)')}
                            onMouseLeave={(e) => !isOnboardingSaving && (e.currentTarget.style.filter = 'brightness(1)')}
                        >
                            <span>{t('onboarding_validate_btn', 'Valider et commencer')}</span>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <polygon points="5 3 19 12 5 21 5 3"></polygon>
                            </svg>
                        </button>
                    </form>
                </div>
            </div>
        )}

        {/* MODALE CADEAU DE BIENVENUE (30 JOURS OFFERTS SANS ENGAGEMENT) */}
        {isWelcomeGiftOpen && (
            <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100vw',
                height: '100vh',
                backgroundColor: 'rgba(11, 14, 20, 0.95)',
                backdropFilter: 'blur(10px)',
                zIndex: 100000,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                boxSizing: 'border-box'
            }}>
                <div style={{
                    backgroundColor: '#0B0E14',
                    border: '1.5px solid #FFD700',
                    borderRadius: '16px',
                    padding: isMobile ? '24px 18px' : '30px 26px',
                    maxWidth: '440px',
                    width: '100%',
                    boxShadow: '0 0 35px rgba(255, 215, 0, 0.25)',
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: isMobile ? '12px' : '15px',
                    boxSizing: 'border-box',
                    animation: 'fadeIn 0.25s ease-out'
                }}>
                    {/* Icône Cadeau SVG (aucun emoji) */}
                    <div style={{
                        width: isMobile ? '56px' : '64px',
                        height: isMobile ? '56px' : '64px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.18) 0%, rgba(212, 175, 55, 0.05) 100%)',
                        border: '1.5px solid #FFD700',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 20px rgba(255, 215, 0, 0.35)'
                    }}>
                        <IconGift size={isMobile ? "28px" : "32px"} color="#FFD700" />
                    </div>

                    {/* Titre et sous-titre */}
                    <div>
                        <h2 style={{
                            margin: '0 0 6px 0',
                            fontSize: isMobile ? '18px' : '21px',
                            color: '#FFD700',
                            fontFamily: 'Orbitron, sans-serif',
                            letterSpacing: '1px'
                        }}>
                            {t('welcome_gift_title', 'CADEAU DE BIENVENUE')}
                        </h2>
                        <p style={{
                            margin: 0,
                            fontSize: isMobile ? '12.5px' : '14px',
                            color: '#EAEDF2',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            lineHeight: '1.5'
                        }}>
                            <span>{t('welcome_gift_intro', 'Pour fêter ton arrivée sur ZOOMAP,')}</span>
                            <br />
                            <span style={{
                                display: 'inline-block',
                                marginTop: '4px',
                                color: '#4CE0D2',
                                fontWeight: '600'
                            }}>
                                {t('welcome_gift_highlight_before', "profite de 30 jours d'accès ")}
                                <span style={{
                                    fontSize: isMobile ? '15.5px' : '17px',
                                    fontWeight: '800',
                                    fontFamily: 'Orbitron, sans-serif',
                                    letterSpacing: '0.5px'
                                }}>
                                    PREMIUM
                                </span>
                                {t('welcome_gift_highlight_after', " 100% offerts.")}
                            </span>
                        </p>
                    </div>

                    {/* 3 Avantages avec icônes SVG existantes (pas d'emojis, centrés) */}
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{
                            background: 'rgba(255, 215, 0, 0.06)',
                            border: '1px solid rgba(255, 215, 0, 0.22)',
                            borderRadius: '10px',
                            padding: isMobile ? '8px 12px' : '9px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <IconDuel size={isMobile ? "18px" : "20px"} color="#FFD700" />
                            <span style={{ fontSize: isMobile ? '12px' : '13px', color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '500', textAlign: 'center' }}>
                                {t('welcome_gift_feature_duel', 'Mode Duel en direct débloqué')}
                            </span>
                        </div>

                        <div style={{
                            background: 'rgba(255, 215, 0, 0.06)',
                            border: '1px solid rgba(255, 215, 0, 0.22)',
                            borderRadius: '10px',
                            padding: isMobile ? '8px 12px' : '9px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <IconSolo size={isMobile ? "18px" : "20px"} color="#FFD700" />
                            <span style={{ fontSize: isMobile ? '12px' : '13px', color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '500', textAlign: 'center' }}>
                                {t('welcome_gift_feature_solo', '5 parties par jour au lieu de 2')}
                            </span>
                        </div>

                        <div style={{
                            background: 'rgba(255, 215, 0, 0.06)',
                            border: '1px solid rgba(255, 215, 0, 0.22)',
                            borderRadius: '10px',
                            padding: isMobile ? '8px 12px' : '9px 14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '10px'
                        }}>
                            <IconReplay size={isMobile ? "18px" : "20px"} color="#FFD700" />
                            <span style={{ fontSize: isMobile ? '12px' : '13px', color: '#EAEDF2', fontFamily: 'Inter, system-ui, sans-serif', fontWeight: '500', textAlign: 'center' }}>
                                {t('welcome_gift_feature_replay', 'Replay illimité de ta collection')}
                            </span>
                        </div>
                    </div>

                    {/* Date d'échéance dynamique */}
                    <div style={{
                        width: '100%',
                        boxSizing: 'border-box',
                        padding: '8px 12px',
                        background: 'rgba(76, 224, 209, 0.08)',
                        border: '1px solid rgba(76, 224, 209, 0.35)',
                        borderRadius: '8px',
                        color: '#4CE0D2',
                        fontSize: isMobile ? '11.5px' : '13px',
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: '600',
                        letterSpacing: '0.3px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                    }}>
                        <span style={{ fontSize: '10px' }}>★</span>
                        <span>
                            {t('welcome_gift_end_date', 'Votre statut prendra fin le {{date}}.', { 
                                date: formatExpirationDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000), i18n.language) 
                            })}
                        </span>
                    </div>

                    {/* Bouton Récupérer mon cadeau */}
                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                        <button
                            onClick={handleClaimWelcomeGift}
                            disabled={isClaimingGift}
                            style={{
                                width: '100%',
                                padding: isMobile ? '12px 16px' : '13px 20px',
                                background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                                color: '#0A0F18',
                                border: 'none',
                                borderRadius: '10px',
                                fontSize: isMobile ? '12px' : '13.5px',
                                fontWeight: 'bold',
                                fontFamily: 'Orbitron, sans-serif',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                cursor: isClaimingGift ? 'wait' : 'pointer',
                                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                transition: 'all 0.2s ease',
                                opacity: isClaimingGift ? 0.7 : 1
                            }}
                            onMouseEnter={(e) => !isClaimingGift && (e.currentTarget.style.filter = 'brightness(1.08)')}
                            onMouseLeave={(e) => !isClaimingGift && (e.currentTarget.style.filter = 'brightness(1)')}
                        >
                            <span>{t('welcome_gift_claim_btn', 'Récupérer mon cadeau 30 jours premium')}</span>
                        </button>

                        {/* Réassurance sans CB & sans engagement (avec IconShield SVG) */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            color: '#8A95A5',
                            fontSize: isMobile ? '10.5px' : '11px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                            textAlign: 'center',
                            flexWrap: 'wrap'
                        }}>
                            <IconShield size="14px" color="#4CE0D2" />
                            <span>{t('welcome_gift_reassurance', '100% gratuit • Aucune carte bancaire requise • Aucun engagement')}</span>
                        </div>

                        {/* Passer */}
                        <button
                            onClick={handleDismissWelcomeGift}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: '#5C6675',
                                fontSize: '12px',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                marginTop: '2px',
                                fontFamily: 'Inter, system-ui, sans-serif'
                            }}
                        >
                            {t('welcome_gift_later', 'Plus tard')}
                        </button>
                    </div>
                </div>
            </div>
        )}

        {/* BOUTON D'INSTALLATION (Uniquement mobile, si non installé et aucune modale ouverte) */}
            {isMobile && !isStandalone && !isAnyModalOpen && (
                <button
                    onClick={() => setIsInstallModalOpen(true)}
                    style={{
                        position: 'absolute',
                        top: '50px',
                        right: '4px', // Ou "right: '70px'" selon où tu as de la place sur ton interface
                        zIndex: 1000,
                        background: 'rgba(10, 15, 24, 0.8)',
                        border: '1px solid rgba(76, 224, 209, 0.5)',
                        borderRadius: '20px',
                        padding: '8px 14px',
                        color: '#FFF',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '13px',
                        fontFamily: 'Orbitron, sans-serif',
                        backdropFilter: 'blur(5px)',
                        cursor: 'pointer'
                    }}
                >
                    {/* Icône Smartphone SVG bien visible */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CE0D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                    
                    {t('install_button', 'Installer l\'application')}
                    
                    {/* Le petit voyant rouge clignotant */}
                    <div style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#FF3B30',
                        borderRadius: '50%',
                        boxShadow: '0 0 5px #FF3B30',
                        animation: 'pingRadar 2s infinite',
                        marginLeft: '2px'
                    }} />
                </button>
            )}

            {/* MODALE D'EXPLICATION D'INSTALLATION */}
            {isInstallModalOpen && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 10050, background: 'rgba(10, 15, 24, 0.9)', backdropFilter: 'blur(5px)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
                    <div style={{ background: 'linear-gradient(180deg, #1A2433 0%, #101620 100%)', border: '1px solid #4CE0D2', borderRadius: '16px', padding: '24px', color: 'white', maxWidth: '380px', width: '100%', position: 'relative', boxShadow: '0 10px 30px rgba(0,0,0,0.5)' }}>
                        
                        <button onClick={() => setIsInstallModalOpen(false)} style={{ position: 'absolute', top: '12px', right: '12px', background: 'transparent', color: '#FFF', border: 'none', fontSize: '20px', cursor: 'pointer' }}>✕</button>
                        
                        <h3 style={{ margin: '0 0 24px 0', fontFamily: 'Orbitron, sans-serif', color: '#FFF', fontSize: '18px', textAlign: 'center' }}>
                            {t('install_title', 'Installer ZooMap')}
                        </h3>
                        
                        {/* Section iOS */}
                        <div style={{ marginBottom: '20px', background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px' }}>
                            <h4 style={{ margin: '0 0 12px 0', fontFamily: 'Orbitron, sans-serif', color: '#4CE0D2', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '600' }}>
                                {/* Logo Apple SVG */}
                                <svg width="18" height="18" viewBox="0 0 384 512" fill="#FFFFFF">
                                    <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
                                </svg>
                                iOS (iPhone / iPad)
                            </h4>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#E0E6ED' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 'bold', color: '#4CE0D2', fontFamily: 'Inter, sans-serif' }}>1/</span>
                                    <span style={{ fontFamily: 'Inter, sans-serif' }}>
                                        {t('install_ios_step1', 'Appuyez sur l\'icône Partager en bas de l\'écran')}
                                        {/* Icône Share iOS */}
                                        <svg style={{ verticalAlign: 'middle', marginLeft: '6px', marginBottom: '2px' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4CE0D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/>
                                        </svg>
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 'bold', color: '#4CE0D2', fontFamily: 'Inter, sans-serif' }}>2/</span>
                                    <span style={{ fontFamily: 'Inter, sans-serif' }}>
                                        {t('install_ios_step2', 'Sélectionnez "Sur l\'écran d\'accueil"')}
                                        {/* Icône Add to Home iOS */}
                                        <svg style={{ verticalAlign: 'middle', marginLeft: '6px', marginBottom: '2px' }} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4CE0D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="4" y="4" width="16" height="16" rx="2" ry="2"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Section Android */}
                        <div style={{ background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px' }}>
                            <h4 style={{ margin: '0 0 12px 0', fontFamily: 'Orbitron, sans-serif', color: '#4CE0D2', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '16px', fontWeight: '600' }}>
                                {/* Logo Android SVG */}
                                <svg width="18" height="18" viewBox="0 0 448 512" fill="#3DDC84">
                                    <path d="M279.1 82.2l32.2-55.8c1.6-2.7.7-6.1-2.1-7.7-2.7-1.6-6.1-.7-7.7 2.1L269 77.2c-13.9-6.3-29-9.8-44.9-9.8s-31.1 3.5-44.9 9.8L146.9 20.8c-1.6-2.7-5.1-3.6-7.7-2.1-2.7 1.6-3.6 5.1-2.1 7.7l32.2 55.8C89.9 126.1 32 195.1 32 280h384c0-84.9-57.9-153.9-136.9-197.8zM128 224c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24zm192 0c-13.3 0-24-10.7-24-24s10.7-24 24-24 24 10.7 24 24-10.7 24-24 24z"/>
                                </svg>
                                Android
                            </h4>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '14px', color: '#E0E6ED' }}>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 'bold', color: '#4CE0D2', fontFamily: 'Inter, sans-serif' }}>1/</span>
                                    <span style={{ fontFamily: 'Inter, sans-serif' }}>
                                        {t('install_android_step1', 'Appuyez sur les 3 points du navigateur en haut à droite')}
                                        {/* Icône 3 points Android */}
                                        <svg style={{ verticalAlign: 'middle', marginLeft: '4px', marginBottom: '2px' }} width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                            <circle cx="12" cy="5" r="2.5"/><circle cx="12" cy="12" r="2.5"/><circle cx="12" cy="19" r="2.5"/>
                                        </svg>
                                    </span>
                                </div>
                                <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                                    <span style={{ fontWeight: 'bold', color: '#4CE0D2', fontFamily: 'Inter, sans-serif' }}>2/</span>
                                    <span style={{ fontFamily: 'Inter, sans-serif' }}>
                                        {t('install_android_step2', '"Installer l\'application" ou "Installer et créer un raccourci"')}
                                        {/* Icône Installation (Écran + Flèche) calquée sur ton image */}
                                        <svg style={{ verticalAlign: 'middle', marginLeft: '6px', marginBottom: '2px' }} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4CE0D2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <rect x="3" y="5" width="18" height="12" rx="2" ry="2"/>
                                            <line x1="9" y1="21" x2="15" y2="21"/>
                                            <line x1="12" y1="17" x2="12" y2="21"/>
                                            <path d="M12 7v5"/>
                                            <path d="M9 10l3 3 3-3"/>
                                        </svg>
                                    </span>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            )}

            {/* MODALE D'INTRODUCTION DUEL (FACE-OFF VS) */}
            {showDuelIntro && duelPlayersData.player1 && duelPlayersData.player2 && (
                <DuelIntroModal
                    player1={duelPlayersData.player1}
                    player2={duelPlayersData.player2}
                    isMobile={isMobile}
                    onFinish={handleFinishDuelIntro}
                    t={t}
                />
            )}

            {/* MODALE DE FINALISATION DUEL (CÉLÉBRATION VAINQUEUR VS PERDANT) */}
            {gameFeedback?.isDuelFinished && (
                <DuelOutroModal
                    isWin={gameFeedback.isWon}
                    isTie={gameFeedback.isTie}
                    isLost={gameFeedback.isLost}
                    isRefund={gameFeedback.isRefund}
                    isForfeitWin={gameFeedback.isForfeitWin}
                    myData={{
                        displayName: userData?.displayName || (user?.displayName ? user.displayName.trim().split(' ')[0] : '') || user?.email?.split('@')[0] || 'Moi',
                        soloGrade: userSoloGrade
                    }}
                    oppData={{
                        displayName: gameFeedback?.oppData?.displayName || opponentData?.displayName || t('rival', 'Rival'),
                        soloGrade: gameFeedback?.oppData?.soloGrade || opponentData?.soloGrade || getOpponentSoloGrade(opponentData)
                    }}
                    myDistance={gameFeedback.myDistance}
                    oppDistance={gameFeedback.oppDistance}
                    isMobile={isMobile}
                    distanceUnit={distanceUnit}
                    onContinue={() => {
                        setGameFeedback(null);
                        setGameId(null);
                        setDuelRoomId(null);
                        setLastGuessLocation(null);
                        setGuessHistory([]);
                        setShowDuelIntro(false);
                        duelIntroShownRef.current = false;
                        resetMapToInitialView();
                    }}
                    t={t}
                />
            )}

            {/* ANIMATION INTERSTITIELLE DU SCORE */}
            {showScoreAnimation && (
                <ScoreAnimation
                    score={finalScore}
                    isMobile={isMobile}
                    gameMode={currentGameMode}
                    availableZooms={isPremiumUser ? Math.max(0, 5 - gamesPlayedCount) : Math.max(0, 2 - gamesPlayedCount)}
                    isPremiumUser={isPremiumUser}
                    countdownText={countdownText}
                    onOpenLeaderboard={() => {
                        fetchLeaderboard(0);
                        setDailyDateOffset(0);
                        setLeaderboardTab(currentGameMode === 'daily' ? 'daily' : 'solo');
                        setIsLeaderboardOpen(true);
                    }}
                    onComplete={() => {
                        setShowScoreAnimation(false);
                        resetMapToInitialView();
                    }}
                    onStartNewGame={() => {
                        setShowScoreAnimation(false);
                        resetMapToInitialView();
                        handleStartGame('solo');
                    }}
                    onUnlockPremium={() => {
                        setShowScoreAnimation(false);
                        setCurrentView('account');
                        setIsInfoModalOpen(false);
                        setIsBadgeModalOpen(false);
                    }}
                />
            )}

            {/* ALERTE VISUELLE DU TEMPS RESTANT (HUD CENTRAL) */}
            {timeAlert && (
                <div
                    key={timeAlert.key}
                    style={{
                        position: 'fixed',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 99999,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        gap: isMobile ? '10px' : '14px',
                        padding: isMobile ? '12px 20px' : '16px 28px',
                        borderRadius: '50px',
                        background: 'rgba(15, 23, 42, 0.92)',
                        backdropFilter: 'blur(12px)',
                        WebkitBackdropFilter: 'blur(12px)',
                        border: `2px solid ${
                            timeAlert.urgency === 'urgent' 
                                ? '#dc3545' 
                                : timeAlert.urgency === 'warning' 
                                    ? '#f39c12' 
                                    : '#4CE0D2'
                        }`,
                        boxShadow: `0 10px 35px rgba(0, 0, 0, 0.6), 0 0 25px ${
                            timeAlert.urgency === 'urgent' 
                                ? 'rgba(220, 53, 69, 0.5)' 
                                : timeAlert.urgency === 'warning' 
                                    ? 'rgba(243, 156, 18, 0.45)' 
                                    : 'rgba(76, 224, 210, 0.35)'
                        }`,
                        color: 'white',
                        fontFamily: 'Montserrat, sans-serif',
                        animation: 'timeAlertPop 2s cubic-bezier(0.16, 1, 0.3, 1) forwards',
                        userSelect: 'none',
                    }}
                >
                    <span style={{ fontSize: isMobile ? '22px' : '28px', lineHeight: 1 }}>
                        {timeAlert.urgency === 'urgent' ? '⚠️' : '⏳'}
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ 
                            fontSize: isMobile ? '10px' : '11px', 
                            textTransform: 'uppercase', 
                            letterSpacing: '1px', 
                            color: timeAlert.urgency === 'urgent' ? '#ff6b6b' : timeAlert.urgency === 'warning' ? '#f1c40f' : '#7bede2', 
                            fontWeight: 'bold',
                            lineHeight: 1.2
                        }}>
                            {t('time_alert_title', 'Temps restant')}
                        </span>
                        <span style={{ 
                            fontSize: isMobile ? '15px' : '18px', 
                            fontWeight: '800', 
                            letterSpacing: '0.5px',
                            whiteSpace: 'nowrap'
                        }}>
                            {timeAlert.text}
                        </span>
                    </div>
                </div>
            )}

        {/* ---------------------------------------------------- */}
            {/* MODALES DE LA COLLECTION */}
            {/* ---------------------------------------------------- */}
            
            {/* 1. Modale du Pokédex (La grille de 150 cases) */}
            {isPokedexOpen && (
                <PokedexModal
                    isMobile={isMobile}
                    mesZoomsTrouves={mesZoomsTrouves}
                    totalZooms={TOTAL_ZOOMS} // La grille fait 150 cases
                    onClose={() => setIsPokedexOpen(false)}
                    onOpenDisplayZoom={(zoom) => {
                        // Quand on clique sur une miniature, on ouvre l'image en grand
                        setSelectedDisplayZoom(zoom);
                    }}
                    onPlayGame={handleReplayGame} // <--- ON BRANCHE LA FONCTION ICI !
                    isPremiumUser={isPremiumUser}
                    onUnlockPremium={() => {
                        setIsPokedexOpen(false);
                        setCurrentView('account');
                    }}
                    t={t} // <--- ON PASSE LA FONCTION DE TRADUCTION ICI
                />
            )}

            {/* 2. Modale d'affichage détaillé du Zoom (Vidéo + Image) */}
            {selectedDisplayZoom && (
                <DisplayZoomModal
                    zoom={selectedDisplayZoom}
                    isMobile={isMobile}
                    onClose={() => setSelectedDisplayZoom(null)}
                    t={t} // <--- ET ICI
                />
            )}

            {/* 3. Modale de Comparatif des Offres (Standard vs Premium) */}
            {isCompareModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        backgroundColor: 'rgba(0, 0, 0, 0.85)',
                        zIndex: 10050,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backdropFilter: 'blur(8px)',
                        padding: isMobile ? '12px' : '20px',
                        boxSizing: 'border-box'
                    }}
                    onClick={() => setIsCompareModalOpen(false)}
                >
                    <div
                        style={{
                            width: '100%',
                            maxWidth: '580px',
                            maxHeight: '92vh',
                            backgroundColor: '#0B0E14',
                            border: '1px solid #D4AF37',
                            borderRadius: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            boxShadow: '0 15px 40px rgba(0,0,0,0.8), 0 0 25px rgba(212, 175, 55, 0.2)',
                            overflow: 'hidden',
                            color: '#EAEDF2',
                            fontFamily: 'Inter, sans-serif'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header de la modale */}
                        <div
                            style={{
                                padding: isMobile ? '14px 16px' : '18px 24px',
                                borderBottom: '1px solid #1A1E26',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#141B26',
                                position: 'relative'
                            }}
                        >
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <h3
                                    style={{
                                        margin: 0,
                                        fontSize: isMobile ? '16px' : '19px',
                                        fontFamily: 'Orbitron, sans-serif',
                                        color: '#FFD700',
                                        letterSpacing: '0.5px'
                                    }}
                                >
                                    {t('compare_modal_title', 'Comparatif des offres')}
                                </h3>
                            </div>
                            <button
                                onClick={() => setIsCompareModalOpen(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '22px',
                                    cursor: 'pointer',
                                    color: '#5C6675',
                                    padding: '0 4px',
                                    lineHeight: 1,
                                    transition: 'color 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#EAEDF2'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#5C6675'}
                            >
                                ✖
                            </button>
                        </div>

                        {/* Corps / Tableau comparatif */}
                        <div style={{ padding: isMobile ? '14px 12px' : '20px 24px', overflowY: 'auto' }}>
                            <table
                                style={{
                                    width: '100%',
                                    borderCollapse: 'separate',
                                    borderSpacing: 0,
                                    borderRadius: '10px',
                                    overflow: 'hidden',
                                    border: '1px solid #1A1E26'
                                }}
                            >
                                <thead>
                                    <tr style={{ backgroundColor: '#161C26' }}>
                                        <th
                                            style={{
                                                padding: isMobile ? '10px 8px' : '14px 12px',
                                                textAlign: 'left',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#8A95A5',
                                                fontWeight: '600',
                                                borderBottom: '2px solid #1A1E26',
                                                width: isMobile ? '40%' : '44%'
                                            }}
                                        >
                                            {t('feature_col', 'Fonctionnalités')}
                                        </th>
                                        <th
                                            style={{
                                                padding: isMobile ? '10px 6px' : '14px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid rgba(255, 255, 255, 0.2)',
                                                borderLeft: '1px solid #1A1E26',
                                                backgroundColor: 'rgba(255, 255, 255, 0.03)',
                                                width: isMobile ? '30%' : '28%'
                                            }}
                                        >
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <span>{t('status_standard', 'Standard')}</span>
                                            </div>
                                        </th>
                                        <th
                                            style={{
                                                padding: isMobile ? '10px 6px' : '14px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFD700',
                                                fontWeight: 'bold',
                                                borderBottom: '2px solid #FFD700',
                                                borderLeft: '1px solid rgba(255, 215, 0, 0.3)',
                                                backgroundColor: 'rgba(255, 215, 0, 0.08)',
                                                width: isMobile ? '30%' : '28%',
                                                textShadow: '0 0 8px rgba(255, 215, 0, 0.3)'
                                            }}
                                        >
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                                                <span>★ {t('status_premium', 'Premium')}</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {/* Ligne 1: Mode Daily zoom */}
                                    <tr style={{ borderBottom: '1px solid #1A1E26' }}>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 8px' : '13px 12px',
                                                fontSize: isMobile ? '11px' : '13px',
                                                color: '#EAEDF2',
                                                borderBottom: '1px solid #1A1E26'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    <IconDaily size={isMobile ? '16px' : '20px'} color="#4CE0D2" />
                                                </span>
                                                <span style={{ fontWeight: '500' }}>{t('mode_daily_zoom', 'Mode Daily zoom')}</span>
                                            </div>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #1A1E26',
                                                borderLeft: '1px solid #1A1E26',
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                            }}
                                        >
                                            <span style={{ color: '#4CE0D2', marginRight: '4px' }}>✓</span>
                                            <span>{t('yes', 'Oui')}</span>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFD700',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #1A1E26',
                                                borderLeft: '1px solid rgba(255, 215, 0, 0.3)',
                                                backgroundColor: 'rgba(255, 215, 0, 0.06)'
                                            }}
                                        >
                                            <span style={{ marginRight: '4px' }}>✓</span>
                                            <span>{t('yes', 'Oui')}</span>
                                        </td>
                                    </tr>

                                    {/* Ligne 2: Mode Solo */}
                                    <tr style={{ borderBottom: '1px solid #1A1E26' }}>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 8px' : '13px 12px',
                                                fontSize: isMobile ? '11px' : '13px',
                                                color: '#EAEDF2',
                                                borderBottom: '1px solid #1A1E26'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    <IconSolo size={isMobile ? '16px' : '20px'} color="#4CE0D2" />
                                                </span>
                                                <span style={{ fontWeight: '500' }}>{t('mode_solo', 'Mode Solo')}</span>
                                            </div>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFFFFF',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #1A1E26',
                                                borderLeft: '1px solid #1A1E26',
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                            }}
                                        >
                                            2 {t('per_day_suffix', 'par jour')}
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFD700',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #1A1E26',
                                                borderLeft: '1px solid rgba(255, 215, 0, 0.3)',
                                                backgroundColor: 'rgba(255, 215, 0, 0.06)'
                                            }}
                                        >
                                            5 {t('per_day_suffix', 'par jour')}
                                        </td>
                                    </tr>

                                    {/* Ligne 3: Mode Duel */}
                                    <tr style={{ borderBottom: '1px solid #1A1E26' }}>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 8px' : '13px 12px',
                                                fontSize: isMobile ? '11px' : '13px',
                                                color: '#EAEDF2',
                                                borderBottom: '1px solid #1A1E26'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    <IconDuel size={isMobile ? '16px' : '20px'} color="#4CE0D2" />
                                                </span>
                                                <span style={{ fontWeight: '500' }}>{t('mode_duel', 'Mode Duel')}</span>
                                            </div>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#8A95A5',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #1A1E26',
                                                borderLeft: '1px solid #1A1E26',
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                            }}
                                        >
                                            <span style={{ marginRight: '4px' }}>✕</span>
                                            <span>{t('no', 'Non')}</span>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFD700',
                                                fontWeight: 'bold',
                                                borderBottom: '1px solid #1A1E26',
                                                borderLeft: '1px solid rgba(255, 215, 0, 0.3)',
                                                backgroundColor: 'rgba(255, 215, 0, 0.06)'
                                            }}
                                        >
                                            <span style={{ marginRight: '4px' }}>✓</span>
                                            <span>{t('yes', 'Oui')}</span>
                                        </td>
                                    </tr>

                                    {/* Ligne 4: Rejouer la collection */}
                                    <tr>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 8px' : '13px 12px',
                                                fontSize: isMobile ? '11px' : '13px',
                                                color: '#EAEDF2'
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '6px' : '8px' }}>
                                                <span style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                                    <IconReplay size={isMobile ? '16px' : '20px'} color="#4CE0D2" />
                                                </span>
                                                <span style={{ fontWeight: '500' }}>{t('replay_collection', 'Rejouer la collection')}</span>
                                            </div>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#8A95A5',
                                                fontWeight: 'bold',
                                                borderLeft: '1px solid #1A1E26',
                                                backgroundColor: 'rgba(255, 255, 255, 0.02)'
                                            }}
                                        >
                                            <span style={{ marginRight: '4px' }}>✕</span>
                                            <span>{t('no', 'Non')}</span>
                                        </td>
                                        <td
                                            style={{
                                                padding: isMobile ? '10px 6px' : '13px 10px',
                                                textAlign: 'center',
                                                fontSize: isMobile ? '11px' : '13px',
                                                fontFamily: 'Orbitron, sans-serif',
                                                color: '#FFD700',
                                                fontWeight: 'bold',
                                                borderLeft: '1px solid rgba(255, 215, 0, 0.3)',
                                                backgroundColor: 'rgba(255, 215, 0, 0.06)'
                                            }}
                                        >
                                            <span style={{ marginRight: '4px' }}>✓</span>
                                            <span>{t('yes', 'Oui')}</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* --- CHOIX DES TARIFS PREMIUM (Mensuel vs Annuel en Cyan) --- */}
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: isMobile ? '8px' : '14px',
                                    marginTop: isMobile ? '10px' : '14px',
                                    marginBottom: isMobile ? '6px' : '8px',
                                    width: '100%',
                                    boxSizing: 'border-box'
                                }}
                            >
                                {/* Option 1 : Premium Mensuel (L'ancre) */}
                                <div
                                    onClick={() => handleSubscribe('monthly')}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        background: 'linear-gradient(180deg, rgba(26, 30, 38, 0.75) 0%, rgba(14, 19, 28, 0.9) 100%)',
                                        border: '1.5px solid #FFD700',
                                        borderRadius: '12px',
                                        padding: isMobile ? '6px 5px 5px 5px' : '8px 10px 7px 10px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        position: 'relative',
                                        boxShadow: '0 4px 15px rgba(0, 0, 0, 0.4)',
                                        boxSizing: 'border-box',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(255, 215, 0, 0.35)';
                                        e.currentTarget.style.borderColor = '#FFE55C';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                        e.currentTarget.style.boxShadow = '0 4px 15px rgba(0, 0, 0, 0.4)';
                                        e.currentTarget.style.borderColor = '#FFD700';
                                    }}
                                >
                                    {/* 1. Titre option (aligné) */}
                                    <div style={{
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '10px' : '12px',
                                        fontWeight: '700',
                                        color: '#FFD700',
                                        letterSpacing: isMobile ? '0.5px' : '0.8px',
                                        textTransform: 'uppercase',
                                        minHeight: isMobile ? '16px' : '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        lineHeight: '1.15'
                                    }}>
                                        {t('plan_monthly', 'Premium Mensuel')}
                                    </div>

                                    {/* 2. Prix (aligné, même taille de police) */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        justifyContent: 'center',
                                        gap: '3px',
                                        marginTop: '1px',
                                        marginBottom: '1px',
                                        minHeight: isMobile ? '22px' : '26px'
                                    }}>
                                        <span style={{
                                            fontFamily: 'Orbitron, sans-serif',
                                            fontSize: isMobile ? '20px' : '23px',
                                            fontWeight: '800',
                                            color: '#EAEDF2',
                                            letterSpacing: '0.5px'
                                        }}>
                                            3,99 €
                                        </span>
                                        <span style={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: isMobile ? '9.5px' : '11.5px',
                                            color: '#8A95A5',
                                            fontWeight: '500'
                                        }}>
                                            {t('per_month', '/ mois')}
                                        </span>
                                    </div>

                                    {/* 3. Moyen de facturation (aligné) */}
                                    <div style={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: isMobile ? '10px' : '12px',
                                        color: '#8A95A5',
                                        lineHeight: '1.15',
                                        textAlign: 'center',
                                        minHeight: isMobile ? '22px' : '26px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {t('monthly_billing_desc', 'Facturé mensuellement. Annulable à tout moment.')}
                                    </div>

                                    {/* 4. Espace réservé pour alignement strict avec le badge annuel */}
                                    <div style={{
                                        minHeight: isMobile ? '16px' : '18px',
                                        marginTop: '2px'
                                    }} />
                                </div>

                                {/* Option 2 : Premium Annuel */}
                                <div
                                    onClick={() => handleSubscribe('annual')}
                                    style={{
                                        flex: 1,
                                        minWidth: 0,
                                        background: 'linear-gradient(180deg, rgba(255, 215, 0, 0.08) 0%, rgba(18, 16, 12, 0.95) 100%)',
                                        border: '2px solid #FFD700',
                                        borderRadius: '12px',
                                        padding: isMobile ? '6px 5px 5px 5px' : '8px 10px 7px 10px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'flex-start',
                                        alignItems: 'center',
                                        position: 'relative',
                                        boxShadow: '0 0 20px rgba(255, 215, 0, 0.2), 0 4px 15px rgba(0, 0, 0, 0.5)',
                                        boxSizing: 'border-box',
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                        e.currentTarget.style.boxShadow = '0 0 25px rgba(255, 215, 0, 0.4), 0 6px 20px rgba(0, 0, 0, 0.6)';
                                        e.currentTarget.style.borderColor = '#FFF08A';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                        e.currentTarget.style.boxShadow = '0 0 20px rgba(255, 215, 0, 0.2), 0 4px 15px rgba(0, 0, 0, 0.5)';
                                        e.currentTarget.style.borderColor = '#FFD700';
                                    }}
                                >
                                    {/* 1. Titre option (aligné) */}
                                    <div style={{
                                        fontFamily: 'Orbitron, sans-serif',
                                        fontSize: isMobile ? '10px' : '12px',
                                        fontWeight: '700',
                                        color: '#FFD700',
                                        letterSpacing: isMobile ? '0.5px' : '0.8px',
                                        textTransform: 'uppercase',
                                        minHeight: isMobile ? '16px' : '18px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        lineHeight: '1.15'
                                    }}>
                                        {t('plan_annual', 'Premium Annuel')}
                                    </div>

                                    {/* 2. Prix (aligné, même taille de police) */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        justifyContent: 'center',
                                        gap: '3px',
                                        marginTop: '1px',
                                        marginBottom: '1px',
                                        minHeight: isMobile ? '22px' : '26px'
                                    }}>
                                        <span style={{
                                            fontFamily: 'Orbitron, sans-serif',
                                            fontSize: isMobile ? '20px' : '23px',
                                            fontWeight: '800',
                                            color: '#FFD700',
                                            letterSpacing: '0.5px',
                                            textShadow: '0 0 12px rgba(255, 215, 0, 0.4)'
                                        }}>
                                            2,08 €
                                        </span>
                                        <span style={{
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: isMobile ? '9.5px' : '11.5px',
                                            color: '#FFD700',
                                            fontWeight: '600'
                                        }}>
                                            {t('per_month', '/ mois')}
                                        </span>
                                    </div>

                                    {/* 3. Moyen de facturation (aligné) */}
                                    <div style={{
                                        fontFamily: 'Inter, sans-serif',
                                        fontSize: isMobile ? '10px' : '12px',
                                        color: '#EAEDF2',
                                        fontWeight: '500',
                                        lineHeight: '1.15',
                                        textAlign: 'center',
                                        minHeight: isMobile ? '22px' : '26px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}>
                                        {t('annual_billing_desc', 'Facturé 24,99 € une fois par an.')}
                                    </div>

                                    {/* 4. Badge Économisez 48 % */}
                                    <div style={{
                                        minHeight: isMobile ? '16px' : '18px',
                                        marginTop: '2px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '100%'
                                    }}>
                                        <div style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '3px',
                                            background: 'rgba(255, 215, 0, 0.12)',
                                            border: '1px solid rgba(255, 215, 0, 0.35)',
                                            borderRadius: '5px',
                                            padding: isMobile ? '1.5px 5px' : '2px 7px',
                                            color: '#FFD700',
                                            fontFamily: 'Inter, sans-serif',
                                            fontSize: isMobile ? '8.5px' : '10.5px',
                                            fontWeight: '700',
                                            textAlign: 'center',
                                            lineHeight: '1.2'
                                        }}>
                                            <span style={{ fontSize: '10px' }}>★</span>
                                            <span>{t('annual_savings_badge', 'Économisez 48 %')}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer de la modale */}
                            <div
                                style={{
                                    marginTop: isMobile ? '12px' : '16px',
                                    display: 'flex',
                                    flexDirection: isMobile ? 'column' : 'row',
                                    gap: '10px',
                                    justifyContent: 'flex-end',
                                    alignItems: 'center'
                                }}
                            >
                                {!isPremiumUser ? (
                                    <>
                                        <button
                                            onClick={async () => {
                                                await handleTestPremium();
                                                setIsCompareModalOpen(false);
                                            }}
                                            style={{
                                                flex: 1,
                                                width: isMobile ? '100%' : 'auto',
                                                background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                                                color: '#0B0E14',
                                                border: 'none',
                                                borderRadius: '8px',
                                                padding: isMobile ? '10px 14px' : '11px 18px',
                                                fontWeight: 'bold',
                                                fontFamily: 'Orbitron, sans-serif',
                                                cursor: 'pointer',
                                                fontSize: isMobile ? '12px' : '13px',
                                                boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                transition: 'transform 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                                            onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                                        >
                                            <span>★</span>
                                            <span>{t('btn_promo_free', 'Obtenir 30 jours gratuits (Aucun engagement)')}</span>
                                        </button>
                                        <button
                                            onClick={() => setIsCompareModalOpen(false)}
                                            style={{
                                                width: isMobile ? '100%' : 'auto',
                                                background: 'transparent',
                                                border: '1px solid #5C6675',
                                                color: '#EAEDF2',
                                                borderRadius: '8px',
                                                padding: isMobile ? '10px 14px' : '11px 18px',
                                                fontWeight: 'bold',
                                                cursor: 'pointer',
                                                fontSize: isMobile ? '12px' : '13px',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#EAEDF2'}
                                            onMouseLeave={(e) => e.currentTarget.style.borderColor = '#5C6675'}
                                        >
                                            {t('close', 'Fermer')}
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => setIsCompareModalOpen(false)}
                                        style={{
                                            width: isMobile ? '100%' : 'auto',
                                            background: 'linear-gradient(135deg, #FFD700 0%, #D4AF37 100%)',
                                            color: '#0B0E14',
                                            border: 'none',
                                            borderRadius: '8px',
                                            padding: isMobile ? '10px 20px' : '11px 24px',
                                            fontWeight: 'bold',
                                            fontFamily: 'Orbitron, sans-serif',
                                            cursor: 'pointer',
                                            fontSize: isMobile ? '12px' : '13px',
                                            boxShadow: '0 4px 12px rgba(212, 175, 55, 0.4)'
                                        }}
                                    >
                                        {t('close', 'Fermer')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 4. Modale de Résiliation / Désabonnement Premium */}
            {isCancelModalOpen && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.82)',
                        backdropFilter: 'blur(6px)',
                        zIndex: 99999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '16px'
                    }}
                    onClick={() => setIsCancelModalOpen(false)}
                >
                    <div
                        style={{
                            background: '#0B0E14',
                            border: '1.5px solid rgba(255, 76, 76, 0.5)',
                            borderRadius: '16px',
                            width: '100%',
                            maxWidth: '480px',
                            padding: isMobile ? '20px 16px' : '26px 24px',
                            boxShadow: '0 15px 40px rgba(0,0,0,0.8), 0 0 20px rgba(255, 76, 76, 0.15)',
                            textAlign: 'center',
                            boxSizing: 'border-box'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 style={{
                            margin: '0 0 12px 0',
                            color: '#EAEDF2',
                            fontFamily: 'Orbitron, sans-serif',
                            fontSize: isMobile ? '16px' : '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}>
                            <span>⚠️</span>
                            <span>{t('unsubscribe_modal_title', "Résilier l'abonnement")}</span>
                        </h3>

                        <p style={{
                            color: '#EAEDF2',
                            fontSize: isMobile ? '12px' : '13px',
                            lineHeight: '1.55',
                            margin: '0 0 18px 0',
                            textAlign: 'left',
                            background: 'rgba(255, 255, 255, 0.03)',
                            padding: '12px 14px',
                            borderRadius: '8px',
                            border: '1px solid #1A1E26'
                        }}>
                            {t('unsubscribe_modal_desc', "Vous pouvez résilier votre abonnement à tout moment. Conformément à nos conditions, vos avantages Premium resteront actifs jusqu'à la fin de la période déjà réglée. Pour gérer ou annuler vos paiements récurrents Stripe, utilisez le lien présent dans vos e-mails de facturation Stripe ou contactez contact@zoomap.world.")}
                        </p>

                        <div style={{
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            gap: '10px',
                            justifyContent: 'center'
                        }}>
                            <button
                                onClick={handleUnsubscribe}
                                style={{
                                    background: 'rgba(255, 76, 76, 0.18)',
                                    border: '1px solid #FF6B6B',
                                    color: '#FF6B6B',
                                    borderRadius: '8px',
                                    padding: isMobile ? '11px 14px' : '12px 18px',
                                    fontWeight: 'bold',
                                    fontFamily: 'Orbitron, sans-serif',
                                    fontSize: isMobile ? '11px' : '12px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 76, 76, 0.35)';
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'rgba(255, 76, 76, 0.18)';
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                <span>✕</span>
                                <span>{t('confirm_cancel_btn', 'Confirmer la résiliation')}</span>
                            </button>

                            <button
                                onClick={() => setIsCancelModalOpen(false)}
                                style={{
                                    background: 'transparent',
                                    border: '1px solid #5C6675',
                                    color: '#EAEDF2',
                                    borderRadius: '8px',
                                    padding: isMobile ? '11px 14px' : '12px 18px',
                                    fontWeight: 'bold',
                                    fontFamily: 'Inter, sans-serif',
                                    fontSize: isMobile ? '12px' : '13px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.borderColor = '#EAEDF2'}
                                onMouseLeave={(e) => e.currentTarget.style.borderColor = '#5C6675'}
                            >
                                {t('keep_subscription_btn', 'Conserver mon abonnement')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

    </>
);
}

export default App;
